import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';

const BACKEND = 'https://tambo-production-826b.up.railway.app';
const ADMIN_EMAIL = 'jesustrevi1020@gmail.com';
const INV_TAX = 0.0825;

// Resolves the Supabase token at call time (not from React state) so it works
// immediately after sign-in before the component re-render completes.
// On a 401 the token is refreshed once; if that also fails the session is ended.
async function adminFetch(url, options) {
  var sessionResult = await supabase.auth.getSession();
  var tok = sessionResult.data && sessionResult.data.session && sessionResult.data.session.access_token;
  if (!tok) {
    supabase.auth.signOut(); // triggers onAuthStateChange → setSession(null) in Admin
    throw new Error('NOT_AUTHENTICATED');
  }
  function makeReq(t) {
    var merged = Object.assign({}, options, {
      headers: Object.assign({}, options && options.headers, { 'Authorization': 'Bearer ' + t }),
    });
    return fetch(url, merged);
  }
  var res = await makeReq(tok);
  if (res.status === 401) {
    var refreshResult = await supabase.auth.refreshSession();
    var newTok = refreshResult.data && refreshResult.data.session && refreshResult.data.session.access_token;
    if (!newTok || refreshResult.error) {
      supabase.auth.signOut();
      throw new Error('NOT_AUTHENTICATED');
    }
    res = await makeReq(newTok);
    if (res.status === 401) {
      supabase.auth.signOut();
      throw new Error('NOT_AUTHENTICATED');
    }
  }
  return res;
}

// Pure helper — returns staleness badge info or null if confirmed today.
function inventoryStaleness(lastConfirmedAt) {
  if (!lastConfirmedAt) return { label: 'never confirmed', color: '#dc2626' };
  var daysAgo = Math.floor((Date.now() - new Date(lastConfirmedAt).getTime()) / 86400000);
  if (daysAgo === 0) return null;
  if (daysAgo === 1) return { label: '1d old', color: '#d97706' };
  return { label: daysAgo + 'd old', color: '#dc2626' };
}

const ITEM_OPTIONS = [
  { label: '10-Yard Dumpster Rental', rate: 350 },
  { label: '15-Yard Dumpster Rental', rate: 400 },
  { label: '20-Yard Dumpster Rental', rate: 450 },
  { label: '30-Yard Dumpster Rental', rate: 550 },
  { label: '40-Yard Dumpster Rental', rate: 650 },
  { label: 'Environmental Fee', rate: 15 },
  { label: 'Delivery Fee', rate: 75 },
  { label: 'Extra Day Fee', rate: 50 },
  { label: 'Custom', rate: null },
];
const ITEM_RATES = Object.fromEntries(ITEM_OPTIONS.filter(o => o.rate !== null).map(o => [o.label, o.rate]));
const DEFAULT_TERMS = 'Payment is due by the date specified above. All dumpster rentals are subject to Tambo Technologies LLC terms of service. No hazardous materials. Overage fees apply for excess weight.';
const SIZE_LABELS = { '7yd': '7-Yard (Concrete Only)', '10yd': '10-Yard', '11yd': '11-Yard', '13yd': '13-Yard', '14yd': '14-Yard', '15yd': '15-Yard', '17yd': '17-Yard', '20yd': '20-Yard', '21yd': '21-Yard', '25yd': '25-Yard', '30yd': '30-Yard', '40yd': '40-Yard' };
const HAULER_SIZES = Object.keys(SIZE_LABELS);
const DURATION_OPTS = [
  { key: '3day', label: '3 Days', priceField: 'price_3day' },
  { key: '5day', label: '5 Days', priceField: 'price_5day' },
  { key: '7day', label: '7 Days', priceField: 'price_7day' },
];

function emptyInvoice() {
  return {
    guest_name: '', guest_email: '', guest_phone: '', company_name: '', billing_address: '', service_address: '',
    payment_terms: 'net7', notes: '', is_recurring: false, recurring_interval: 'monthly',
    terms_and_conditions: DEFAULT_TERMS,
    market_name: 'Unassigned',
    tax_enabled: true,
    extra_emails: [],
    extra_phones: [],
    line_items: [{ itemType: '', description: '', quantity: 1, rate: '', amount: 0 }],
  };
}
function resolveMarketFromZip(zip, marketList) {
  if (!zip) return 'Unassigned';
  const z3 = zip.slice(0, 3);
  const z4 = zip.slice(0, 4);
  for (const m of (marketList || [])) {
    if (m.market_name === 'Default') continue;
    const prefixes = (m.zip_prefixes || '').split(',').map(function(p) { return p.trim(); }).filter(Boolean);
    if (prefixes.includes(z3) || prefixes.includes(z4)) return m.market_name;
  }
  return 'Unassigned';
}
function invTotals(items, taxEnabled) {
  const sub = Math.round(items.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0) * 100) / 100;
  const tax = taxEnabled !== false ? Math.round(sub * INV_TAX * 100) / 100 : 0;
  return { sub, tax, total: Math.round((sub + tax) * 100) / 100 };
}
function fmtDate(raw) {
  if (!raw) return '—';
  return new Date(String(raw).split('T')[0] + 'T12:00:00')
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function invDisplayStatus(inv) {
  if (inv.status === 'void') return 'void';
  if (inv.status === 'paid') return 'paid';
  if ((inv.status === 'partially_paid' || inv.status === 'sent') && inv.due_date) {
    const nowCT = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
    const dueStr = String(inv.due_date).split('T')[0];
    if (nowCT > dueStr) return 'overdue';
  }
  return inv.status || 'draft';
}
function displayNum(inv) { return inv.invoice_display || inv.invoice_ref || `INV-${(inv.invoice_number || 0) + 1000}`; }
function fmtMoney(n) { return '$' + Number(n || 0).toFixed(2); }

// ─────────────────────────────────────────────────────────────
// Dashboard tab – national command center
// ─────────────────────────────────────────────────────────────
function DashboardTab({ summary, loading, error, onNavigate, onNewInvoice }) {
  const fmtBig = function(n) {
    if (n >= 1000000) return '$' + (n / 1000000).toFixed(2) + 'M';
    if (n >= 1000)    return '$' + (n / 1000).toFixed(1) + 'K';
    return '$' + Number(n || 0).toFixed(2);
  };

  const cardBase = {
    background: '#fff',
    borderRadius: 14,
    padding: '24px 22px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.07), 0 4px 14px rgba(0,0,0,0.05)',
    border: '1px solid #f0f0ef',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.18s, transform 0.15s',
  };

  function lift(e)  { e.currentTarget.style.boxShadow = '0 6px 28px rgba(26,122,74,0.16)'; e.currentTarget.style.transform = 'translateY(-2px)'; }
  function drop(e)  { e.currentTarget.style.boxShadow = cardBase.boxShadow; e.currentTarget.style.transform = 'none'; }

  if (loading) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 15, color: '#9ca3af', fontWeight: 500 }}>Loading dashboard…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '60px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 14, color: '#ef4444' }}>{error}</div>
      </div>
    );
  }
  if (!summary) return null;

  const topMarkets = (summary.markets || []).filter(function(m) { return m.market_name !== 'Unassigned'; }).slice(0, 3);
  const accentColors = ['#1A7A4A', '#3b82f6', '#8b5cf6'];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 30 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111', margin: '0 0 4px' }}>National Command Center</h2>
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Live data across all markets</p>
      </div>

      {/* Revenue + Commission */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 14 }}>
        <div style={{ ...cardBase, background: '#0d0d0d', border: 'none', cursor: 'default' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#6b7280', marginBottom: 10 }}>Total Company Revenue</div>
          <div style={{ fontSize: 46, fontWeight: 800, color: '#fff', lineHeight: 1, marginBottom: 6 }}>{fmtBig(summary.total_revenue)}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 'auto', paddingTop: 12 }}>All markets combined</div>
        </div>

        <div style={{ ...cardBase, background: '#1A7A4A', border: 'none', cursor: 'default' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#86efac', marginBottom: 10 }}>Tambo Commission</div>
          <div style={{ fontSize: 46, fontWeight: 800, color: '#fff', lineHeight: 1, marginBottom: 6 }}>{fmtBig(summary.total_commission)}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#86efac', marginTop: 2 }}>
            {summary.commission_percent.toFixed(1)}% commission rate
          </div>
        </div>
      </div>

      {/* Haulers + Clients + Processes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 14 }}>
        {/* Haulers */}
        <div style={{ ...cardBase, cursor: 'pointer' }} onClick={function() { onNavigate('haulers'); }} onMouseEnter={lift} onMouseLeave={drop}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#9ca3af', marginBottom: 10 }}>Haulers</div>
          <div style={{ fontSize: 42, fontWeight: 800, color: '#111', lineHeight: 1 }}>{summary.hauler_count}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 5 }}>Active haulers</div>
          {summary.pending_haulers > 0 && (
            <div style={{ marginTop: 12, background: '#fef3c7', borderRadius: 8, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>{summary.pending_haulers} need approval</span>
            </div>
          )}
          <div style={{ marginTop: 'auto', paddingTop: 14, fontSize: 12, color: '#1A7A4A', fontWeight: 600 }}>View Haulers →</div>
        </div>

        {/* Clients */}
        <div style={{ ...cardBase, cursor: 'pointer' }} onClick={function() { onNavigate('clients'); }} onMouseEnter={lift} onMouseLeave={drop}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#9ca3af', marginBottom: 10 }}>Clients</div>
          {summary.pending_clients > 0 ? (
            <>
              <div style={{ fontSize: 42, fontWeight: 800, color: '#dc2626', lineHeight: 1 }}>{summary.pending_clients}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 5 }}>Pending approvals</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 36, fontWeight: 800, color: '#1A7A4A', lineHeight: 1 }}>✓</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 5 }}>All approved</div>
            </>
          )}
          <div style={{ marginTop: 'auto', paddingTop: 14, fontSize: 12, color: '#1A7A4A', fontWeight: 600 }}>View Clients →</div>
        </div>

        {/* Processes */}
        <div style={{ ...cardBase, cursor: 'pointer' }} onClick={function() { onNavigate('processes'); }} onMouseEnter={lift} onMouseLeave={drop}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#9ca3af', marginBottom: 10 }}>Processes</div>
          <div style={{ fontSize: 34, lineHeight: 1 }}>⚙️</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Order lifecycle & SMS workflow reference</div>
          <div style={{ marginTop: 'auto', paddingTop: 14, fontSize: 12, color: '#1A7A4A', fontWeight: 600 }}>View Processes →</div>
        </div>

        {/* All Orders shortcut */}
        <div style={{ ...cardBase, cursor: 'pointer' }} onClick={function() { onNavigate('all'); }} onMouseEnter={lift} onMouseLeave={drop}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#9ca3af', marginBottom: 10 }}>Orders</div>
          <div style={{ fontSize: 34, lineHeight: 1 }}>📋</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>View all orders and invoices</div>
          <div style={{ marginTop: 'auto', paddingTop: 14, fontSize: 12, color: '#1A7A4A', fontWeight: 600 }}>View Orders →</div>
        </div>
      </div>

      {/* Markets + Alerts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 20 }}>
        {/* Markets */}
        <div style={{ ...cardBase, cursor: 'pointer' }} onClick={function() { onNavigate('market-portal'); }} onMouseEnter={lift} onMouseLeave={drop}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#9ca3af', marginBottom: 16 }}>Top Markets by Revenue</div>
          {topMarkets.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No market data yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {topMarkets.map(function(m, idx) {
                const barPct = summary.total_revenue > 0 ? Math.max(4, m.revenue / summary.total_revenue * 100) : 4;
                return (
                  <div key={m.market_name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: 6,
                          background: accentColors[idx], color: '#fff',
                          fontSize: 11, fontWeight: 800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>{idx + 1}</div>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>{m.market_name}</span>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>{fmtBig(m.revenue)}</span>
                    </div>
                    <div style={{ height: 5, background: '#f3f4f6', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: barPct + '%', borderRadius: 10, background: accentColors[idx] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 'auto', paddingTop: 16, fontSize: 12, color: '#1A7A4A', fontWeight: 600 }}>Manage Markets →</div>
        </div>

        {/* Alerts & Messaging */}
        <div style={{ ...cardBase, cursor: 'default' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#9ca3af', marginBottom: 14 }}>Alerts & Messaging</div>
          {summary.unacknowledged_rejections > 0 ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', marginTop: 4, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#dc2626', lineHeight: 1 }}>{summary.unacknowledged_rejections}</div>
                <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 3 }}>Hauler rejection{summary.unacknowledged_rejections !== 1 ? 's' : ''} need attention</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: 22 }}>✅</div>
              <span style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>No pending alerts</span>
            </div>
          )}
          <div style={{ marginTop: 16, fontSize: 12, color: '#9ca3af', fontStyle: 'italic', lineHeight: 1.5 }}>
            Full messaging center — coming soon
          </div>
        </div>
      </div>

      {/* New Invoice CTA */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onNewInvoice}
          style={{ padding: '11px 26px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, letterSpacing: '0.02em', boxShadow: '0 2px 8px rgba(26,122,74,0.25)' }}
        >
          + New Invoice
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Processes tab – order lifecycle reference chart
// ─────────────────────────────────────────────────────────────
function ProcessesTab() {
  const C  = { client: '#1D4ED8', hauler: '#1A7A4A', admin: '#D97706' };

  const steps = [
    { col: 'client', num: 1,  title: 'Search & Browse',          desc: 'Client enters delivery address to view available dumpster sizes and pricing in their area' },
    { col: 'client', num: 2,  title: 'Book & Pay',               desc: 'Selects size, rental duration, delivery date — pays via Stripe or chooses pay later' },
    { col: 'admin',  num: 3,  title: 'Order Created',            desc: 'TMB number + invoice auto-generated. Admin receives SMS alert immediately', notify: ['Admin SMS'] },
    { col: 'admin',  num: 4,  title: 'Haulers Notified',         desc: 'Nearby haulers texted job alert with size, address, delivery date, and payout amount', notify: ['Hauler SMS'] },
    { col: 'hauler', num: 5,  title: 'Accept Job — Y',           desc: 'Hauler replies Y → job assigned, dumpster stock −1. Admin & client both notified', notify: ['Admin SMS', 'Client SMS'] },
    { col: 'hauler', num: 6,  title: 'Deliver — DELIVERED',      desc: 'Hauler drops off dumpster, texts DELIVERED. Order status → delivered', notify: ['Admin SMS', 'Client SMS'] },
    { col: 'client', num: 7,  title: 'Request Pickup — READY',   desc: 'Client texts READY to Tambo when dumpster is full. Order status → pickup_requested' },
    { col: 'hauler', num: 8,  title: 'Pickup Dispatched',        desc: 'Hauler receives pickup SMS with address and order details — replies DONE when complete', notify: ['Hauler SMS'] },
    { col: 'hauler', num: 9,  title: 'Complete — DONE',          desc: 'Hauler texts DONE → stock +1 restored. Admin gets SMS, client receives thank-you', notify: ['Admin SMS', 'Client SMS'] },
    { col: 'admin',  num: 10, title: 'Order Completed',          desc: 'Order status set to completed. Full lifecycle finished' },
  ];

  const notifyColors = { 'Admin SMS': C.admin, 'Client SMS': C.client, 'Hauler SMS': C.hauler };

  function StepCard({ step }) {
    const color = C[step.col];
    return (
      <div style={{
        background: '#fff', border: `2px solid ${color}`, borderLeft: `5px solid ${color}`,
        borderRadius: 8, padding: '11px 14px', width: '100%', boxSizing: 'border-box',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{
            minWidth: 20, height: 20, borderRadius: '50%', background: color, color: '#fff',
            fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>{step.num}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#111', lineHeight: 1.3 }}>{step.title}</div>
        </div>
        <div style={{ fontSize: 11, color: '#555', lineHeight: 1.5, paddingLeft: 28 }}>{step.desc}</div>
        {step.notify && (
          <div style={{ display: 'flex', gap: 4, marginTop: 6, paddingLeft: 28, flexWrap: 'wrap' }}>
            {step.notify.map(n => (
              <span key={n} style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                padding: '2px 6px', borderRadius: 20,
                background: notifyColors[n] + '18', color: notifyColors[n],
                border: `1px solid ${notifyColors[n]}40`,
              }}>{n}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  const cols = ['client', 'hauler', 'admin'];

  const statuses = [
    { label: 'pending',          desc: 'Order placed',           color: '#6b7280' },
    { label: 'accepted',         desc: 'Hauler assigned',        color: C.hauler },
    { label: 'delivered',        desc: 'Dumpster dropped off',   color: C.admin },
    { label: 'pickup_requested', desc: 'Client requested pickup', color: '#8B5CF6' },
    { label: 'completed',        desc: 'Picked up',              color: '#1A7A4A' },
  ];

  const haulerCmds = [
    { keyword: 'Y',         meaning: 'Accept the job' },
    { keyword: 'N',         meaning: 'Decline the job' },
    { keyword: 'DELIVERED', meaning: 'Dumpster dropped off at site' },
    { keyword: 'DONE',      meaning: 'Dumpster picked up (order complete)' },
  ];
  const clientCmds = [
    { keyword: 'READY', meaning: 'Request pickup — dumpster is full' },
  ];

  return (
    <div style={{ maxWidth: 940, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111', margin: '0 0 4px' }}>Order Lifecycle</h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Reference guide — complete flow from booking to pickup.</p>
      </div>

      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
        {[
          { key: 'client', label: 'CLIENT', sub: 'Booking & Communication' },
          { key: 'hauler', label: 'HAULER', sub: 'Delivery & Pickup' },
          { key: 'admin',  label: 'ADMIN',  sub: 'System & Oversight' },
        ].map(({ key, label, sub }) => (
          <div key={key} style={{
            background: C[key], color: '#fff', borderRadius: 8, padding: '10px 14px', textAlign: 'center',
          }}>
            <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: '0.07em' }}>{label}</div>
            <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Swimlane steps */}
      {steps.map((step, i) => {
        const prev = steps[i - 1];
        const sameColAsPrev = prev && prev.col === step.col;
        return (
          <React.Fragment key={step.num}>
            {i > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {cols.map(c => (
                  <div key={c} style={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    height: sameColAsPrev && c === step.col ? 20 : 8,
                    color: C[c], fontSize: 18, fontWeight: 700,
                  }}>
                    {sameColAsPrev && c === step.col ? '↓' : ''}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {cols.map(c => (
                <div key={c}>
                  {c === step.col ? <StepCard step={step} /> : null}
                </div>
              ))}
            </div>
          </React.Fragment>
        );
      })}

      {/* Status flow */}
      <div style={{ marginTop: 28, background: '#F9FAFB', borderRadius: 10, padding: '16px 20px', border: '1px solid #E5E7EB' }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#6b7280', marginBottom: 12, textTransform: 'uppercase' }}>Order Status Flow</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
          {statuses.map((s, i) => (
            <React.Fragment key={s.label}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: s.color + '18', color: s.color, border: `2px solid ${s.color}`,
                  borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                }}>{s.label}</div>
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{s.desc}</div>
              </div>
              {i < statuses.length - 1 && (
                <div style={{ color: '#d1d5db', fontSize: 18, fontWeight: 300, paddingTop: 2 }}>→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* SMS Keywords */}
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '16px 18px', border: `1px solid ${C.hauler}30` }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.07em', color: C.hauler, marginBottom: 10, textTransform: 'uppercase' }}>Hauler SMS Commands</div>
          {haulerCmds.map(k => (
            <div key={k.keyword} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{
                fontFamily: 'monospace', fontWeight: 800, fontSize: 12, background: C.hauler, color: '#fff',
                padding: '2px 8px', borderRadius: 5, minWidth: 74, textAlign: 'center', flexShrink: 0,
              }}>{k.keyword}</span>
              <span style={{ fontSize: 12, color: '#374151' }}>{k.meaning}</span>
            </div>
          ))}
        </div>
        <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '16px 18px', border: `1px solid ${C.client}30` }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.07em', color: C.client, marginBottom: 10, textTransform: 'uppercase' }}>Client SMS Commands</div>
          {clientCmds.map(k => (
            <div key={k.keyword} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{
                fontFamily: 'monospace', fontWeight: 800, fontSize: 12, background: C.client, color: '#fff',
                padding: '2px 8px', borderRadius: 5, minWidth: 74, textAlign: 'center', flexShrink: 0,
              }}>{k.keyword}</span>
              <span style={{ fontSize: 12, color: '#374151' }}>{k.meaning}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ marginTop: 12, display: 'flex', gap: 20, padding: '10px 16px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB', alignItems: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', flexShrink: 0 }}>LEGEND</div>
        {[
          { label: 'Client actions', color: C.client },
          { label: 'Hauler actions', color: C.hauler },
          { label: 'Admin / System', color: C.admin },
        ].map(({ label, color }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#374151' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Hardcoded center coordinates for known Texas markets; unknown markets are geocoded at runtime
// Each entry: keywords (case-insensitive substrings) that identify the market, coords, and optional radius override
var KNOWN_CENTERS = [
  { keywords: ['rgv', 'rio grande'],  coords: { lat: 26.20, lng: -98.23 }, radius: 70000 },
  { keywords: ['san antonio'],        coords: { lat: 29.42, lng: -98.49 } },
  { keywords: ['houston'],            coords: { lat: 29.76, lng: -95.37 } },
  { keywords: ['dallas'],             coords: { lat: 32.78, lng: -96.80 } },
  { keywords: ['corpus'],             coords: { lat: 27.80, lng: -97.40 } },
  { keywords: ['austin'],             coords: { lat: 30.27, lng: -97.74 } },
];

function resolveCenter(marketName) {
  var lower = (marketName || '').toLowerCase();
  for (var i = 0; i < KNOWN_CENTERS.length; i++) {
    var entry = KNOWN_CENTERS[i];
    for (var j = 0; j < entry.keywords.length; j++) {
      if (lower.indexOf(entry.keywords[j]) !== -1) return entry;
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Level 2 – Markets List Portal
// ─────────────────────────────────────────────────────────────
function MarketListPortal({ onSelect, onBack }) {
  const [markets,      setMarkets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  // ── Add-market modal state ──
  const [showModal,    setShowModal]    = useState(false);
  const [form,         setForm]         = useState({ market_name: '', fee_percent: '20', environmental_fee: '0', zip_prefixes: '', counties: '', is_active: true });
  const [takenZips,    setTakenZips]    = useState([]); // [{ name, prefixes }]
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState('');
  const [created,      setCreated]      = useState(null); // newly-created market object

  // ── County discovery state ──
  const [cityInput,    setCityInput]    = useState('');
  const [discovering,  setDiscovering]  = useState(false);
  const [discoverErr,  setDiscoverErr]  = useState('');
  const [nearCounties, setNearCounties] = useState([]); // sorted unique names found around city
  const [selCounties,  setSelCounties]  = useState(new Set()); // admin-checked subset

  const mapRef       = useRef(null); // DOM node for the coverage map
  const mapOvlRef    = useRef([]);   // active circle + marker overlays

  function fetchOverview() {
    setLoading(true); setError('');
    adminFetch(`${BACKEND}/api/admin/markets-overview`)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.error) throw new Error(d.error);
        setMarkets(d.markets || []);
      })
      .catch(function(err) { setError(err.message); })
      .finally(function() { setLoading(false); });
  }

  useEffect(function() { fetchOverview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Coverage map init ──
  useEffect(function() {
    var active = markets.filter(function(m) { return m.market_name !== 'Unassigned' && m.market_name !== 'Default'; });
    if (!active.length || !mapRef.current) return;
    var apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;

    var pollIv = null;

    function initMap() {
      if (!window.google || !window.google.maps || !mapRef.current) return;

      // Remove any overlays from a previous render
      mapOvlRef.current.forEach(function(o) { if (o.setMap) o.setMap(null); });
      mapOvlRef.current = [];

      var map = new window.google.maps.Map(mapRef.current, {
        center: { lat: 29.5, lng: -97.5 },
        zoom: 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        scaleControl: false,
        rotateControl: false,
        gestureHandling: 'cooperative',
      });

      var bounds = new window.google.maps.LatLngBounds();
      var infoWindow = new window.google.maps.InfoWindow();

      Promise.all(active.map(function(m) {
        var entry = resolveCenter(m.market_name);
        if (entry) return Promise.resolve({ market: m, coords: entry.coords, radius: entry.radius || 55000 });
        return fetch('https://maps.googleapis.com/maps/api/geocode/json?address=' + encodeURIComponent(m.market_name + ', TX') + '&key=' + apiKey)
          .then(function(r) { return r.json(); })
          .then(function(d) {
            if (d.results && d.results[0]) {
              var loc = d.results[0].geometry.location;
              return { market: m, coords: { lat: loc.lat, lng: loc.lng }, radius: 55000 };
            }
            return null;
          })
          .catch(function() { return null; });
      })).then(function(results) {
        results.filter(Boolean).forEach(function(item) {
          var m = item.market;
          var coords = item.coords;
          var radius = item.radius || 55000;

          bounds.extend(new window.google.maps.LatLng(coords.lat, coords.lng));

          var circle = new window.google.maps.Circle({
            map: map,
            center: coords,
            radius: radius,
            fillColor: '#1A7A4A',
            fillOpacity: 0.18,
            strokeColor: '#1A7A4A',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            clickable: true,
          });

          var marker = new window.google.maps.Marker({
            map: map,
            position: coords,
            title: m.market_name,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#1A7A4A',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2.5,
              labelOrigin: new window.google.maps.Point(0, -16),
            },
            label: {
              text: m.market_name,
              color: '#166534',
              fontSize: '11px',
              fontWeight: '700',
            },
            zIndex: 10,
          });

          var infoContent = '<div style="font-family:sans-serif;padding:4px 2px;min-width:150px">' +
            '<div style="font-weight:800;font-size:14px;color:#111;margin-bottom:6px">' + m.market_name + '</div>' +
            '<div style="font-size:12px;color:#6b7280;line-height:1.7">' +
              '<div>Revenue: <strong style="color:#1A7A4A">' + fmtMoney(m.revenue) + '</strong></div>' +
              '<div>Orders: <strong style="color:#111">' + (m.order_count || 0) + '</strong></div>' +
              '<div>Haulers: <strong style="color:#111">' + (m.hauler_count || 0) + '</strong></div>' +
            '</div>' +
            '<div style="margin-top:8px;font-size:11px;color:#9ca3af">Click to open market →</div>' +
          '</div>';

          function handleClick() { onSelect(m.market_name); }
          function handleOver() { infoWindow.setContent(infoContent); infoWindow.setPosition(coords); infoWindow.open(map); }
          function handleOut() { infoWindow.close(); }

          circle.addListener('click', handleClick);
          marker.addListener('click', handleClick);
          circle.addListener('mouseover', handleOver);
          marker.addListener('mouseover', handleOver);
          circle.addListener('mouseout', handleOut);
          marker.addListener('mouseout', handleOut);

          mapOvlRef.current.push(circle, marker);
          if (circle.getBounds) bounds.union(circle.getBounds());
        });

        if (!bounds.isEmpty()) map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      });
    }

    if (window.google && window.google.maps) {
      initMap();
    } else {
      if (!document.getElementById('gmaps-script')) {
        var s = document.createElement('script');
        s.id = 'gmaps-script';
        s.src = 'https://maps.googleapis.com/maps/api/js?key=' + apiKey + '&libraries=places&loading=async&callback=__gmapsReady';
        document.head.appendChild(s);
      }
      pollIv = setInterval(function() {
        if (window.google && window.google.maps) {
          clearInterval(pollIv);
          pollIv = null;
          initMap();
        }
      }, 150);
    }

    return function() {
      if (pollIv) clearInterval(pollIv);
      mapOvlRef.current.forEach(function(o) { if (o.setMap) o.setMap(null); });
      mapOvlRef.current = [];
    };
  }, [markets]); // eslint-disable-line react-hooks/exhaustive-deps

  function openModal() {
    setForm({ market_name: '', fee_percent: '20', environmental_fee: '0', zip_prefixes: '', counties: '', is_active: true });
    setSaveError(''); setCreated(null);
    setCityInput(''); setDiscoverErr(''); setNearCounties([]); setSelCounties(new Set());
    // Fetch existing markets so we can show taken ZIP prefixes
    adminFetch(`${BACKEND}/api/admin/markets`)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setTakenZips(
          (d.markets || [])
            .filter(function(m) { return m.market_name !== 'Default' && m.zip_prefixes; })
            .map(function(m) { return { name: m.market_name, prefixes: m.zip_prefixes }; })
        );
      })
      .catch(function() {});
    setShowModal(true);
  }

  async function saveMarket() {
    if (!form.market_name.trim()) { setSaveError('Market name is required.'); return; }
    setSaving(true); setSaveError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/markets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market_name:       form.market_name.trim(),
          fee_percent:       parseFloat(form.fee_percent) || 20,
          environmental_fee: parseFloat(form.environmental_fee) || 0,
          zip_prefixes:      form.zip_prefixes.trim(),
          counties:          form.counties.trim(),
          is_active:         form.is_active,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to create market');
      setCreated(d.market);
      fetchOverview();
    } catch (err) { setSaveError(err.message); }
    finally { setSaving(false); }
  }

  function setF(key, val) { setForm(function(p) { return { ...p, [key]: val }; }); }

  async function discoverCounties() {
    const city = cityInput.trim();
    if (!city) { setDiscoverErr('Enter a city name first (e.g. "Austin, TX").'); return; }
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) { setDiscoverErr('Google Maps API key not found in environment.'); return; }

    setDiscovering(true); setDiscoverErr(''); setNearCounties([]); setSelCounties(new Set());

    try {
      // 1. Geocode the city to get lat/lng + primary county
      const geoRes = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(city)}&key=${apiKey}`
      );
      const geoData = await geoRes.json();
      if (!geoData.results || !geoData.results.length) throw new Error('City not found. Try "Austin, TX" format.');

      const place = geoData.results[0];
      const lat = place.geometry.location.lat;
      const lng = place.geometry.location.lng;

      const primaryComp = place.address_components.find(function(c) { return c.types.includes('administrative_area_level_2'); });
      const primaryCounty = primaryComp ? primaryComp.long_name.replace(/ County$/i, '') : null;

      // 2. Sample 8 points ~25 miles around city center
      const dLat = 25 / 69;
      const dLng = 25 / (69 * Math.cos(lat * Math.PI / 180));
      const points = [
        { lat: lat + dLat,        lng },
        { lat: lat - dLat,        lng },
        { lat,                    lng: lng + dLng },
        { lat,                    lng: lng - dLng },
        { lat: lat + dLat * 0.7,  lng: lng + dLng * 0.7 },
        { lat: lat + dLat * 0.7,  lng: lng - dLng * 0.7 },
        { lat: lat - dLat * 0.7,  lng: lng + dLng * 0.7 },
        { lat: lat - dLat * 0.7,  lng: lng - dLng * 0.7 },
      ];

      // 3. Reverse-geocode all points in parallel, filtering to county-level results
      const revResults = await Promise.all(
        points.map(function(pt) {
          return fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${pt.lat.toFixed(5)},${pt.lng.toFixed(5)}&result_type=administrative_area_level_2&key=${apiKey}`
          ).then(function(r) { return r.json(); }).catch(function() { return { results: [] }; });
        })
      );

      // 4. Collect unique county names
      const found = new Set();
      if (primaryCounty) found.add(primaryCounty);
      revResults.forEach(function(r) {
        (r.results || []).forEach(function(res) {
          var comp = res.address_components.find(function(c) { return c.types.includes('administrative_area_level_2'); });
          if (comp) found.add(comp.long_name.replace(/ County$/i, ''));
        });
      });

      const sorted = Array.from(found).sort();
      setNearCounties(sorted);

      // Pre-select primary county
      const pre = new Set();
      if (primaryCounty) { pre.add(primaryCounty); setF('counties', primaryCounty); }
      setSelCounties(pre);

    } catch (err) {
      setDiscoverErr(err.message);
    } finally {
      setDiscovering(false);
    }
  }

  function toggleCounty(name) {
    setSelCounties(function(prev) {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      setF('counties', Array.from(next).sort().join(','));
      return next;
    });
  }

  const hdr  = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', padding: '10px 14px', borderBottom: '1px solid #f0f0ef', textAlign: 'left', whiteSpace: 'nowrap' };
  const cell = { padding: '13px 14px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#374151', verticalAlign: 'middle' };
  const inp  = { width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lbl  = { display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' };

  if (loading) return <div style={{ padding: '80px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading markets…</div>;
  if (error)   return <div style={{ padding: '60px 0', textAlign: 'center', color: '#ef4444', fontSize: 13 }}>{error}</div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}>← Dashboard</button>
        <span style={{ color: '#d1d5db', fontSize: 13 }}>›</span>
        <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Markets Overview</span>
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111', margin: '0 0 4px' }}>Markets Overview</h2>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>{markets.length} market{markets.length !== 1 ? 's' : ''} · click a row to drill in</p>
        </div>
        <button
          onClick={openModal}
          style={{ padding: '10px 20px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700, boxShadow: '0 2px 8px rgba(26,122,74,0.22)', whiteSpace: 'nowrap' }}
        >
          + Add New Market
        </button>
      </div>

      {/* Markets table */}
      {markets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '70px 0', color: '#9ca3af' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#374151', marginBottom: 6 }}>No market data yet</div>
          <div style={{ fontSize: 13 }}>Create your first market above, or run the backfill SQL once orders exist.</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.07), 0 4px 14px rgba(0,0,0,0.05)', border: '1px solid #f0f0ef', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={hdr}>Market</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Revenue</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Commission</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Rate</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Orders</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Haulers</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Clients</th>
                  <th style={hdr}></th>
                </tr>
              </thead>
              <tbody>
                {markets.map(function(m, idx) {
                  const isUnassigned = m.market_name === 'Unassigned';
                  return (
                    <tr
                      key={m.market_name}
                      onClick={function() { onSelect(m.market_name); }}
                      style={{ cursor: 'pointer', transition: 'background 0.12s' }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = '#f0fdf4'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = ''; }}
                    >
                      <td style={{ ...cell, fontWeight: 700 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                            background: isUnassigned ? '#f3f4f6' : (idx === 0 ? '#E8F5EE' : idx === 1 ? '#eff6ff' : idx === 2 ? '#f5f3ff' : '#fafafa'),
                            color: isUnassigned ? '#9ca3af' : (idx === 0 ? '#1A7A4A' : idx === 1 ? '#3b82f6' : idx === 2 ? '#7c3aed' : '#6b7280'),
                            fontSize: 12, fontWeight: 800,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>{isUnassigned ? '?' : idx + 1}</div>
                          <span style={{ color: isUnassigned ? '#9ca3af' : '#111' }}>{m.market_name}</span>
                        </div>
                      </td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: '#111' }}>{fmtMoney(m.revenue)}</td>
                      <td style={{ ...cell, textAlign: 'right', color: '#1A7A4A', fontWeight: 600 }}>{fmtMoney(m.commission)}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>
                        <span style={{ background: '#E8F5EE', color: '#1A7A4A', borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 700 }}>
                          {m.commission_percent.toFixed(1)}%
                        </span>
                      </td>
                      <td style={{ ...cell, textAlign: 'right' }}>{m.order_count}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{m.hauler_count}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{m.client_count}</td>
                      <td style={{ ...cell, color: '#1A7A4A', fontWeight: 600, whiteSpace: 'nowrap' }}>View →</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Coverage Map ── */}
      {markets.filter(function(m) { return m.market_name !== 'Unassigned' && m.market_name !== 'Default'; }).length > 0 && (
        <div style={{ marginTop: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', whiteSpace: 'nowrap' }}>Market Coverage Map</div>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            <div style={{ fontSize: 11, color: '#9ca3af' }}>Click a market to drill in</div>
          </div>
          <div
            ref={mapRef}
            style={{ width: '100%', height: 460, borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', background: '#f0f0ef' }}
          />
        </div>
      )}

      {/* ── Add Market Modal ── */}
      {showModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
          onClick={function(e) { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 14, padding: '32px 28px', width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>

            {created ? (
              /* ── Success state ── */
              <div>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ fontSize: 44, marginBottom: 10 }}>🎉</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 6 }}>Market created!</div>
                  <div style={{ fontSize: 14, color: '#6b7280' }}>
                    <strong style={{ color: '#1A7A4A' }}>{created.market_name}</strong> is now active.
                  </div>
                </div>

                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 18px', marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#166534', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Next Steps</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      'Orders placed at ZIPs starting with ' + (created.zip_prefixes || '(your prefixes)') + ' will auto-tag to this market.',
                      'Approve haulers who serve this area — they\'ll appear in the Market Detail portal under Haulers.',
                      'Set the Tambo fee % if needed in Mkt Settings tab.',
                      'Run the backfill SQL if you have existing orders in those ZIPs that need re-tagging.',
                    ].map(function(step, i) {
                      return (
                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: '#166534' }}>
                          <span style={{ background: '#1A7A4A', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                          <span>{step}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <button
                  onClick={function() { setShowModal(false); }}
                  style={{ width: '100%', padding: '11px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}
                >
                  Done
                </button>
              </div>
            ) : (
              /* ── Form state ── */
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111' }}>Add New Market</h3>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>New orders in these ZIPs will auto-tag to this market</p>
                  </div>
                  <button onClick={function() { setShowModal(false); }} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 4px' }}>✕</button>
                </div>

                {/* Market name */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Market Name *</label>
                  <input
                    style={inp}
                    placeholder="e.g. Austin"
                    value={form.market_name}
                    onChange={function(e) { setF('market_name', e.target.value); }}
                    autoFocus
                  />
                </div>

                {/* Fee % */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Tambo Fee %</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number" min="0" max="100" step="0.5"
                      style={{ ...inp, width: 90, textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#1A7A4A' }}
                      value={form.fee_percent}
                      onChange={function(e) { setF('fee_percent', e.target.value); }}
                    />
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#1A7A4A' }}>%</span>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>markup applied to hauler rates</span>
                  </div>
                </div>

                {/* Environmental fee */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Environmental Fee ($)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>$</span>
                    <input
                      type="number" min="0" step="0.01"
                      style={{ ...inp, width: 90, textAlign: 'center', fontSize: 15, fontWeight: 700 }}
                      value={form.environmental_fee}
                      onChange={function(e) { setF('environmental_fee', e.target.value); }}
                    />
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>flat fee per order (0 = disabled)</span>
                  </div>
                </div>

                {/* ZIP prefixes */}
                <div style={{ marginBottom: 6 }}>
                  <label style={lbl}>ZIP Prefixes (comma-separated)</label>
                  <input
                    style={inp}
                    placeholder="e.g. 787,786"
                    value={form.zip_prefixes}
                    onChange={function(e) { setF('zip_prefixes', e.target.value); }}
                  />
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                    Use the first 3 digits of every ZIP code in this market. Orders and clients with matching ZIPs auto-tag here.
                  </div>
                </div>

                {/* Taken ZIP prefixes warning */}
                {takenZips.length > 0 && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6 }}>Already assigned ZIP prefixes:</div>
                    {takenZips.map(function(tz) {
                      return (
                        <div key={tz.name} style={{ color: '#78350f', marginBottom: 3 }}>
                          <strong>{tz.name}:</strong> {tz.prefixes}
                        </div>
                      );
                    })}
                    <div style={{ color: '#b45309', marginTop: 6 }}>Avoid overlap to prevent orders being tagged to the wrong market.</div>
                  </div>
                )}

                {/* County discovery */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Counties</label>
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 9, padding: '14px 14px 12px' }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                      Enter a city to discover nearby counties, then check the ones this market should serve.
                    </div>

                    {/* City input + Discover button */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <input
                        style={{ ...inp, flex: 1 }}
                        placeholder="e.g. Austin, TX"
                        value={cityInput}
                        onChange={function(e) { setCityInput(e.target.value); }}
                        onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); discoverCounties(); } }}
                      />
                      <button
                        type="button"
                        onClick={discoverCounties}
                        disabled={discovering}
                        style={{ padding: '9px 14px', background: discovering ? '#e5e7eb' : '#111', color: discovering ? '#9ca3af' : '#fff', border: 'none', borderRadius: 7, cursor: discovering ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        {discovering ? 'Searching…' : 'Discover'}
                      </button>
                    </div>

                    {/* Error */}
                    {discoverErr && (
                      <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{discoverErr}</div>
                    )}

                    {/* County checklist */}
                    {nearCounties.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          Counties found nearby — select all that apply:
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '6px 12px' }}>
                          {nearCounties.map(function(county) {
                            const checked = selCounties.has(county);
                            return (
                              <label key={county} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, color: checked ? '#111' : '#6b7280', fontWeight: checked ? 600 : 400, padding: '4px 0' }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={function() { toggleCounty(county); }}
                                  style={{ width: 14, height: 14, accentColor: '#1A7A4A', cursor: 'pointer', flexShrink: 0 }}
                                />
                                {county}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Editable text fallback — always visible, synced with checkboxes */}
                  <input
                    style={{ ...inp, marginTop: 8, fontSize: 12, color: '#6b7280' }}
                    placeholder="Or type manually: Travis,Williamson"
                    value={form.counties}
                    onChange={function(e) {
                      setF('counties', e.target.value);
                      // Sync checkbox state to match manual edits
                      var typed = e.target.value.split(',').map(function(s) { return s.trim(); }).filter(Boolean);
                      setSelCounties(new Set(typed));
                    }}
                  />
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Counties auto-filled by checkboxes above, or type directly</div>
                </div>

                {/* Active toggle */}
                <div style={{ marginBottom: 22, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="mkt-active"
                    checked={form.is_active}
                    onChange={function(e) { setF('is_active', e.target.checked); }}
                    style={{ width: 16, height: 16, accentColor: '#1A7A4A', cursor: 'pointer' }}
                  />
                  <label htmlFor="mkt-active" style={{ fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>Active (immediately live)</label>
                </div>

                {saveError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>
                    {saveError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={function() { setShowModal(false); }}
                    style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveMarket}
                    disabled={saving}
                    style={{ flex: 2, padding: '10px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: saving ? 0.7 : 1 }}
                  >
                    {saving ? 'Creating…' : 'Create Market'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Level 3 – Individual Market Detail Portal
// ─────────────────────────────────────────────────────────────
function MarketDetailPortal({ marketName, onBack, onOpenHauler, onOpenClient, onNewInvoice, isOwner, onRecordPayment, onRemovePayment, onVoidInvoice, onDuplicateInvoice, onDownloadPdf }) {
  const [activeTab, setActiveTab] = useState('orders');
  const [haulVis, setHaulVis] = useState(10); // haulers pagination
  const [cliVis,  setCliVis]  = useState(10); // clients pagination
  const [orders,    setOrders]    = useState(null);
  const [haulers,   setHaulers]   = useState(null);
  const [clients,   setClients]   = useState(null);
  const [fin,       setFin]       = useState(null);
  const [requests,  setRequests]  = useState(null);
  const [loading,   setLoading]   = useState({});
  const [errors,    setErrors]    = useState({});

  const [confirmModal, setConfirmModal] = useState(null); // { message, onConfirm, label? }

  // ── Deletion confirmation flow ──
  const [delStep,    setDelStep]    = useState(0); // 0=closed 1=warning 2=type-confirm
  const [delChecked, setDelChecked] = useState(false);
  const [delInput,   setDelInput]   = useState('');
  const [deleting,   setDeleting]   = useState(false);
  const [delErr,     setDelErr]     = useState('');
  const [mktList,    setMktList]    = useState([]); // for "move to market" inline dropdown
  const [selOrder,      setSelOrder]      = useState(null); // order open in detail view
  const [selInv,        setSelInv]        = useState(null); // matching invoice
  const [selInvLoading, setSelInvLoading] = useState(false);
  const [mktVisible,    setMktVisible]    = useState(10);   // pagination
  const [mktActId,      setMktActId]      = useState(null); // invoice action in progress
  const [mktActErr,     setMktActErr]     = useState('');
  const [mktPayModal,   setMktPayModal]   = useState(null); // { inv }
  const [mktPayMethod,  setMktPayMethod]  = useState('cash');
  const [mktPayReason,  setMktPayReason]  = useState('');
  const [mktPaySaving,  setMktPaySaving]  = useState(false);
  const [mktPayError,   setMktPayError]   = useState('');

  const enc = encodeURIComponent(marketName);

  function load(tab, stateKey, setter) {
    if (loading[stateKey]) return;
    setLoading(function(p) { return { ...p, [stateKey]: true }; });
    setErrors(function(p) { return { ...p, [stateKey]: '' }; });
    adminFetch(`${BACKEND}/api/admin/markets/${enc}/${tab}`)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.error) throw new Error(d.error);
        setter(d[tab] || d);
      })
      .catch(function(err) { setErrors(function(p) { return { ...p, [stateKey]: err.message }; }); })
      .finally(function() { setLoading(function(p) { return { ...p, [stateKey]: false }; }); });
  }

  useEffect(function() {
    adminFetch(`${BACKEND}/api/admin/markets`)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        var names = (d.markets || [])
          .map(function(m) { return m.market_name; })
          .filter(function(n) { return n !== 'Default'; });
        if (!names.includes('Unassigned')) names.unshift('Unassigned');
        setMktList(names);
      })
      .catch(function() {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function removeOrder(orderId) {
    setOrders(function(prev) { return (prev || []).filter(function(o) { return o.id !== orderId; }); });
  }

  function refreshOrders() {
    setLoading(function(p) { return { ...p, orders: false }; });
    load('orders', 'orders', setOrders);
  }

  async function openOrder(o) {
    setSelOrder(o); setSelInv(null); setMktActErr('');
    if (o.invoice_id) {
      setSelInvLoading(true);
      try {
        const r = await fetch(`${BACKEND}/api/invoices/${o.invoice_id}`);
        const d = await r.json();
        if (r.ok) setSelInv(d.invoice || d);
      } catch (_) {}
      finally { setSelInvLoading(false); }
    }
  }

  async function mktSendInvoice(inv) {
    if (!inv.guest_email) { setMktActErr('No email on this invoice.'); return; }
    setMktActId(inv.id + '_send'); setMktActErr('');
    try {
      const r = await adminFetch(`${BACKEND}/api/invoices/${inv.id}/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inv.guest_email }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Send failed');
      setSelInv(function(p) { return { ...p, status: 'sent' }; });
    } catch (err) { setMktActErr(err.message); }
    finally { setMktActId(null); }
  }

  function mktMarkPaid(inv) {
    setMktPayModal({ inv });
    setMktPayMethod('cash');
    setMktPayReason('');
    setMktPayError('');
  }

  async function mktSubmitMarkPaid() {
    if (!mktPayReason.trim()) { setMktPayError('Please enter a reason/note.'); return; }
    setMktPaySaving(true); setMktPayError('');
    try {
      const r = await adminFetch(`${BACKEND}/api/invoices/${mktPayModal.inv.id}/pay`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          manual_override: true,
          payment_method: mktPayMethod,
          reason: mktPayReason,
          performed_by: ADMIN_EMAIL,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Mark paid failed');
      setSelInv(function(p) { return { ...p, ...d.invoice }; });
      setMktPayModal(null);
    } catch (err) { setMktPayError(err.message); }
    finally { setMktPaySaving(false); }
  }

  function mktUnmarkPaid(inv) {
    setConfirmModal({ message: 'Unmark this invoice as paid? It will go back to sent.', label: 'Unmark Paid', onConfirm: async function() {
      setMktActId(inv.id + '_unpay'); setMktActErr('');
      try {
        const r = await adminFetch(`${BACKEND}/api/invoices/${inv.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'sent' }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Unmark paid failed');
        setSelInv(function(p) { return { ...p, status: 'sent' }; });
      } catch (err) { setMktActErr(err.message); }
      finally { setMktActId(null); }
    }});
  }

  function mktDeleteInvoice(inv) {
    setConfirmModal({ message: 'Delete draft invoice? This cannot be undone.', label: 'Delete', onConfirm: async function() {
      setMktActId(inv.id + '_del'); setMktActErr('');
      try {
        const r = await adminFetch(`${BACKEND}/api/invoices/${inv.id}`, { method: 'DELETE' });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Delete failed');
        setSelInv(null);
      } catch (err) { setMktActErr(err.message); }
      finally { setMktActId(null); }
    }});
  }

  async function handleDelete() {
    setDeleting(true); setDelErr('');
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/markets/by-name/${enc}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Deletion failed');
      onBack(); // return to market list — market is gone
    } catch (err) {
      setDelErr(err.message);
      setDeleting(false);
    }
  }

  useEffect(function() { load('orders', 'orders', setOrders); }, [marketName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function() {
    if (activeTab === 'orders'     && orders   === null) load('orders',     'orders',   setOrders);
    if (activeTab === 'haulers'    && haulers  === null) load('haulers',    'haulers',  setHaulers);
    if (activeTab === 'clients'    && clients  === null) load('clients',    'clients',  setClients);
    if (activeTab === 'financials' && fin      === null) load('financials', 'fin',      setFin);
    if (activeTab === 'requests'   && requests === null) load('requests',   'requests', setRequests);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { key: 'orders',     label: 'Orders' },
    { key: 'haulers',    label: 'Haulers' },
    { key: 'clients',    label: 'Clients' },
    { key: 'financials', label: 'Financials' },
    { key: 'requests',   label: 'Requests' },
  ];

  const cell = { padding: '11px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12, color: '#374151', verticalAlign: 'middle' };
  const th   = { padding: '9px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', borderBottom: '1px solid #f0f0ef', background: '#fafafa', textAlign: 'left', whiteSpace: 'nowrap' };
  const Spin = function() { return <div style={{ padding: '50px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>; };
  const Err  = function({ msg }) { return <div style={{ padding: '40px 0', textAlign: 'center', color: '#ef4444', fontSize: 12 }}>{msg}</div>; };
  const Empty = function({ icon, msg }) { return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 13 }}>{msg}</div>
    </div>
  ); };

  function StatusDot({ status }) {
    const c = status === 'completed' ? '#1A7A4A' : status === 'accepted' ? '#3b82f6' : status === 'pending' ? '#f59e0b' : status === 'approved' ? '#1A7A4A' : '#9ca3af';
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, flexShrink: 0, display: 'inline-block' }} />
      <span style={{ fontSize: 11, fontWeight: 600, color: c }}>{status}</span>
    </span>;
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}>← Markets</button>
        <span style={{ color: '#d1d5db', fontSize: 13 }}>›</span>
        <span style={{ fontSize: 13, color: '#111', fontWeight: 600 }}>{marketName}</span>
      </div>

      {/* Market header */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #f0f0ef', padding: '22px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: 6 }}>Market</div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#111', margin: 0 }}>{marketName}</h2>
          </div>
          {fin && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: 4 }}>Revenue</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#111' }}>{fmtMoney(fin.revenue)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: 4 }}>Commission</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1A7A4A' }}>{fmtMoney(fin.commission)}</div>
                <div style={{ fontSize: 11, color: '#86efac', fontWeight: 600 }}>{fin.commission_percent.toFixed(1)}%</div>
              </div>
            </div>
          )}
        </div>
        {/* Danger zone — hidden for Unassigned which isn't a real configured market */}
        {marketName !== 'Unassigned' && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={function() { setDelStep(1); setDelChecked(false); setDelInput(''); setDelErr(''); }}
              style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 11px', letterSpacing: '0.03em' }}
            >
              Delete Market
            </button>
          </div>
        )}
      </div>

      {/* ── 3-step deletion modal ── */}
      {delStep > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px 24px', width: '100%', maxWidth: 480, boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>

            {/* Step 1 — warning + impact + checkbox */}
            {delStep === 1 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                  <span style={{ fontSize: 28 }}>⚠️</span>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>Delete {marketName}?</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>This action cannot be undone</div>
                  </div>
                </div>

                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>What will happen</div>
                  {[
                    'All orders in this market will be moved to Unassigned',
                    'The market fee % and ZIP prefix settings are permanently removed',
                    'New orders at those ZIPs will fall through to Unassigned',
                    'Order history, hauler records, and invoices are preserved',
                  ].map(function(item, i) {
                    return (
                      <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#7f1d1d', marginBottom: 4 }}>
                        <span style={{ color: '#dc2626', flexShrink: 0 }}>•</span>
                        <span>{item}</span>
                      </div>
                    );
                  })}
                  {fin && fin.order_count > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #fecaca', fontSize: 12, color: '#991b1b', fontWeight: 700 }}>
                      {fin.order_count} orders ({fmtMoney(fin.revenue)} revenue) will be unassigned.
                    </div>
                  )}
                </div>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 22, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                  <input
                    type="checkbox"
                    checked={delChecked}
                    onChange={function(e) { setDelChecked(e.target.checked); }}
                    style={{ width: 16, height: 16, accentColor: '#dc2626', cursor: 'pointer', flexShrink: 0, marginTop: 2 }}
                  />
                  I understand this will permanently delete the <strong style={{ color: '#111', margin: '0 3px' }}>{marketName}</strong> market and unassign all its orders.
                </label>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={function() { setDelStep(0); }}
                    style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={function() { setDelStep(2); }}
                    disabled={!delChecked}
                    style={{ flex: 2, padding: '10px 0', background: delChecked ? '#dc2626' : '#f3f4f6', color: delChecked ? '#fff' : '#9ca3af', border: 'none', borderRadius: 9, cursor: delChecked ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700, transition: 'background 0.15s' }}
                  >
                    Continue →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 — type market name to confirm */}
            {delStep === 2 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                  <span style={{ fontSize: 28 }}>🔴</span>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>Final confirmation</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Type the market name to unlock deletion</div>
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                    Type <strong style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '2px 7px', borderRadius: 4, fontSize: 13 }}>{marketName}</strong> to confirm:
                  </div>
                  <input
                    autoFocus
                    value={delInput}
                    onChange={function(e) { setDelInput(e.target.value); setDelErr(''); }}
                    onKeyDown={function(e) { if (e.key === 'Enter' && delInput === marketName && !deleting) handleDelete(); }}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid ' + (delInput === marketName ? '#dc2626' : '#e5e7eb'), borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace', letterSpacing: '0.02em', transition: 'border-color 0.15s' }}
                    placeholder={marketName}
                  />
                </div>

                {delErr && (
                  <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
                    {delErr}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={function() { setDelStep(1); }}
                    disabled={deleting}
                    style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={delInput !== marketName || deleting}
                    style={{ flex: 2, padding: '10px 0', background: delInput === marketName && !deleting ? '#dc2626' : '#f3f4f6', color: delInput === marketName && !deleting ? '#fff' : '#9ca3af', border: 'none', borderRadius: 9, cursor: delInput === marketName && !deleting ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700 }}
                  >
                    {deleting ? 'Deleting…' : 'Delete Market'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid #e5e7eb', marginBottom: 22 }}>
        {tabs.map(function(t) {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={function() { setActiveTab(t.key); }}
              style={{ padding: '8px 18px', background: 'none', border: 'none', borderBottom: isActive ? '2px solid #1A7A4A' : '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? '#1A7A4A' : '#6b7280', transition: 'color 0.12s' }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Orders tab ── */}
      {activeTab === 'orders' && (
        loading.orders ? <Spin /> : errors.orders ? <Err msg={errors.orders} /> :
        !orders || orders.length === 0 ? (
          <div>
            {onNewInvoice && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button
                  onClick={function() { onNewInvoice(refreshOrders); }}
                  style={{ background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  + New Invoice
                </button>
              </div>
            )}
            <Empty icon="📋" msg="No orders in this market yet." />
          </div>
        ) :
        selOrder ? (
          /* ── Detail view ── */
          <div>
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={function() { setSelOrder(null); setSelInv(null); setMktActErr(''); }}
                style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}
              >
                ← Back to orders
              </button>
            </div>
            <OrderDetail
              order={selOrder}
              onMarketChanged={function(id, mkt) {
                setSelOrder(function(p) { return { ...p, market_name: mkt }; });
                removeOrder(id);
              }}
              onClientUpdated={function(fields) { setSelOrder(function(p) { return { ...p, ...fields }; }); }}
            />
            {selInvLoading && (
              <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading invoice…</div>
            )}
            {selInv && !selInvLoading && (
              <div style={{ marginTop: 28 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>Billing</span>
                  <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                </div>
                <InvoiceDetail
                  inv={selInv}
                  actionId={mktActId}
                  error={mktActErr}
                  isOwner={isOwner}
                  onSend={function() { mktSendInvoice(selInv); }}
                  onMarkPaid={function() { mktMarkPaid(selInv); }}
                  onUnmarkPaid={function() { mktUnmarkPaid(selInv); }}
                  onDelete={function() { mktDeleteInvoice(selInv); }}
                  onRecordPayment={onRecordPayment ? function() { onRecordPayment(selInv); } : undefined}
                  onRemovePayment={onRemovePayment ? function(pid) { onRemovePayment(selInv, pid); } : undefined}
                  onVoid={onVoidInvoice ? function() { onVoidInvoice(selInv); } : undefined}
                  onDuplicate={onDuplicateInvoice ? function() { onDuplicateInvoice(selInv); } : undefined}
                  onDownloadPdf={onDownloadPdf ? function() { onDownloadPdf(selInv); } : undefined}
                />
              </div>
            )}
          </div>
        ) : (
          /* ── Paginated list ── */
          <div>
            {onNewInvoice && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button
                  onClick={function() { onNewInvoice(refreshOrders); }}
                  style={{ background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  + New Invoice
                </button>
              </div>
            )}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0ef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead><tr>
                  <th style={th}>Order #</th>
                  <th style={th}>Client</th>
                  <th style={th}>Size</th>
                  <th style={th}>Hauler</th>
                  <th style={th}>Status</th>
                  <th style={{ ...th, textAlign: 'right' }}>Charged</th>
                  <th style={{ ...th, textAlign: 'right' }}>Commission</th>
                  <th style={th}>Date</th>
                  {mktList.length > 0 && <th style={th}>Move to Market</th>}
                </tr></thead>
                <tbody>
                  {orders.slice(0, mktVisible).map(function(o) {
                    return (
                      <tr
                        key={o.id}
                        onClick={function() { openOrder(o); }}
                        style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={function(e) { e.currentTarget.style.background = '#f0f9ff'; }}
                        onMouseLeave={function(e) { e.currentTarget.style.background = ''; }}
                      >
                        <td style={{ ...cell, fontWeight: 700, color: '#1A7A4A' }}>{o.order_number}</td>
                        <td style={cell}>{o.guest_name || o.guest_email || '—'}</td>
                        <td style={cell}>{SIZE_LABELS[o.size] || o.size || '—'}</td>
                        <td style={{ ...cell, color: '#6b7280' }}>{o.hauler_name || '—'}</td>
                        <td style={cell}><StatusDot status={o.status} /></td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(o.total_charged)}</td>
                        <td style={{ ...cell, textAlign: 'right', color: '#1A7A4A', fontWeight: 600 }}>{fmtMoney(o.tambo_commission)}</td>
                        <td style={{ ...cell, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(o.created_at)}</td>
                        {mktList.length > 0 && (
                          <td style={cell} onClick={function(e) { e.stopPropagation(); }}>
                            <MoveCell
                              orderId={o.id}
                              currentMarket={o.market_name || 'Unassigned'}
                              mktList={mktList}
                              onMoved={removeOrder}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginBottom: 8 }}>
                Showing {Math.min(mktVisible, orders.length)} of {orders.length} orders
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {orders.length > mktVisible && (
                  <button
                    onClick={function() { setMktVisible(function(v) { return v + 10; }); }}
                    style={{ background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Show More
                  </button>
                )}
                {mktVisible > 10 && (
                  <button
                    onClick={function() { setMktVisible(10); }}
                    style={{ background: 'transparent', color: '#1A7A4A', border: '2px solid #1A7A4A', borderRadius: 7, padding: '6px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Show Less
                  </button>
                )}
              </div>
            </div>
          </div>
          </div>
        )
      )}

      {/* ── Haulers tab ── */}
      {activeTab === 'haulers' && (
        loading.haulers ? <Spin /> : errors.haulers ? <Err msg={errors.haulers} /> :
        !haulers || haulers.length === 0 ? <Empty icon="🚛" msg="No haulers are assigned to this market yet. Set a market when adding a hauler, or update existing haulers via SQL." /> : (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0ef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead><tr>
                  <th style={th}>Company</th>
                  <th style={th}>Contact</th>
                  <th style={th}>Phone</th>
                  <th style={th}>Status</th>
                  <th style={{ ...th, textAlign: 'right' }}>Dispatched</th>
                  <th style={{ ...th, textAlign: 'right' }}>Accepted</th>
                </tr></thead>
                <tbody>
                  {haulers.slice(0, haulVis).map(function(h) {
                    return (
                      <tr
                        key={h.id}
                        onClick={function() { if (onOpenHauler) onOpenHauler(h); }}
                        style={{ cursor: onOpenHauler ? 'pointer' : 'default', transition: 'background 0.1s' }}
                        onMouseEnter={function(e) { e.currentTarget.style.background = '#f0f9ff'; }}
                        onMouseLeave={function(e) { e.currentTarget.style.background = ''; }}
                      >
                        <td style={{ ...cell, fontWeight: 700, color: '#111' }}>
                          {h.company_name}
                          {onOpenHauler && <span style={{ marginLeft: 6, fontSize: 10, color: '#9ca3af' }}>→</span>}
                        </td>
                        <td style={{ ...cell, color: '#6b7280' }}>{h.owner_first_name} {h.owner_last_name}</td>
                        <td style={cell}>{h.phone || '—'}</td>
                        <td style={cell}><StatusDot status={h.status} /></td>
                        <td style={{ ...cell, textAlign: 'right' }}>{h.total_dispatches}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: '#1A7A4A' }}>{h.accepted_jobs}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginBottom: 8 }}>
                Showing {Math.min(haulVis, haulers.length)} of {haulers.length} haulers
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {haulers.length > haulVis && (
                  <button onClick={function() { setHaulVis(function(v) { return v + 10; }); }}
                    style={{ background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Show More
                  </button>
                )}
                {haulVis > 10 && (
                  <button onClick={function() { setHaulVis(10); }}
                    style={{ background: 'transparent', color: '#1A7A4A', border: '2px solid #1A7A4A', borderRadius: 7, padding: '6px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Show Less
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      )}

      {/* ── Clients tab ── */}
      {activeTab === 'clients' && (
        loading.clients ? <Spin /> : errors.clients ? <Err msg={errors.clients} /> :
        !clients || clients.length === 0 ? <Empty icon="👤" msg="No clients have placed orders in this market yet." /> : (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0ef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead><tr>
                  <th style={th}>Name</th>
                  <th style={th}>Email</th>
                  <th style={th}>Rating</th>
                  <th style={{ ...th, textAlign: 'right' }}>Orders</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total Spent</th>
                </tr></thead>
                <tbody>
                  {clients.slice(0, cliVis).map(function(c, i) {
                    const isRegistered = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.client_id || '');
                    const ratingColor = c.avg_rating >= 4 ? '#10b981' : c.avg_rating >= 3 ? '#f59e0b' : c.avg_rating ? '#DC2626' : '#d1d5db';
                    return (
                      <tr
                        key={i}
                        onClick={function() { if (onOpenClient) onOpenClient(c); }}
                        style={{ cursor: onOpenClient ? 'pointer' : 'default', transition: 'background 0.1s' }}
                        onMouseEnter={function(e) { e.currentTarget.style.background = '#f0f9ff'; }}
                        onMouseLeave={function(e) { e.currentTarget.style.background = ''; }}
                      >
                        <td style={{ ...cell, fontWeight: 600, color: '#111' }}>
                          {c.name || '—'}
                          {isRegistered && onOpenClient && <span style={{ marginLeft: 6, fontSize: 10, color: '#9ca3af' }}>→</span>}
                          {!isRegistered && <span style={{ marginLeft: 6, fontSize: 10, color: '#d1d5db' }}>guest</span>}
                        </td>
                        <td style={{ ...cell, color: '#6b7280' }}>{c.email || '—'}</td>
                        <td style={cell}>
                          <span style={{ fontWeight: 700, color: ratingColor, fontSize: 12 }}>
                            {c.avg_rating ? `★ ${Number(c.avg_rating).toFixed(1)}` : '—'}
                          </span>
                          {c.review_count > 0 && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 3 }}>({c.review_count})</span>}
                        </td>
                        <td style={{ ...cell, textAlign: 'right' }}>{c.order_count}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: '#1A7A4A' }}>{fmtMoney(c.total_spent)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginBottom: 8 }}>
                Showing {Math.min(cliVis, clients.length)} of {clients.length} clients
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {clients.length > cliVis && (
                  <button onClick={function() { setCliVis(function(v) { return v + 10; }); }}
                    style={{ background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Show More
                  </button>
                )}
                {cliVis > 10 && (
                  <button onClick={function() { setCliVis(10); }}
                    style={{ background: 'transparent', color: '#1A7A4A', border: '2px solid #1A7A4A', borderRadius: 7, padding: '6px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Show Less
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      )}

      {/* ── Financials tab ── */}
      {activeTab === 'financials' && (
        loading.fin ? <Spin /> : errors.fin ? <Err msg={errors.fin} /> :
        !fin ? null : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            {[
              { label: 'Total Revenue',     value: fmtMoney(fin.revenue),                  color: '#111',    bg: '#fff' },
              { label: 'Tambo Commission',  value: fmtMoney(fin.commission),               color: '#1A7A4A', bg: '#f0fdf4' },
              { label: 'Hauler Payouts',    value: fmtMoney(fin.hauler_payouts),            color: '#1d4ed8', bg: '#eff6ff' },
              { label: 'Commission Rate',   value: fin.commission_percent.toFixed(1) + '%', color: '#1A7A4A', bg: '#fff' },
              { label: 'Completed Orders',  value: fin.completed_orders,                    color: '#111',    bg: '#fff' },
              { label: 'Active Orders',     value: fin.active_orders,                       color: '#111',    bg: '#fff' },
              { label: 'Pending Orders',    value: fin.pending_orders,                      color: '#111',    bg: '#fff' },
              { label: 'Manual Invoices',   value: `${fin.manual_invoice_count} (${fmtMoney(fin.manual_invoice_revenue)})`, color: '#111', bg: '#fff' },
              { label: 'Pending Payouts',   value: `${fin.pending_payout_count || 0} jobs · ${fmtMoney(fin.pending_payout_amount || 0)}`, color: '#f59e0b', bg: '#fffbeb' },
            ].map(function(item) {
              return (
                <div key={item.label} style={{ background: item.bg, border: '1px solid #f0f0ef', borderRadius: 12, padding: '20px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: 8 }}>{item.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: item.color }}>{item.value}</div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Requests tab ── */}
      {activeTab === 'requests' && (
        loading.requests ? <Spin /> : errors.requests ? <Err msg={errors.requests} /> :
        !requests || requests.length === 0 ? <Empty icon="✅" msg="No pending requests for this market." /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {requests.map(function(req) {
              const isCancel = req.request_type === 'cancel';
              return (
                <div key={req.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 20, background: isCancel ? '#fef2f2' : '#eff6ff', color: isCancel ? '#dc2626' : '#1d4ed8', border: `1px solid ${isCancel ? '#fca5a5' : '#93c5fd'}` }}>
                      {isCancel ? 'CANCEL' : 'RESCHEDULE'}
                    </span>
                    <span style={{ fontWeight: 800, fontSize: 14, color: '#111' }}>#{req.order_number}</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>— {req.size} · {req.order_status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {req.guest_name && <span style={{ marginRight: 14 }}>Client: <strong>{req.guest_name}</strong></span>}
                    {req.reason && <span>Reason: {req.reason}</span>}
                  </div>
                  {req.proposed_date && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#374151' }}>Proposed: {fmtDate(req.proposed_date)}</div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>Submitted {fmtDate(req.created_at)}</div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Market portal: Mark Invoice Paid modal ── */}
      {mktPayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}
          onClick={function(e) { if (e.target === e.currentTarget && !mktPaySaving) setMktPayModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>Mark Invoice Paid</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{displayNum(mktPayModal.inv)} — {fmtMoney(mktPayModal.inv.total)}</div>
              </div>
              <button onClick={function() { setMktPayModal(null); }} disabled={mktPaySaving} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Payment Method *</label>
                <select value={mktPayMethod} onChange={function(e) { setMktPayMethod(e.target.value); }} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13 }}>
                  {['cash', 'zelle', 'check', 'other'].map(function(m) { return <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>; })}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reason / Note *</label>
                <textarea value={mktPayReason} onChange={function(e) { setMktPayReason(e.target.value); }} rows={3} placeholder="e.g., Client paid via Zelle on 7/2/2026" style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            {mktPayError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{mktPayError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={function() { setMktPayModal(null); }} disabled={mktPaySaving} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={mktSubmitMarkPaid} disabled={mktPaySaving} style={{ flex: 2, padding: '10px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: mktPaySaving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: mktPaySaving ? 0.7 : 1 }}>
                {mktPaySaving ? 'Saving…' : 'Mark as Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <p style={{ fontSize: 15, color: '#111', lineHeight: 1.55, margin: '0 0 24px' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={function() { setConfirmModal(null); }} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={function() { var fn = confirmModal.onConfirm; setConfirmModal(null); fn(); }} style={{ flex: 1, padding: '10px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>{confirmModal.label || 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showAdminPwd, setShowAdminPwd] = useState(false);
  const [authError, setAuthError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const [tab, setTab] = useState('dashboard');

  const [dashSummary, setDashSummary] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState('');

  const [marketPortalView, setMarketPortalView] = useState('list'); // 'list' | 'detail'
  const [selectedMarketName, setSelectedMarketName] = useState(null);
  const [mktReturnCtx, setMktReturnCtx] = useState(null); // { marketName } — set when navigating from market portal to main tab

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');

  const [haulers, setHaulers] = useState([]);
  const [haulersLoading, setHaulersLoading] = useState(false);
  const [haulersError, setHaulersError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState('');
  const [expandedHaulerId, setExpandedHaulerId] = useState(null);
  const [inlineMarketDraft, setInlineMarketDraft] = useState('');
  const [inlineMarketSaving, setInlineMarketSaving] = useState(false);
  const [inlineMarketMsg, setInlineMarketMsg] = useState('');
  const [suspendLoading, setSuspendLoading] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(null);
  const [docUrls, setDocUrls] = useState({});
  const [docUrlsLoading, setDocUrlsLoading] = useState({});

  const [editingHauler, setEditingHauler] = useState(null);
  const [haulerDraft, setHaulerDraft] = useState({});
  const [haulerEditSaving, setHaulerEditSaving] = useState(false);
  const [haulerEditError, setHaulerEditError] = useState('');
  const [haulerEditSuccess, setHaulerEditSuccess] = useState(false);

  const [editingClient, setEditingClient] = useState(null);
  const [clientDraft, setClientDraft] = useState({});
  const [clientEditSaving, setClientEditSaving] = useState(false);
  const [clientEditError, setClientEditError] = useState('');
  const [clientEditSuccess, setClientEditSuccess] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invError, setInvError] = useState('');
  const [invSendMsg, setInvSendMsg] = useState('');
  const [invActionId, setInvActionId] = useState(null);
  const [editingInv, setEditingInv] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [invDeleteTarget, setInvDeleteTarget] = useState(null);
  const [selectedInv, setSelectedInv] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [showNewInv, setShowNewInv] = useState(false);
  const [newInv, setNewInv] = useState(emptyInvoice());
  const [newInvSaving, setNewInvSaving] = useState(false);
  const [newInvError, setNewInvError] = useState('');

  const invAddrInputRef = useRef(null);
  const invAddrAcRef = useRef(null);
  const invSvcAddrInputRef = useRef(null);
  const invSvcAddrAcRef = useRef(null);
  const [newInvSameAsBilling, setNewInvSameAsBilling] = useState(true);
  const [editSameAsBilling, setEditSameAsBilling] = useState(false);
  const editSvcAddrInputRef = useRef(null);
  const editSvcAddrAcRef = useRef(null);
  const mktPortalRefreshRef = useRef(null); // refresh fn set when modal opened from a market portal
  const [invLockedMarket, setInvLockedMarket] = useState(null); // non-null = market dropdown locked to this value
  const [invAvailable, setInvAvailable] = useState(null);
  const [invAvailLoading, setInvAvailLoading] = useState(false);
  const [invAvailError, setInvAvailError] = useState('');
  const [invDuration, setInvDuration] = useState('3day');
  const [invSelectedHauler, setInvSelectedHauler] = useState(null);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [clientDetails, setClientDetails] = useState({});
  const [clientActionLoading, setClientActionLoading] = useState(null);
  const [clientActionError, setClientActionError] = useState('');
  const [clientRemoveLoading, setClientRemoveLoading] = useState(null);
  const [rejectClientModal, setRejectClientModal] = useState(null); // { id, name }
  const [rejectReason, setRejectReason] = useState('');

  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState('');
  const [requestActionId, setRequestActionId] = useState(null);
  const [requestSuccessId, setRequestSuccessId] = useState(null);

  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInResult, setCheckInResult] = useState('');
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderResult, setReminderResult] = useState('');

  const [reassignQueue, setReassignQueue] = useState([]);
  const [reassignQueueLoading, setReassignQueueLoading] = useState(false);
  const [reassignQueueError, setReassignQueueError] = useState('');
  const [reassignQueueHaulers, setReassignQueueHaulers] = useState({});
  const [reassignQueueHaulersLoading, setReassignQueueHaulersLoading] = useState({});
  const [reassignQueueSelected, setReassignQueueSelected] = useState({});
  const [reassignQueueAssigning, setReassignQueueAssigning] = useState(null);
  const [reassignQueueMsg, setReassignQueueMsg] = useState({});

  const [markets, setMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [marketsError, setMarketsError] = useState('');
  const [marketDrafts, setMarketDrafts] = useState({});
  const [marketSaving, setMarketSaving] = useState(null);
  const [marketSaveSuccess, setMarketSaveSuccess] = useState(null);

  const [ordersVisible, setOrdersVisible] = useState(10);
  const [orderSearch, setOrderSearch] = useState('');
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('all');
  const ordersListRef = useRef(null);

  // ── Owner-only state ──
  const [isOwner, setIsOwner] = useState(false);
  const [deletedOrders, setDeletedOrders] = useState([]);
  const [deletedInvoices, setDeletedInvoices] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  // Soft-delete order dialog
  const [orderDeleteTarget, setOrderDeleteTarget] = useState(null);
  const [orderDeleteTextHauler, setOrderDeleteTextHauler] = useState(false);
  const [orderDeleteLoading, setOrderDeleteLoading] = useState(false);
  const [orderDeleteError, setOrderDeleteError] = useState('');
  const [orderDeleteWarnPaid, setOrderDeleteWarnPaid] = useState(false);
  const [orderDeleteWarnOnSite, setOrderDeleteWarnOnSite] = useState(false);
  // Permanent delete order
  const [orderPermDeleteTarget, setOrderPermDeleteTarget] = useState(null);
  const [orderPermDeleteConfirm, setOrderPermDeleteConfirm] = useState('');
  const [orderPermDeleteLoading, setOrderPermDeleteLoading] = useState(false);
  const [orderPermDeleteError, setOrderPermDeleteError] = useState('');
  // Soft-delete standalone invoice (owner path)
  const [invOwnerDeleteTarget, setInvOwnerDeleteTarget] = useState(null);
  const [invOwnerDeleteLoading, setInvOwnerDeleteLoading] = useState(false);
  // Permanent delete standalone invoice
  const [invPermDeleteTarget, setInvPermDeleteTarget] = useState(null);
  const [invPermDeleteConfirm, setInvPermDeleteConfirm] = useState('');
  const [invPermDeleteLoading, setInvPermDeleteLoading] = useState(false);
  const [invPermDeleteError, setInvPermDeleteError] = useState('');

  const [rejections, setRejections] = useState([]);
  const [showRejectionPanel, setShowRejectionPanel] = useState(false);
  const [selectedRejection, setSelectedRejection] = useState(null);
  const [rejectionHaulers, setRejectionHaulers] = useState([]);
  const [rejectionHaulersLoading, setRejectionHaulersLoading] = useState(false);
  const [redispatchLoading, setRedispatchLoading] = useState(null);

  const [staleExpanded, setStaleExpanded] = useState(false);
  const [markContactedLoading, setMarkContactedLoading] = useState(null);
  const [createPayoutLoading, setCreatePayoutLoading] = useState(null); // order id or null

  // ── Payouts tab ──
  const [payoutsSummary, setPayoutsSummary] = useState(null);
  const [payoutsList, setPayoutsList]       = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsError, setPayoutsError]     = useState('');
  const [payoutsStatusFilter, setPayoutsStatusFilter] = useState('pending');
  const [payoutsMarketFilter, setPayoutsMarketFilter] = useState('all');
  const [payModal, setPayModal] = useState(null); // { mode, haulerName, payoutIds, totalAmount }
  const [payForm, setPayForm]   = useState({ payment_reference: '', payment_method: 'ACH', paid_at: '', notes: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError]     = useState('');
  const [payoutsVisible, setPayoutsVisible] = useState(10);
  // Payout detail view
  const [payoutDetailPayout,     setPayoutDetailPayout]     = useState(null);
  const [payoutDetailOrder,      setPayoutDetailOrder]      = useState(null);
  const [payoutDetailInv,        setPayoutDetailInv]        = useState(null);
  const [payoutDetailInvLoading, setPayoutDetailInvLoading] = useState(false);

  // ── Add Hauler modal ──
  const [showAddHauler, setShowAddHauler]   = useState(false);
  const emptyHaulerForm = { first_name: '', last_name: '', email: '', phone: '', company_name: '', market_name: '', sizes: [], notes: '' };
  const [addHaulerForm, setAddHaulerForm]   = useState(emptyHaulerForm);
  const [addHaulerLoading, setAddHaulerLoading] = useState(false);
  const [addHaulerError, setAddHaulerError]     = useState('');
  const [addHaulerResult, setAddHaulerResult]   = useState(null);

  // ── Send Setup Link ──
  const [setupLinkLoading, setSetupLinkLoading] = useState(null); // hauler id or null
  const [setupLinkSent, setSetupLinkSent]       = useState({}); // { [haulerId]: sentAt }

  // ── Invoice Mark Paid modal ──
  const [invPayModal,  setInvPayModal]  = useState(null); // { inv, context: 'orders' | 'payouts' }
  const [invPayMethod, setInvPayMethod] = useState('cash');
  const [invPayReason, setInvPayReason] = useState('');
  const [invPaySaving, setInvPaySaving] = useState(false);
  const [invPayError,  setInvPayError]  = useState('');
  const [confirmModal, setConfirmModal] = useState(null); // { message, onConfirm, label? }
  // ── Invoice Record Payment modal (partial/full) ──
  const [invRecordPayModal,  setInvRecordPayModal]  = useState(null); // inv
  const emptyPayForm = function() { return { amount: '', method: 'cash', reference: '', date: new Date().toISOString().split('T')[0], note: '' }; };
  const [invRecordPayForm,   setInvRecordPayForm]   = useState(emptyPayForm());
  const [invRecordPayError,  setInvRecordPayError]  = useState('');
  const [invRecordPaySaving, setInvRecordPaySaving] = useState(false);
  // ── Invoice Void / Duplicate ──

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: listener } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => listener.subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session && session.user.email === ADMIN_EMAIL) {
      fetchOrders(); fetchInvoices(); fetchRequests(); fetchDashboard(); fetchMe();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    if (!session || session.user.email !== ADMIN_EMAIL) return;
    if (tab === 'dashboard') fetchDashboard();
    if (tab === 'market-portal') setMarketPortalView('list');
    if (tab === 'haulers') { fetchHaulers(); if (markets.length === 0) fetchMarkets(); }
    if (tab === 'clients' && clients.length === 0) fetchClients();
    if (tab === 'requests') fetchRequests();
    if (tab === 'reassign') fetchReassignQueue();
    if (tab === 'markets') fetchMarkets();
    if (tab === 'payouts') { fetchPayoutsSummary(); fetchPayouts(); if (markets.length === 0) fetchMarkets(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(function() {
    if (tab === 'payouts' && session && session.user.email === ADMIN_EMAIL) fetchPayouts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutsStatusFilter, payoutsMarketFilter]);

  useEffect(function() {
    if (!expandedHaulerId) { setInlineMarketMsg(''); return; }
    var found = haulers.find(function(x) { return x.id === expandedHaulerId; });
    setInlineMarketDraft(found ? (found.market_name || '') : '');
    setInlineMarketMsg('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedHaulerId]);

  // Poll for unacknowledged hauler rejections every 30s + on window focus
  useEffect(function() {
    if (!session || session.user.email !== ADMIN_EMAIL) return;
    function pollRejections() {
      adminFetch(`${BACKEND}/api/admin/rejections/unacknowledged`)
        .then(function(r) { return r.json(); })
        .then(function(d) { setRejections(d.rejections || []); })
        .catch(function(err) { console.error('[rejections poll]', err.message); });
    }
    pollRejections();
    const iv = setInterval(pollRejections, 30000);
    window.addEventListener('focus', pollRejections);
    return function() { clearInterval(iv); window.removeEventListener('focus', pollRejections); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Load Google Maps + init Places Autocomplete on the invoice address field when modal opens
  useEffect(function() {
    if (!showNewInv) {
      invAddrAcRef.current = null;
      invSvcAddrAcRef.current = null;
      setInvAvailable(null);
      setInvAvailError('');
      setInvSelectedHauler(null);
      setInvLockedMarket(null);
      setNewInvSameAsBilling(true);
      mktPortalRefreshRef.current = null;
      return;
    }
    if (markets.length === 0) fetchMarkets();
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;

    function initInvAc() {
      if (!window.google?.maps?.places || !invAddrInputRef.current || invAddrAcRef.current) return;
      invAddrAcRef.current = new window.google.maps.places.Autocomplete(
        invAddrInputRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['address_components', 'formatted_address', 'geometry'] }
      );
      invAddrAcRef.current.addListener('place_changed', function() {
        const place = invAddrAcRef.current.getPlace();
        if (!place.address_components) return;
        const get = function(t) { const c = place.address_components.find(function(c) { return c.types.includes(t); }); return c ? c.short_name : ''; };
        const zip = get('postal_code');
        const fullAddr = place.formatted_address || invAddrInputRef.current.value;
        const suggestedMarket = resolveMarketFromZip(zip, markets);
        setNewInv(function(p) { return { ...p, billing_address: fullAddr, market_name: suggestedMarket }; });
        if (zip) {
          const lat = place.geometry?.location?.lat();
          const lng = place.geometry?.location?.lng();
          lookupInvAvailability(zip, lat, lng);
        }
      });
    }

    if (window.google?.maps?.places) {
      setTimeout(initInvAc, 50);
    } else if (!document.getElementById('gmaps-script')) {
      window.__gmapsReady = initInvAc;
      const s = document.createElement('script');
      s.id = 'gmaps-script';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&solution_channel=GMP_QB_addressselection_v1&callback=__gmapsReady`;
      document.head.appendChild(s);
    } else {
      window.__gmapsReady = initInvAc;
    }
  }, [showNewInv]); // eslint-disable-line react-hooks/exhaustive-deps

  // Service address autocomplete for new invoice (activates when user unchecks same-as-billing)
  useEffect(function() {
    if (!showNewInv || newInvSameAsBilling) { invSvcAddrAcRef.current = null; return; }
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;
    function initSvcAc() {
      if (!window.google?.maps?.places || !invSvcAddrInputRef.current || invSvcAddrAcRef.current) return;
      invSvcAddrAcRef.current = new window.google.maps.places.Autocomplete(
        invSvcAddrInputRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['formatted_address'] }
      );
      invSvcAddrAcRef.current.addListener('place_changed', function() {
        const place = invSvcAddrAcRef.current.getPlace();
        if (place.formatted_address) setNewInv(function(p) { return { ...p, service_address: place.formatted_address }; });
      });
    }
    if (window.google?.maps?.places) { setTimeout(initSvcAc, 50); }
    else { const prev = window.__gmapsReady; window.__gmapsReady = function() { if (prev) prev(); initSvcAc(); }; }
  }, [showNewInv, newInvSameAsBilling]); // eslint-disable-line react-hooks/exhaustive-deps

  // Service address autocomplete for edit invoice
  useEffect(function() {
    if (!editSameAsBilling || !editSvcAddrInputRef.current) { editSvcAddrAcRef.current = null; return; }
  }, [editSameAsBilling]);
  useEffect(function() {
    if (editSameAsBilling) { editSvcAddrAcRef.current = null; return; }
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;
    function initEditSvcAc() {
      if (!window.google?.maps?.places || !editSvcAddrInputRef.current || editSvcAddrAcRef.current) return;
      editSvcAddrAcRef.current = new window.google.maps.places.Autocomplete(
        editSvcAddrInputRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['formatted_address'] }
      );
      editSvcAddrAcRef.current.addListener('place_changed', function() {
        const place = editSvcAddrAcRef.current.getPlace();
        if (place.formatted_address) setEditDraft(function(p) { return { ...p, service_address: place.formatted_address }; });
      });
    }
    if (window.google?.maps?.places) { setTimeout(initEditSvcAc, 50); }
    else { const prev = window.__gmapsReady; window.__gmapsReady = function() { if (prev) prev(); initEditSvcAc(); }; }
  }, [editSameAsBilling]); // eslint-disable-line react-hooks/exhaustive-deps

  async function lookupInvAvailability(zip, lat, lng) {
    setInvAvailLoading(true); setInvAvailError(''); setInvAvailable(null); setInvSelectedHauler(null);
    try {
      const params = new URLSearchParams({ zip });
      if (lat != null) params.set('lat', lat);
      if (lng != null) params.set('lng', lng);
      const res = await fetch(`${BACKEND}/api/orders/search?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      setInvAvailable(data.results || []);
    } catch (err) { setInvAvailError(err.message); setInvAvailable([]); }
    finally { setInvAvailLoading(false); }
  }

  function addDumpsterToInvoice(result) {
    const durOpt = DURATION_OPTS.find(function(d) { return d.key === invDuration; }) || DURATION_OPTS[0];
    const description = `${result.label} Dumpster Rental – ${durOpt.label}`;
    const rate = result[durOpt.priceField] || result.price_3day;
    setNewInv(function(prev) {
      const hasEmpty = prev.line_items.length === 1 && !prev.line_items[0].description && !prev.line_items[0].itemType;
      const newItem = { itemType: 'Custom', description, quantity: 1, rate, amount: rate };
      return { ...prev, line_items: hasEmpty ? [newItem] : [...prev.line_items, newItem] };
    });
    if (result.hauler_id) {
      setInvSelectedHauler({ hauler_id: result.hauler_id, name: result.hauler_name || 'Selected Hauler' });
    }
  }

  async function login(e) {
    e.preventDefault();
    setAuthError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setAuthError(error.message);
  }

  async function handleAdminForgot(e) {
    e.preventDefault();
    setForgotLoading(true);
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://fasttambo.com/reset-password?role=admin',
    });
    setForgotLoading(false);
    setForgotSent(true);
  }

  async function fetchDashboard() {
    setDashLoading(true); setDashError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/dashboard-summary`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load dashboard');
      setDashSummary(data);
    } catch (err) { setDashError(err.message); }
    finally { setDashLoading(false); }
  }

  async function fetchOrders() {
    setOrdersLoading(true); setOrdersError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load orders');
      setOrders(data.orders || []);
    } catch (err) { setOrdersError(err.message); }
    finally { setOrdersLoading(false); }
  }

  async function fetchRejectionHaulers(orderId) {
    setRejectionHaulersLoading(true); setRejectionHaulers([]);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${orderId}/available-haulers`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load haulers');
      setRejectionHaulers(data.haulers || []);
    } catch (err) { console.error('[fetchRejectionHaulers]', err.message); }
    finally { setRejectionHaulersLoading(false); }
  }

  async function acknowledgeRejection(id) {
    await adminFetch(`${BACKEND}/api/admin/rejections/${id}/acknowledge`, { method: 'POST' })
      .catch(function(err) { console.error('[ack rejection]', err.message); });
    setRejections(function(prev) { return prev.filter(function(r) { return r.id !== id; }); });
    if (selectedRejection && selectedRejection.id === id) {
      setSelectedRejection(null);
      setRejectionHaulers([]);
    }
  }

  async function acknowledgeAllRejections() {
    await Promise.all(rejections.map(function(r) {
      return adminFetch(`${BACKEND}/api/admin/rejections/${r.id}/acknowledge`, { method: 'POST' })
        .catch(function(err) { console.error('[ack rejection]', err.message); });
    }));
    setRejections([]);
    setShowRejectionPanel(false);
    setSelectedRejection(null);
    setRejectionHaulers([]);
  }

  async function redispatchHauler(orderId, haulerId, force) {
    setRedispatchLoading(haulerId);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${orderId}/redispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hauler_id: haulerId, force: !!force }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.margin_tier === 'red' && !force) {
          setRedispatchLoading(null);
          setConfirmModal({ message: `⛔ LOSS ALERT: Hauler payout ($${parseFloat(data.payout || 0).toFixed(2)}) exceeds client subtotal ($${parseFloat(data.client_subtotal || 0).toFixed(2)}) — shortfall $${parseFloat(data.shortfall || 0).toFixed(2)}. This order will LOSE MONEY. Assign anyway?`, label: 'Assign Anyway', onConfirm: function() { redispatchHauler(orderId, haulerId, true); } });
          return;
        }
        throw new Error(data.error || 'Redispatch failed');
      }
      if (data.margin_tier === 'yellow') {
        alert(`⚠️ Low margin warning: $${parseFloat(data.payout || 0).toFixed(2)} payout on this order. Dispatched.`);
      }
      if (selectedRejection) await acknowledgeRejection(selectedRejection.id);
    } catch (err) { alert('Redispatch failed: ' + err.message); }
    finally { setRedispatchLoading(null); }
  }

  async function createPayout(orderId) {
    setCreatePayoutLoading(orderId);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${orderId}/create-payout`, { method: 'POST' });
      let data;
      try { data = await res.json(); } catch (_) { data = {}; }
      if (!res.ok) {
        const msg = data.error || `HTTP ${res.status}`;
        console.error('[createPayout] failed:', msg);
        alert('Create payout failed: ' + msg);
        return;
      }
      await fetchOrders();
      alert('Payout row created — $' + parseFloat(data.payout?.amount || 0).toFixed(2));
    } catch (err) {
      console.error('[createPayout] error:', err);
      alert('Create payout failed: ' + err.message);
    } finally { setCreatePayoutLoading(null); }
  }

  function openInvoiceModalForMarket(mkt, refreshFn) {
    mktPortalRefreshRef.current = refreshFn || null;
    setInvLockedMarket(mkt);
    setNewInv(function() { return { ...emptyInvoice(), market_name: mkt }; });
    setNewInvError('');
    setShowNewInv(true);
  }

  function openHaulerFromMarket(h, marketName) {
    setMktReturnCtx({ marketName });
    if (haulers.length === 0) fetchHaulers();
    setExpandedHaulerId(h.id);
    setTab('haulers');
  }

  function openClientFromMarket(c, marketName) {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.client_id || '');
    if (!isUUID) {
      alert((c.name || c.email || 'This client') + ' is a guest (no account). View their orders in the Orders tab instead.');
      return;
    }
    setMktReturnCtx({ marketName });
    if (clients.length === 0) fetchClients();
    setExpandedClientId(c.client_id);
    if (!clientDetails[c.client_id]) fetchClientDetail(c.client_id);
    setTab('clients');
  }

  async function fetchPayoutsSummary() {
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/payouts/summary`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setPayoutsSummary(d);
    } catch (err) { console.error('[payouts summary]', err.message); }
  }

  async function fetchPayouts() {
    setPayoutsLoading(true); setPayoutsError('');
    try {
      const params = new URLSearchParams();
      if (payoutsStatusFilter && payoutsStatusFilter !== 'all') params.set('status', payoutsStatusFilter);
      if (payoutsMarketFilter && payoutsMarketFilter !== 'all') params.set('market_name', payoutsMarketFilter);
      const r = await adminFetch(`${BACKEND}/api/admin/payouts?${params.toString()}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setPayoutsList(d.payouts || []);
    } catch (err) { setPayoutsError(err.message); }
    finally { setPayoutsLoading(false); }
  }

  async function markPayoutPaid() {
    if (!payModal) return;
    setPayLoading(true); setPayError('');
    try {
      let r, d;
      if (payModal.mode === 'single') {
        r = await adminFetch(`${BACKEND}/api/admin/payouts/${payModal.payoutIds[0]}/mark-paid`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payForm),
        });
        d = await r.json();
      } else {
        r = await adminFetch(`${BACKEND}/api/admin/payouts/mark-paid-batch`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payout_ids: payModal.payoutIds, ...payForm }),
        });
        d = await r.json();
      }
      if (!r.ok) throw new Error(d.error || 'Failed to mark paid');
      setPayModal(null);
      fetchPayouts();
      fetchPayoutsSummary();
      if (payoutDetailPayout && payModal.payoutIds.includes(payoutDetailPayout.id)) {
        setPayoutDetailPayout(function(p) {
          return { ...p, status: 'paid', paid_at: payForm.paid_at || new Date().toISOString(), payment_method: payForm.payment_method, payment_reference: payForm.payment_reference };
        });
      }
    } catch (err) { setPayError(err.message); }
    finally { setPayLoading(false); }
  }

  async function openPayoutOrder(payout) {
    console.log('[openPayoutOrder] payout clicked:', payout);
    console.log('[openPayoutOrder] order_id:', payout.order_id);
    setPayoutDetailPayout(payout);
    setPayoutDetailOrder(null);
    setPayoutDetailInv(null);
    setInvError('');
    try {
      const url = `${BACKEND}/api/admin/orders/${payout.order_id}`;
      console.log('[openPayoutOrder] fetching:', url);
      const r = await fetch(url);
      const d = await r.json();
      console.log('[openPayoutOrder] order fetch status:', r.status, 'data:', d);
      if (!r.ok) throw new Error(d.error || 'Failed to load order');
      setPayoutDetailOrder(d.order);
      if (d.order.invoice_id) {
        console.log('[openPayoutOrder] fetching invoice:', d.order.invoice_id);
        setPayoutDetailInvLoading(true);
        try {
          const ir = await fetch(`${BACKEND}/api/invoices/${d.order.invoice_id}`);
          const id2 = await ir.json();
          console.log('[openPayoutOrder] invoice fetch status:', ir.status, 'data:', id2);
          if (ir.ok) setPayoutDetailInv(id2.invoice || id2);
        } catch (invErr) { console.error('[openPayoutOrder] invoice fetch error:', invErr.message); }
        finally { setPayoutDetailInvLoading(false); }
      } else {
        console.log('[openPayoutOrder] no invoice_id on order');
      }
    } catch (err) {
      console.error('[openPayoutOrder] error:', err.message);
      setPayoutDetailPayout(null);
    }
  }

  async function pdSendInvoice(inv) {
    if (!inv.guest_email) { setInvError('No email on this invoice.'); return; }
    setInvActionId(inv.id + '_send'); setInvError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inv.guest_email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      const ir = await fetch(`${BACKEND}/api/invoices/${inv.id}`);
      const id2 = await ir.json();
      if (ir.ok) setPayoutDetailInv(id2.invoice || id2);
    } catch (err) { setInvError(err.message); }
    finally { setInvActionId(null); }
  }

  function pdMarkPaid(inv) {
    setInvPayModal({ inv, context: 'payouts' });
    setInvPayMethod('cash');
    setInvPayReason('');
    setInvPayError('');
  }

  function pdUnmarkPaid(inv) {
    setConfirmModal({ message: `Unmark ${displayNum(inv)} as paid? It will go back to "sent".`, label: 'Unmark Paid', onConfirm: async function() {
      setInvActionId(inv.id + '_unpay'); setInvError('');
      try {
        const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'sent' }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unmark paid failed');
        const ir = await fetch(`${BACKEND}/api/invoices/${inv.id}`);
        const id2 = await ir.json();
        if (ir.ok) setPayoutDetailInv(id2.invoice || id2);
      } catch (err) { setInvError(err.message); }
      finally { setInvActionId(null); }
    }});
  }

  function pdDeleteInvoice(inv) {
    setConfirmModal({ message: `Delete draft ${displayNum(inv)}? This cannot be undone.`, label: 'Delete', onConfirm: async function() {
      setInvActionId(inv.id + '_del'); setInvError('');
      try {
        const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}`, { method: 'DELETE' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Delete failed');
        setPayoutDetailInv(null);
      } catch (err) { setInvError(err.message); }
      finally { setInvActionId(null); }
    }});
  }

  async function submitAddHauler() {
    if (!addHaulerForm.email || !addHaulerForm.first_name) { setAddHaulerError('First name and email are required.'); return; }
    if (!addHaulerForm.market_name) { setAddHaulerError('Please select a market.'); return; }
    setAddHaulerLoading(true); setAddHaulerError('');
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/haulers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(addHaulerForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to create hauler');
      setAddHaulerResult(d);
      fetchHaulers();
    } catch (err) { setAddHaulerError(err.message); }
    finally { setAddHaulerLoading(false); }
  }

  async function sendSetupLink(hauler) {
    setSetupLinkLoading(hauler.id);
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/haulers/${hauler.id}/send-setup-link`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to send welcome email');
      setSetupLinkSent(function(prev) { return { ...prev, [hauler.id]: d.sent_at }; });
      if (d.tempPassword) {
        alert('Welcome email sent!\n\nNew temp password set:\n' + d.tempPassword + '\n\nThis has been emailed and texted to the hauler.');
      }
      await fetchHaulers();
    } catch (err) {
      alert('Error sending welcome email: ' + err.message);
    } finally {
      setSetupLinkLoading(null);
    }
  }

  async function fetchHaulers() {
    setHaulersLoading(true); setHaulersError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load haulers');
      setHaulers(data.haulers || []);
    } catch (err) { setHaulersError(err.message); }
    finally { setHaulersLoading(false); }
  }

  async function fetchInvoices() {
    setInvLoading(true); setInvError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load invoices');
      const list = data.invoices || [];
      setInvoices(list);
      return list;
    } catch (err) { setInvError(err.message); return []; }
    finally { setInvLoading(false); }
  }

  async function fetchMe() {
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/me`);
      if (res.ok) { const d = await res.json(); setIsOwner(!!d.isOwner); }
    } catch (err) { /* non-fatal */ }
  }

  async function fetchDeleted() {
    setDeletedLoading(true);
    try {
      const [ro, ri] = await Promise.all([
        adminFetch(`${BACKEND}/api/admin/orders/deleted`).then(function(r) { return r.json(); }),
        adminFetch(`${BACKEND}/api/admin/invoices/deleted`).then(function(r) { return r.json(); }),
      ]);
      setDeletedOrders(ro.orders || []);
      setDeletedInvoices(ri.invoices || []);
    } catch (err) { console.error('[fetchDeleted]', err.message); }
    finally { setDeletedLoading(false); }
  }

  async function softDeleteOrder(order, textHauler) {
    setOrderDeleteLoading(true); setOrderDeleteError(''); setOrderDeleteWarnPaid(false); setOrderDeleteWarnOnSite(false);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text_hauler: !!textHauler }),
      });
      const data = await res.json();
      if (!res.ok) { setOrderDeleteError(data.error || 'Failed to delete'); return; }
      if (data.warn_paid) setOrderDeleteWarnPaid(true);
      if (data.warn_on_site) setOrderDeleteWarnOnSite(true);
      setOrders(function(prev) { return prev.filter(function(o) { return o.id !== order.id; }); });
      setInvoices(function(prev) { return prev.filter(function(i) { return i.order_id !== order.id; }); });
      setOrderDeleteTarget(null);
      setSelectedOrder(null); setSelectedInv(null);
    } catch (err) { setOrderDeleteError(err.message); }
    finally { setOrderDeleteLoading(false); }
  }

  async function restoreOrder(order) {
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/restore`, { method: 'POST' });
      if (!res.ok) { const d = await res.json(); alert(d.error || 'Failed to restore'); return; }
      setDeletedOrders(function(prev) { return prev.filter(function(o) { return o.id !== order.id; }); });
      fetchOrders(); fetchInvoices();
    } catch (err) { alert(err.message); }
  }

  async function permDeleteOrder(order) {
    if (orderPermDeleteConfirm !== order.order_number) {
      setOrderPermDeleteError(`Type "${order.order_number}" to confirm.`); return;
    }
    setOrderPermDeleteLoading(true); setOrderPermDeleteError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_order_number: orderPermDeleteConfirm }),
      });
      const data = await res.json();
      if (!res.ok) { setOrderPermDeleteError(data.error || 'Failed'); return; }
      setDeletedOrders(function(prev) { return prev.filter(function(o) { return o.id !== order.id; }); });
      setOrderPermDeleteTarget(null); setOrderPermDeleteConfirm('');
    } catch (err) { setOrderPermDeleteError(err.message); }
    finally { setOrderPermDeleteLoading(false); }
  }

  async function softDeleteInvoiceOwner(inv) {
    setInvOwnerDeleteLoading(true);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/invoices/${inv.id}/delete`, { method: 'POST' });
      if (!res.ok) { const d = await res.json(); alert(d.error || 'Failed'); return; }
      setInvoices(function(prev) { return prev.filter(function(i) { return i.id !== inv.id; }); });
      setInvOwnerDeleteTarget(null);
      setSelectedInv(null);
    } catch (err) { alert(err.message); }
    finally { setInvOwnerDeleteLoading(false); }
  }

  async function restoreInvoice(inv) {
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/invoices/${inv.id}/restore`, { method: 'POST' });
      if (!res.ok) { const d = await res.json(); alert(d.error || 'Failed'); return; }
      setDeletedInvoices(function(prev) { return prev.filter(function(i) { return i.id !== inv.id; }); });
    } catch (err) { alert(err.message); }
  }

  async function permDeleteInvoice(inv) {
    if (invPermDeleteConfirm !== inv.invoice_ref) {
      setInvPermDeleteError(`Type "${inv.invoice_ref}" to confirm.`); return;
    }
    setInvPermDeleteLoading(true); setInvPermDeleteError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/invoices/${inv.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_invoice_ref: invPermDeleteConfirm }),
      });
      const data = await res.json();
      if (!res.ok) { setInvPermDeleteError(data.error || 'Failed'); return; }
      setDeletedInvoices(function(prev) { return prev.filter(function(i) { return i.id !== inv.id; }); });
      setInvPermDeleteTarget(null); setInvPermDeleteConfirm('');
    } catch (err) { setInvPermDeleteError(err.message); }
    finally { setInvPermDeleteLoading(false); }
  }

  async function fetchClients() {
    setClientsLoading(true); setClientsError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/clients`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load clients');
      setClients(data.clients || []);
    } catch (err) { setClientsError(err.message); }
    finally { setClientsLoading(false); }
  }

  async function fetchRequests() {
    setRequestsLoading(true); setRequestsError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/requests/pending`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load requests');
      setRequests(data.requests || []);
    } catch (err) { setRequestsError(err.message); }
    finally { setRequestsLoading(false); }
  }

  async function approveRequest(id) {
    setRequestActionId(id + '_approve'); setRequestsError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/requests/${id}/approve`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to approve');
      setRequestSuccessId(id + '_approve');
      setTimeout(function() { setRequestSuccessId(null); }, 4000);
      await fetchRequests();
    } catch (err) { setRequestsError(err.message); }
    finally { setRequestActionId(null); }
  }

  async function denyRequest(id) {
    setRequestActionId(id + '_deny'); setRequestsError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/requests/${id}/deny`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to deny');
      await fetchRequests();
    } catch (err) { setRequestsError(err.message); }
    finally { setRequestActionId(null); }
  }

  async function markContacted(haulerId) {
    setMarkContactedLoading(haulerId);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${haulerId}/mark-contacted`, { method: 'POST' });
      if (res.ok) {
        setDashSummary(function(prev) {
          if (!prev) return prev;
          return { ...prev, stale_haulers: (prev.stale_haulers || []).filter(function(h) { return h.id !== haulerId; }) };
        });
      }
    } catch (e) { console.error('[mark-contacted]', e.message); }
    finally { setMarkContactedLoading(null); }
  }

  async function fetchReassignQueue() {
    setReassignQueueLoading(true); setReassignQueueError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/needs-reassignment`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load reassignment queue');
      setReassignQueue(data.orders || []);
    } catch (err) { setReassignQueueError(err.message); }
    finally { setReassignQueueLoading(false); }
  }

  async function loadHaulersForReassign(orderId) {
    setReassignQueueHaulersLoading(function(prev) { return { ...prev, [orderId]: true }; });
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${orderId}/available-haulers`);
      const data = await res.json();
      setReassignQueueHaulers(function(prev) { return { ...prev, [orderId]: data.haulers || [] }; });
    } catch (err) {
      setReassignQueueHaulers(function(prev) { return { ...prev, [orderId]: [] }; });
    } finally {
      setReassignQueueHaulersLoading(function(prev) { return { ...prev, [orderId]: false }; });
    }
  }

  async function assignFromReassignQueue(orderId, haulerId) {
    if (!haulerId) return;
    setReassignQueueAssigning(orderId);
    setReassignQueueMsg(function(prev) { return { ...prev, [orderId]: '' }; });
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/orders/${orderId}/manual-assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hauler_id: haulerId }),
      });
      const data = await res.json();
      if (data.ok) {
        setReassignQueue(function(prev) { return prev.filter(function(o) { return o.id !== orderId; }); });
        setReassignQueueMsg(function(prev) { return { ...prev, [orderId]: `Assigned to ${data.hauler}. SMS sent.` }; });
      } else {
        setReassignQueueMsg(function(prev) { return { ...prev, [orderId]: 'Error: ' + (data.error || 'Unknown error') }; });
      }
    } catch (err) {
      setReassignQueueMsg(function(prev) { return { ...prev, [orderId]: 'Network error: ' + err.message }; });
    } finally {
      setReassignQueueAssigning(null);
    }
  }

  async function fetchMarkets() {
    setMarketsLoading(true); setMarketsError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/markets`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load markets');
      const list = data.markets || [];
      setMarkets(list);
      const drafts = {};
      list.forEach(function(m) {
        drafts[m.id] = {
          fee_percent:       m.fee_percent,
          environmental_fee: m.environmental_fee ?? 0,
          zip_prefixes:      m.zip_prefixes || '',
          counties:          m.counties || '',
          is_active:         m.is_active,
        };
      });
      setMarketDrafts(drafts);
    } catch (err) { setMarketsError(err.message); }
    finally { setMarketsLoading(false); }
  }

  async function saveMarket(id) {
    setMarketSaving(id); setMarketsError(''); setMarketSaveSuccess(null);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/markets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(marketDrafts[id]),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setMarketSaveSuccess(id);
      setMarkets(function(prev) { return prev.map(function(m) { return m.id === id ? { ...m, ...marketDrafts[id] } : m; }); });
      setTimeout(function() { setMarketSaveSuccess(null); }, 3000);
    } catch (err) { setMarketsError(err.message); }
    finally { setMarketSaving(null); }
  }

  async function sendCheckIn() {
    setCheckInLoading(true);
    setCheckInResult('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/send-availability-checkin`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setCheckInResult(`✅ Availability check-in sent to ${data.sent} hauler${data.sent !== 1 ? 's' : ''}!${data.failed ? ` (${data.failed} failed)` : ''}`);
    } catch (err) {
      setCheckInResult(`❌ Error: ${err.message}`);
    } finally {
      setCheckInLoading(false);
    }
  }

  async function sendDeliveryReminders() {
    setReminderLoading(true);
    setReminderResult('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/send-delivery-reminders`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      const skipped = data.skipped ? ` (${data.skipped} skipped — no phone)` : '';
      const errors  = data.errors  ? ` (${data.errors} failed)` : '';
      setReminderResult(`✅ Sent ${data.sent} delivery reminder${data.sent !== 1 ? 's' : ''}${skipped}${errors}`);
    } catch (err) {
      setReminderResult(`❌ Error: ${err.message}`);
    } finally {
      setReminderLoading(false);
    }
  }

  async function fetchClientDetail(clientId) {
    setClientDetails(prev => ({ ...prev, [clientId]: { ...(prev[clientId] || {}), loading: true } }));
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/clients/${clientId}`);
      const data = await res.json();
      setClientDetails(prev => ({ ...prev, [clientId]: { orders: data.orders || [], loading: false } }));
    } catch(e) {
      setClientDetails(prev => ({ ...prev, [clientId]: { orders: [], loading: false } }));
    }
  }

  async function doApproveClient(id) {
    setClientActionLoading(id + '_approve'); setClientActionError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/clients/${id}/approve`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { setExpandedClientId(null); await fetchClients(); }
      else setClientActionError(data.error || 'Failed to approve client');
    } catch (err) { setClientActionError('Network error: ' + err.message); }
    finally { setClientActionLoading(null); }
  }

  function approveClient(id, name) {
    setConfirmModal({
      message: `Approve ${name ? name + '\'s' : 'this'} account? They'll receive an email notification.`,
      label: 'Approve',
      onConfirm: function() { doApproveClient(id); },
    });
  }

  async function doRejectClient(id, reason) {
    setClientActionLoading(id + '_reject'); setClientActionError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/clients/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || undefined }),
      });
      const data = await res.json();
      if (res.ok) { setExpandedClientId(null); await fetchClients(); }
      else setClientActionError(data.error || 'Failed to reject client');
    } catch (err) { setClientActionError('Network error: ' + err.message); }
    finally { setClientActionLoading(null); }
  }

  function rejectClient(id, name) {
    setRejectReason('');
    setRejectClientModal({ id, name });
  }

  async function togglePayLater(id, current) {
    setClientActionLoading(id + '_paylater'); setClientActionError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/clients/${id}/pay-later`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved: !current }),
      });
      const data = await res.json();
      if (res.ok) {
        setClients(prev => prev.map(c => c.id === id ? { ...c, pay_later_approved: !current } : c));
      } else { setClientActionError(data.error || 'Failed to update pay later'); }
    } catch (err) { setClientActionError('Network error: ' + err.message); }
    finally { setClientActionLoading(null); }
  }

  function removeClient(id, name) {
    setConfirmModal({ message: `Permanently remove ${name || 'this client'}? This cannot be undone.`, label: 'Remove', onConfirm: async function() {
      setClientRemoveLoading(id); setClientActionError('');
      try {
        const res = await adminFetch(`${BACKEND}/api/clients/${id}`, { method: 'DELETE' });
        const data = await res.json();
        if (res.ok) {
          setExpandedClientId(null);
          setClients(function(prev) { return prev.filter(function(c) { return c.id !== id; }); });
        } else { setClientActionError(data.error || 'Failed to remove client'); }
      } catch (err) { setClientActionError('Network error: ' + err.message); }
      finally { setClientRemoveLoading(null); }
    }});
  }

  async function approveHauler(id) {
    setActionLoading(id); setActionError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${id}/approve`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { setExpandedHaulerId(null); await fetchHaulers(); }
      else setActionError(data.error || 'Failed to approve hauler');
    } catch (err) { setActionError('Network error: ' + err.message); }
    finally { setActionLoading(null); }
  }

  async function rejectHauler(id) {
    setActionLoading(id); setActionError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${id}/reject`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { setExpandedHaulerId(null); await fetchHaulers(); }
      else setActionError(data.error || 'Failed to reject hauler');
    } catch (err) { setActionError('Network error: ' + err.message); }
    finally { setActionLoading(null); }
  }

  async function suspendHauler(id) {
    setSuspendLoading(id); setActionError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${id}/suspend`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { setExpandedHaulerId(null); await fetchHaulers(); }
      else setActionError(data.error || 'Failed to suspend hauler');
    } catch (err) { setActionError('Network error: ' + err.message); }
    finally { setSuspendLoading(null); }
  }

  async function unsuspendHauler(id) {
    setSuspendLoading(id); setActionError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${id}/unsuspend`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { setExpandedHaulerId(null); await fetchHaulers(); }
      else setActionError(data.error || 'Failed to reactivate hauler');
    } catch (err) { setActionError('Network error: ' + err.message); }
    finally { setSuspendLoading(null); }
  }

  async function loadDocUrls(haulerId) {
    setDocUrlsLoading(prev => ({ ...prev, [haulerId]: true }));
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${haulerId}/doc-urls`);
      const data = await res.json();
      if (res.ok) setDocUrls(prev => ({ ...prev, [haulerId]: data }));
    } catch (_) {}
    finally { setDocUrlsLoading(prev => ({ ...prev, [haulerId]: false })); }
  }

  async function saveHaulerEdit() {
    if (!editingHauler) return;
    setHaulerEditSaving(true); setHaulerEditError(''); setHaulerEditSuccess(false);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${editingHauler.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(haulerDraft),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setHaulerEditSuccess(true);
      // Patch the haulers list in place so the table reflects changes without a full refresh
      setHaulers(prev => prev.map(h => h.id === editingHauler.id
        ? { ...h, ...haulerDraft, hauler_email: haulerDraft.email ?? h.hauler_email }
        : h
      ));
    } catch (err) { setHaulerEditError(err.message); }
    finally { setHaulerEditSaving(false); }
  }

  async function saveClientEdit() {
    if (!editingClient) return;
    setClientEditSaving(true); setClientEditError(''); setClientEditSuccess(false);
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/clients/${editingClient.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientDraft),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setClientEditSuccess(true);
      setClients(prev => prev.map(c => c.id === editingClient.id ? { ...c, ...clientDraft } : c));
    } catch (err) { setClientEditError(err.message); }
    finally { setClientEditSaving(false); }
  }

  function removeHauler(id, name) {
    setConfirmModal({ message: `Permanently remove ${name || 'this hauler'}? This cannot be undone.`, label: 'Remove', onConfirm: async function() {
      setRemoveLoading(id); setActionError('');
      try {
        const res = await adminFetch(`${BACKEND}/api/admin/haulers/${id}`, { method: 'DELETE' });
        const data = await res.json();
        if (res.ok) { setExpandedHaulerId(null); await fetchHaulers(); }
        else setActionError(data.error || 'Failed to remove hauler');
      } catch (err) { setActionError('Network error: ' + err.message); }
      finally { setRemoveLoading(null); }
    }});
  }

  async function saveInlineMarket(haulerId) {
    setInlineMarketSaving(true); setInlineMarketMsg('');
    try {
      const res = await adminFetch(`${BACKEND}/api/admin/haulers/${haulerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ market_name: inlineMarketDraft || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setHaulers(function(prev) { return prev.map(function(x) { return x.id === haulerId ? { ...x, market_name: inlineMarketDraft || null } : x; }); });
      setInlineMarketMsg('Saved');
    } catch (err) {
      setInlineMarketMsg(err.message);
    } finally {
      setInlineMarketSaving(false);
    }
  }

  async function createInvoice(sendNow) {
    const items = newInv.line_items.filter(i => i.description);
    if (!items.length) { setNewInvError('Add at least one line item.'); return; }
    setNewInvSaving(true); setNewInvError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_name: newInv.guest_name || undefined,
          guest_email: newInv.guest_email || undefined,
          guest_phone: newInv.guest_phone || undefined,
          company_name: newInv.company_name || undefined,
          billing_address: newInv.billing_address || undefined,
          service_address: newInvSameAsBilling ? null : (newInv.service_address || null),
          payment_terms: newInv.payment_terms || 'net7',
          notes: newInv.notes || undefined,
          is_recurring: newInv.is_recurring,
          recurring_interval: newInv.is_recurring ? newInv.recurring_interval : undefined,
          market_name: newInv.market_name || 'Unassigned',
          line_items: items.map(i => ({
            description: i.description,
            quantity: parseFloat(i.quantity) || 1,
            rate: parseFloat(i.rate) || 0,
            amount: parseFloat(i.amount) || 0,
          })),
          terms_and_conditions: newInv.terms_and_conditions || undefined,
          tax_enabled: newInv.tax_enabled !== false,
          extra_emails: (newInv.extra_emails || []).filter(Boolean),
          extra_phones: (newInv.extra_phones || []).filter(Boolean),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create invoice');
      if (sendNow) {
        if (!newInv.guest_email) throw new Error('Client email required to send.');
        const sr = await adminFetch(`${BACKEND}/api/invoices/${data.invoice.id}/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: newInv.guest_email, hauler_id: invSelectedHauler?.hauler_id || null }),
        });
        const sd = await sr.json();
        if (!sr.ok) throw new Error(sd.error || 'Created but failed to send');
      }
      setShowNewInv(false);
      setNewInv(emptyInvoice());
      setInvSelectedHauler(null);
      setInvLockedMarket(null);
      if (mktPortalRefreshRef.current) { mktPortalRefreshRef.current(); mktPortalRefreshRef.current = null; }
      await fetchInvoices();
    } catch (err) { setNewInvError(err.message); }
    finally { setNewInvSaving(false); }
  }

  async function sendInvoice(inv) {
    if (!inv.guest_email) { setInvError('No email on this invoice.'); return; }
    console.log('[sendInvoice] id:', inv.id, 'email:', inv.guest_email);
    setInvActionId(inv.id + '_send'); setInvError(''); setInvSendMsg('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inv.guest_email }),
      });
      const data = await res.json();
      console.log('[sendInvoice] response:', res.status, data);
      if (!res.ok) throw new Error(data.error || 'Send failed');
      const emailOk = data.email && data.email.startsWith('OK');
      const emailIcon = emailOk ? '✓' : '✗';
      const ccCount = data.cc_count || 0;
      const emailLabel = ccCount > 0 ? `Email ${emailIcon} (+${ccCount} CC)` : `Email ${emailIcon}`;
      const smsOk = data.sms && (data.sms.startsWith('OK') || /^\d+\/\d+/.test(data.sms));
      const smsNoConsent = data.sms === 'SKIPPED(no consent)';
      const smsSkipped = data.sms && data.sms.startsWith('SKIPPED');
      const smsIcon = smsOk ? '✓' : (smsSkipped ? '—' : '✗');
      const smsLabel = smsNoConsent ? 'Text — (no consent)' : `Text ${smsIcon}${data.sms_summary ? ' ' + data.sms_summary : ''}`;
      setInvSendMsg(`${emailLabel}  ·  ${smsLabel}`);
      const fresh = await fetchInvoices();
      if (selectedInv && selectedInv.id === inv.id) {
        const updated = fresh.find(i => i.id === inv.id);
        if (updated) setSelectedInv(updated);
      }
    } catch (err) {
      console.error('[sendInvoice] error:', err.message);
      setInvError('Send failed: ' + err.message);
    }
    finally { setInvActionId(null); }
  }

  function markPaid(inv) {
    setInvPayModal({ inv, context: 'orders' });
    setInvPayMethod('cash');
    setInvPayReason('');
    setInvPayError('');
  }

  async function submitInvMarkPaid() {
    if (!invPayReason.trim()) { setInvPayError('Please enter a reason/note.'); return; }
    setInvPaySaving(true); setInvPayError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${invPayModal.inv.id}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          manual_override: true,
          payment_method: invPayMethod,
          reason: invPayReason,
          performed_by: session?.user?.email || ADMIN_EMAIL,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Mark paid failed');
      if (invPayModal.context === 'orders') {
        const fresh = await fetchInvoices();
        if (selectedInv && selectedInv.id === invPayModal.inv.id) {
          const updated = fresh ? fresh.find(function(i) { return i.id === invPayModal.inv.id; }) : null;
          if (updated) setSelectedInv({ ...updated, payment_log_entries: data.invoice.payment_log_entries || [] });
        }
      } else if (invPayModal.context === 'payouts') {
        const ir = await fetch(`${BACKEND}/api/invoices/${invPayModal.inv.id}`);
        const id2 = await ir.json();
        if (ir.ok) setPayoutDetailInv(id2.invoice || id2);
      }
      setInvPayModal(null);
    } catch (err) { setInvPayError(err.message); }
    finally { setInvPaySaving(false); }
  }

  function unmarkPaid(inv) {
    setConfirmModal({ message: `Unmark ${displayNum(inv)} as paid? It will go back to "sent".`, label: 'Unmark Paid', onConfirm: async function() {
      setInvActionId(inv.id + '_unpay'); setInvError('');
      try {
        const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'sent' }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unmark paid failed');
        const fresh = await fetchInvoices();
        const updated = fresh.find(i => i.id === inv.id);
        if (updated) setSelectedInv(updated);
      } catch (err) { setInvError(err.message); }
      finally { setInvActionId(null); }
    }});
  }

  function openRecordPayment(inv) {
    const entries = inv.payment_log_entries || [];
    const paid = entries.reduce(function(s, p) { return s + parseFloat(p.amount || 0); }, 0);
    const balance = Math.max(0, Math.round((parseFloat(inv.total || 0) - paid) * 100) / 100);
    setInvRecordPayModal(inv);
    setInvRecordPayForm({ amount: balance.toFixed(2), method: 'cash', reference: '', date: new Date().toISOString().split('T')[0], note: '' });
    setInvRecordPayError('');
  }

  async function submitRecordPayment() {
    if (!invRecordPayModal) return;
    const amt = parseFloat(invRecordPayForm.amount);
    if (isNaN(amt) || amt <= 0) { setInvRecordPayError('Enter a valid amount.'); return; }
    setInvRecordPaySaving(true); setInvRecordPayError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${invRecordPayModal.id}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amt,
          method: invRecordPayForm.method,
          reference_number: invRecordPayForm.reference || undefined,
          payment_date: invRecordPayForm.date || undefined,
          note: invRecordPayForm.note || undefined,
          performed_by: session?.user?.email || ADMIN_EMAIL,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to record payment');
      setInvRecordPayModal(null);
      const fresh = await fetchInvoices();
      if (selectedInv && selectedInv.id === data.invoice.id) {
        const updated = fresh ? fresh.find(function(i) { return i.id === data.invoice.id; }) : null;
        if (updated) setSelectedInv({ ...updated, payment_log_entries: data.invoice.payment_log_entries });
      }
    } catch (err) { setInvRecordPayError(err.message); }
    finally { setInvRecordPaySaving(false); }
  }

  function removePaymentEntry(inv, paymentId) {
    setConfirmModal({ message: 'Remove this payment entry? This cannot be undone.', label: 'Remove', onConfirm: async function() {
      setInvActionId(inv.id + '_rmpay');
      try {
        const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}/payments/${paymentId}`, { method: 'DELETE' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to remove payment');
        const fresh = await fetchInvoices();
        if (selectedInv && selectedInv.id === inv.id) {
          const updated = fresh ? fresh.find(function(i) { return i.id === inv.id; }) : null;
          if (updated) setSelectedInv({ ...updated, payment_log_entries: data.invoice.payment_log_entries });
        }
      } catch (err) { setInvError(err.message); }
      finally { setInvActionId(null); }
    }});
  }
  function voidInvoice(inv) {
    setConfirmModal({ message: `Void ${displayNum(inv)}? This will mark it void and expire the payment link. The record is kept.`, label: 'Void Invoice', onConfirm: async function() {
      setInvActionId(inv.id + '_void');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ performed_by: session?.user?.email || ADMIN_EMAIL }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Void failed');
      const fresh = await fetchInvoices();
      const updated = fresh ? fresh.find(function(i) { return i.id === inv.id; }) : null;
      if (updated) setSelectedInv(updated);
    } catch (err) { setInvError(err.message); }
    finally { setInvActionId(null); }
    }});
  }

  async function duplicateInvoice(inv) {
    setInvError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}/duplicate`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Duplicate failed');
      await fetchInvoices();
      setSelectedInv(data.invoice);
      setInvSendMsg(`Duplicated → ${data.invoice.invoice_display} (draft)`);
    } catch (err) { setInvError(err.message); }
  }

  function downloadInvoicePdf(inv) {
    window.open(`${BACKEND}/api/invoices/${inv.id}/pdf`, '_blank');
  }


  function updateLineItem(idx, field, value) {
    setNewInv(prev => {
      const items = prev.line_items.map((it, i) => {
        if (i !== idx) return it;
        const updated = { ...it, [field]: value };
        if (field === 'itemType' && value !== 'Custom') {
          updated.description = value;
          if (ITEM_RATES[value] != null) updated.rate = ITEM_RATES[value];
        }
        updated.amount = Math.round(parseFloat(updated.quantity || 1) * parseFloat(updated.rate || 0) * 100) / 100;
        return updated;
      });
      return { ...prev, line_items: items };
    });
  }

  function addLineItem() {
    setNewInv(prev => ({ ...prev, line_items: [...prev.line_items, { itemType: '', description: '', quantity: 1, rate: '', amount: 0 }] }));
  }

  function removeLineItem(idx) {
    setNewInv(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  }

  function updateEditLineItem(idx, field, value) {
    setEditDraft(prev => {
      const items = prev.line_items.map((it, i) => {
        if (i !== idx) return it;
        const updated = { ...it, [field]: value };
        updated.amount = Math.round(parseFloat(updated.quantity || 1) * parseFloat(updated.rate || 0) * 100) / 100;
        return updated;
      });
      return { ...prev, line_items: items };
    });
  }
  function addEditLineItem() {
    setEditDraft(prev => ({ ...prev, line_items: [...prev.line_items, { description: '', quantity: 1, rate: '', amount: 0 }] }));
  }
  function removeEditLineItem(idx) {
    setEditDraft(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  }

  function openEditInvoice(inv) {
    setEditingInv(inv);
    const refStr = inv.invoice_ref || inv.invoice_display || '';
    const numPart = refStr.startsWith('TMB-') ? refStr.slice(4) : '';
    setEditSameAsBilling(!inv.service_address);
    setEditDraft({
      invoice_ref_number: numPart,
      guest_name: inv.guest_name || '',
      guest_email: inv.guest_email || '',
      guest_phone: inv.guest_phone || '',
      company_name: inv.company_name || '',
      billing_address: inv.billing_address || '',
      service_address: inv.service_address || '',
      notes: inv.notes || '',
      payment_terms: inv.payment_terms || 'net7',
      terms_and_conditions: inv.terms_and_conditions || '',
      tax_enabled: inv.tax_enabled !== false,
      extra_emails: Array.isArray(inv.extra_emails) ? inv.extra_emails : [],
      extra_phones: Array.isArray(inv.extra_phones) ? inv.extra_phones : [],
      line_items: (inv.line_items || []).map(function(item) {
        return { description: item.description || '', quantity: item.quantity || 1, rate: item.rate != null ? item.rate : '', amount: item.amount || 0 };
      }),
    });
    setEditError('');
  }

  async function saveEditedInvoice() {
    if (!editingInv || !editDraft) return;
    const items = editDraft.line_items.filter(function(i) { return i.description; });
    if (!items.length) { setEditError('Add at least one line item.'); return; }
    setEditSaving(true); setEditError('');
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${editingInv.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_ref_number: editDraft.invoice_ref_number || undefined,
          guest_name: editDraft.guest_name || null,
          guest_email: editDraft.guest_email || null,
          guest_phone: editDraft.guest_phone || null,
          company_name: editDraft.company_name || null,
          billing_address: editDraft.billing_address || null,
          service_address: editSameAsBilling ? null : (editDraft.service_address || null),
          notes: editDraft.notes || null,
          payment_terms: editDraft.payment_terms || 'net7',
          terms_and_conditions: editDraft.terms_and_conditions || null,
          tax_enabled: editDraft.tax_enabled !== false,
          extra_emails: (editDraft.extra_emails || []).filter(Boolean),
          extra_phones: (editDraft.extra_phones || []).filter(Boolean),
          line_items: items.map(function(i) {
            return { description: i.description, quantity: parseFloat(i.quantity) || 1, rate: parseFloat(i.rate) || 0, amount: parseFloat(i.amount) || 0 };
          }),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      const savedId = editingInv.id;
      setEditingInv(null); setEditDraft(null);
      const fresh = await fetchInvoices();
      if (selectedInv && selectedInv.id === savedId) {
        const updated = fresh ? fresh.find(function(i) { return i.id === savedId; }) : null;
        if (updated) setSelectedInv(updated);
      }
      setInvSendMsg('Saved — click Resend to send the updated invoice.');
    } catch (err) { setEditError(err.message); }
    finally { setEditSaving(false); }
  }

  function deleteInvoice(inv) {
    setInvDeleteTarget(inv);
  }

  async function confirmDeleteInvoice() {
    if (!invDeleteTarget) return;
    const inv = invDeleteTarget;
    setInvActionId(inv.id + '_del'); setInvError(''); setInvDeleteTarget(null);
    try {
      const res = await adminFetch(`${BACKEND}/api/invoices/${inv.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setInvoices(function(prev) { return prev.filter(function(i) { return i.id !== inv.id; }); });
      if (selectedInv && selectedInv.id === inv.id) setSelectedInv(null);
    } catch (err) { setInvError(err.message); }
    finally { setInvActionId(null); }
  }

  // ── early returns ──────────────────────────────────────
  if (!session) {
    return (
      <div style={s.loginWrap}>
        <h2 onClick={() => { window.location.href = '/'; }} style={{ marginBottom: 24, cursor: 'pointer' }}>Tambo Admin</h2>
        {forgotMode ? (
          forgotSent ? (
            <>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>
                If an account exists for that email, a reset link has been sent. Check your inbox and spam folder.
              </p>
              <button style={s.btn} onClick={() => { setForgotMode(false); setForgotSent(false); }}>← Back to sign in</button>
            </>
          ) : (
            <form onSubmit={handleAdminForgot} style={s.form}>
              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px' }}>Enter your admin email to receive a reset link.</p>
              <input type="email" placeholder="admin@email.com" value={email} onChange={e => setEmail(e.target.value)} style={s.input} required />
              <button type="submit" style={{ ...s.btn, opacity: forgotLoading ? 0.7 : 1 }} disabled={forgotLoading}>
                {forgotLoading ? 'Sending…' : 'Send Reset Email'}
              </button>
              <button type="button" style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', marginTop: 4 }} onClick={() => { setForgotMode(false); setAuthError(''); }}>
                ← Back to sign in
              </button>
            </form>
          )
        ) : (
          <form onSubmit={login} style={s.form}>
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} style={s.input} required />
            <div style={{ position: 'relative' }}>
              <input type={showAdminPwd ? 'text' : 'password'} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} style={{ ...s.input, width: '100%', paddingRight: 40, boxSizing: 'border-box' }} required />
              <button type="button" onClick={() => setShowAdminPwd(v => !v)} tabIndex={-1} aria-label={showAdminPwd ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}>
                {showAdminPwd
                  ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
            {authError && <p style={s.error}>{authError}</p>}
            <button type="submit" style={s.btn}>Sign In</button>
            <p style={{ margin: '4px 0 0', textAlign: 'center' }}>
              <span onClick={() => { setForgotMode(true); setAuthError(''); }} style={{ fontSize: 12, color: '#9ca3af', cursor: 'pointer', textDecoration: 'underline' }}>Forgot password?</span>
            </p>
          </form>
        )}
      </div>
    );
  }

  if (session.user.email !== ADMIN_EMAIL) {
    return (
      <div style={s.loginWrap}>
        <p style={{ marginBottom: 16 }}>Access denied.</p>
        <button onClick={() => supabase.auth.signOut()} style={s.btnRed}>Sign out</button>
      </div>
    );
  }

  const manualInvoices = invoices.filter(i => !i.order_id);
  const orderRevenue = orders.reduce((sum, o) => sum + parseFloat(o.total_charged || 0), 0);
  const invoiceRevenue = manualInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + parseFloat(i.total || 0), 0);
  const totalRevenue = orderRevenue + invoiceRevenue;
  const pendingActions = orders.filter(o => o.status === 'pending').length + manualInvoices.filter(i => i.status === 'draft' || i.status === 'sent').length;
  const pendingHaulers = haulers.filter(h => h.status === 'pending').length;
  const pendingClients = clients.filter(c => c.client_status === 'pending').length;
  const pendingRequests = requests.length;
  // Build order_id → invoice map so linked invoices don't get their own row
  const invByOrderId = {};
  for (const inv of invoices) {
    if (inv.order_id) invByOrderId[inv.order_id] = inv;
  }
  const allItems = [
    // One row per order, carrying its linked invoice if one exists
    ...orders.map(function(o) {
      const linkedInv = invByOrderId[o.id] || null;
      return { _type: 'order', _key: 'o_' + o.id, _date: o.created_at, ref: o.order_number, clientName: o.guest_name || o.guest_email || '—', sizeDesc: SIZE_LABELS[o.size] || o.size || '—', amount: parseFloat(o.total_charged || 0), order: o, invoice: linkedInv, invStatus: linkedInv ? invDisplayStatus(linkedInv) : null };
    }),
    // Manual invoices (no linked order) get their own row
    ...invoices.filter(function(i) { return !i.order_id; }).map(function(i) {
      const fi = i.line_items && i.line_items[0];
      return { _type: 'invoice', _key: 'i_' + i.id, _date: i.created_at, ref: displayNum(i), clientName: i.guest_name || i.company_name || '—', sizeDesc: fi ? fi.description : '—', amount: parseFloat(i.total || 0), order: null, invoice: i, invStatus: invDisplayStatus(i) };
    }),
  ].sort(function(a, b) { return new Date(b._date) - new Date(a._date); });
  const filteredItems = allItems
    .filter(function(item) {
      if (!orderSearch.trim()) return true;
      const q = orderSearch.toLowerCase();
      return (item.ref || '').toLowerCase().includes(q) || (item.clientName || '').toLowerCase().includes(q);
    })
    .filter(function(item) {
      if (ordersStatusFilter === 'all') return true;
      var st = item._type === 'order' ? (item.order && item.order.status) : item.invStatus;
      if (ordersStatusFilter === 'active') {
        return item._type === 'order'
          ? ['pending', 'accepted', 'delivered', 'pickup_requested'].includes(st)
          : ['draft', 'sent'].includes(st);
      }
      if (ordersStatusFilter === 'completed') {
        return item._type === 'order' ? st === 'completed' : st === 'paid';
      }
      if (ordersStatusFilter === 'cancelled') {
        return item._type === 'order' ? st === 'cancelled' : false;
      }
      return true;
    });
  // Deleted items list (owner-only tab)
  const deletedItems = [
    ...deletedOrders.map(function(o) {
      return { _type: 'deleted_order', _key: 'do_' + o.id, _date: o.deleted_at, ref: o.order_number, clientName: o.guest_name || o.guest_email || '—', sizeDesc: o.size || '—', amount: parseFloat(o.total_charged || 0), order: o };
    }),
    ...deletedInvoices.map(function(i) {
      return { _type: 'deleted_invoice', _key: 'di_' + i.id, _date: i.deleted_at, ref: i.invoice_ref || i.invoice_display || '—', clientName: i.guest_name || i.company_name || '—', sizeDesc: (i.line_items && i.line_items[0]) ? i.line_items[0].description : '—', amount: parseFloat(i.total || 0), invoice: i };
    }),
  ].sort(function(a, b) { return new Date(b._date) - new Date(a._date); });
  const { sub: newSub, tax: newTax, total: newTotal } = invTotals(newInv.line_items, newInv.tax_enabled);
  const { sub: editSub, tax: editTax, total: editTotal } = editDraft
    ? invTotals(editDraft.line_items, editDraft.tax_enabled)
    : { sub: 0, tax: 0, total: 0 };
  const payoutsSorted = payoutsList.slice().sort(function(a, b) { return new Date(b.earned_at) - new Date(a.earned_at); });
  const payoutsSliced = payoutsSorted.slice(0, payoutsVisible);

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <h1
          onClick={function() {
            setTab('dashboard');
            setMarketPortalView('list');
            setSelectedMarketName(null);
            setSelectedOrder(null);
            setSelectedInv(null);
          }}
          style={{ margin: 0, fontSize: 22, cursor: 'pointer' }}
        >Tambo Admin</h1>
        <button onClick={() => supabase.auth.signOut()} style={s.btnRed}>Sign out</button>
      </div>

      {/* ── Rejection alert banner ── */}
      {rejections.length > 0 && (
        <div
          onClick={function() { setShowRejectionPanel(true); }}
          style={{ background: '#dc2626', color: '#fff', padding: '11px 20px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600,
            borderBottom: '2px solid #b91c1c' }}
        >
          <span style={{ fontSize: 16 }}>🔴</span>
          {rejections.length} hauler rejection{rejections.length !== 1 ? 's' : ''} need{rejections.length === 1 ? 's' : ''} attention
          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 500, opacity: 0.85 }}>Click to review →</span>
        </div>
      )}

      {/* ── Stale availability banner ── */}
      {dashSummary && (dashSummary.stale_haulers || []).length > 0 && (
        <div style={{ background: '#dc2626', color: '#fff', borderBottom: '2px solid #b91c1c' }}>
          <div
            onClick={function() { setStaleExpanded(function(v) { return !v; }); }}
            style={{ padding: '11px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600 }}
          >
            <span style={{ fontSize: 16 }}>🔴</span>
            {dashSummary.stale_haulers.length} hauler{dashSummary.stale_haulers.length !== 1 ? 's' : ''} haven&apos;t confirmed availability in 2+ days
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 500, opacity: 0.85 }}>
              {staleExpanded ? 'Hide ▲' : 'Review →'}
            </span>
          </div>
          {staleExpanded && (
            <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {dashSummary.stale_haulers.map(function(h) {
                const lastConf = h.inventory && h.inventory.length > 0
                  ? h.inventory.reduce(function(latest, row) {
                      if (!row.last_confirmed_at) return latest;
                      return !latest || row.last_confirmed_at > latest ? row.last_confirmed_at : latest;
                    }, null)
                  : null;
                const lastConfLabel = lastConf
                  ? (function() {
                      const d = new Date(lastConf);
                      const daysAgo = Math.floor((Date.now() - d.getTime()) / 86400000);
                      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      return 'Last confirmed: ' + dateStr + ' (' + daysAgo + ' day' + (daysAgo !== 1 ? 's' : '') + ' ago)';
                    })()
                  : 'Never confirmed';
                const isBusy = markContactedLoading === h.id;
                return (
                  <div key={h.id} style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 8, padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                        {h.company_name}
                        {(h.owner_first_name || h.owner_last_name) && (
                          <span style={{ fontWeight: 400, marginLeft: 6, opacity: 0.85 }}>
                            {[h.owner_first_name, h.owner_last_name].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </div>
                      {h.phone && (
                        <div style={{ fontSize: 12, marginBottom: 2 }}>
                          <a href={'tel:' + h.phone} style={{ color: '#fff', textDecoration: 'underline' }}>{h.phone}</a>
                        </div>
                      )}
                      <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>
                        {h.missed_checkins} day{h.missed_checkins !== 1 ? 's' : ''} missed · {lastConfLabel}
                      </div>
                      {(h.inventory || []).length > 0 && (
                        <div style={{ fontSize: 11, opacity: 0.8 }}>
                          Inventory on file: {h.inventory.map(function(row) { return row.size.replace('yd', '-yd') + ' ×' + row.quantity; }).join(', ')}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={function() { markContacted(h.id); }}
                      disabled={isBusy}
                      style={{ padding: '7px 14px', background: '#fff', color: '#dc2626', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: isBusy ? 'default' : 'pointer', opacity: isBusy ? 0.6 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      {isBusy ? 'Saving...' : 'Mark contacted'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab nav */}
      <div style={s.tabBar}>
        <button style={{ ...s.tabBtn, ...(tab === 'dashboard' ? s.tabBtnActive : {}) }} onClick={() => setTab('dashboard')}>Dashboard</button>
        <button style={{ ...s.tabBtn, ...(tab === 'all' ? s.tabBtnActive : {}) }} onClick={() => setTab('all')}>All Orders</button>
        <button style={{ ...s.tabBtn, ...(tab === 'payouts' ? s.tabBtnActive : {}) }} onClick={() => setTab('payouts')}>
          Payouts
          {payoutsSummary && parseFloat(payoutsSummary.totals?.total_pending) > 0 && (
            <span style={{ ...s.badge, background: '#f59e0b' }}>{payoutsSummary.by_hauler?.length || ''}</span>
          )}
        </button>
        <button style={{ ...s.tabBtn, ...(tab === 'haulers' ? s.tabBtnActive : {}) }} onClick={() => setTab('haulers')}>
          Haulers
          {pendingHaulers > 0 && <span style={s.badge}>{pendingHaulers}</span>}
        </button>
        <button style={{ ...s.tabBtn, ...(tab === 'clients' ? s.tabBtnActive : {}) }} onClick={() => setTab('clients')}>
          Clients
          {pendingClients > 0 && <span style={s.badge}>{pendingClients}</span>}
        </button>
        <button style={{ ...s.tabBtn, ...(tab === 'markets' ? s.tabBtnActive : {}) }} onClick={() => setTab('markets')}>Mkt Settings</button>
        <button style={{ ...s.tabBtn, ...(tab === 'market-portal' ? s.tabBtnActive : {}) }} onClick={() => setTab('market-portal')}>Market Portal</button>
        <button style={{ ...s.tabBtn, ...(tab === 'processes' ? s.tabBtnActive : {}) }} onClick={() => setTab('processes')}>Processes</button>
        <button style={{ ...s.tabBtn, ...(tab === 'requests' ? s.tabBtnActive : {}) }} onClick={() => setTab('requests')}>
          Requests
          {pendingRequests > 0 && <span style={s.badge}>{pendingRequests}</span>}
        </button>
        <button style={{ ...s.tabBtn, ...(tab === 'reassign' ? s.tabBtnActive : {}) }} onClick={() => setTab('reassign')}>
          Reassign
          {reassignQueue.length > 0 && <span style={{ ...s.badge, background: '#dc2626' }}>{reassignQueue.length}</span>}
        </button>
      </div>

      {/* ── Dashboard tab ── */}
      {tab === 'dashboard' && (
        <DashboardTab
          summary={dashSummary}
          loading={dashLoading}
          error={dashError}
          onNavigate={function(t) { if (t === 'market-portal') setMarketPortalView('list'); setTab(t); }}
          onNewInvoice={function() { setTab('all'); setShowNewInv(true); setNewInvError(''); setNewInv(emptyInvoice()); }}
        />
      )}

      {/* ── Market Portal tab (Level 2 + 3) ── */}
      {tab === 'market-portal' && marketPortalView === 'list' && (
        <MarketListPortal
          onSelect={function(name) { setSelectedMarketName(name); setMarketPortalView('detail'); }}
          onBack={function() { setTab('dashboard'); }}
        />
      )}
      {tab === 'market-portal' && marketPortalView === 'detail' && selectedMarketName && (
        <MarketDetailPortal
          marketName={selectedMarketName}
          onBack={function() { setMarketPortalView('list'); }}
          onOpenHauler={function(h) { openHaulerFromMarket(h, selectedMarketName); }}
          onOpenClient={function(c) { openClientFromMarket(c, selectedMarketName); }}
          onNewInvoice={function(refreshFn) { openInvoiceModalForMarket(selectedMarketName, refreshFn); }}
          isOwner={isOwner}
          onRecordPayment={openRecordPayment}
          onRemovePayment={removePaymentEntry}
          onVoidInvoice={voidInvoice}
          onDuplicateInvoice={duplicateInvoice}
          onDownloadPdf={downloadInvoicePdf}
        />
      )}

      {/* ── All Orders tab ── */}
      {tab === 'all' && (
        <>
          <div style={s.stats}>
            <StatCard label="Total Revenue" value={`$${totalRevenue.toFixed(2)}`} />
            <StatCard label="Online Orders" value={orders.length} />
            <StatCard label="Manual Invoices" value={manualInvoices.length} />
            <StatCard label="Pending" value={pendingActions} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              {(selectedInv || selectedOrder) && (
                <button onClick={function() { setSelectedInv(null); setSelectedOrder(null); }} style={s.refreshBtn}>← Back to list</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { fetchOrders(); fetchInvoices(); }} style={s.refreshBtn} disabled={ordersLoading || invLoading}>Refresh</button>
              {!selectedInv && !selectedOrder && (
                <button onClick={() => { setShowNewInv(true); setNewInvError(''); setNewInv(emptyInvoice()); }} style={s.btnInvNew}>
                  + New Invoice
                </button>
              )}
            </div>
          </div>

          {(ordersError || invError) && (
            <p style={{ ...s.error, marginBottom: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 6 }}>{ordersError || invError}</p>
          )}
          {invSendMsg && (
            <p style={{ marginBottom: 12, padding: '8px 12px', background: '#f0fdf4', color: '#166534', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>{invSendMsg}</p>
          )}

          {selectedOrder ? (
            <>
              <OrderDetail
                order={selectedOrder}
                onMarketChanged={function(id, mkt) {
                  setSelectedOrder(function(prev) { return { ...prev, market_name: mkt }; });
                }}
                onClientUpdated={function(fields) { setSelectedOrder(function(p) { return { ...p, ...fields }; }); }}
              />
              {isOwner && (
                <div style={{ marginTop: 16 }}>
                  <button
                    onClick={function() { setOrderDeleteTarget(selectedOrder); setOrderDeleteTextHauler(false); setOrderDeleteError(''); setOrderDeleteWarnPaid(false); setOrderDeleteWarnOnSite(false); }}
                    style={{ ...s.btnSmRed, fontSize: 12, padding: '6px 14px' }}
                  >Delete Order (owner only)</button>
                </div>
              )}
              {selectedInv && (
                <div style={{ marginTop: 28 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>Billing</span>
                    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                  </div>
                  <InvoiceDetail
                    inv={selectedInv}
                    actionId={invActionId}
                    error={invError}
                    isOwner={isOwner}
                    onSend={() => sendInvoice(selectedInv)}
                    onMarkPaid={() => markPaid(selectedInv)}
                    onUnmarkPaid={() => unmarkPaid(selectedInv)}
                    onDelete={() => deleteInvoice(selectedInv)}
                    onEdit={() => openEditInvoice(selectedInv)}
                    onRecordPayment={() => openRecordPayment(selectedInv)}
                    onRemovePayment={(pid) => removePaymentEntry(selectedInv, pid)}
                    onVoid={() => voidInvoice(selectedInv)}
                    onDuplicate={() => duplicateInvoice(selectedInv)}
                    onDownloadPdf={() => downloadInvoicePdf(selectedInv)}
                    onOwnerDelete={() => setInvOwnerDeleteTarget(selectedInv)}
                  />
                </div>
              )}
            </>
          ) : selectedInv ? (
            <>
              <InvoiceDetail
                inv={selectedInv}
                actionId={invActionId}
                error={invError}
                isOwner={isOwner}
                onSend={() => sendInvoice(selectedInv)}
                onMarkPaid={() => markPaid(selectedInv)}
                onUnmarkPaid={() => unmarkPaid(selectedInv)}
                onDelete={() => deleteInvoice(selectedInv)}
                onEdit={() => openEditInvoice(selectedInv)}
                onRecordPayment={() => openRecordPayment(selectedInv)}
                onRemovePayment={(pid) => removePaymentEntry(selectedInv, pid)}
                onVoid={() => voidInvoice(selectedInv)}
                onDuplicate={() => duplicateInvoice(selectedInv)}
                onDownloadPdf={() => downloadInvoicePdf(selectedInv)}
                onOwnerDelete={() => setInvOwnerDeleteTarget(selectedInv)}
              />
            </>
          ) : (ordersLoading || invLoading) ? (
            <p style={{ color: '#6b7280' }}>Loading...</p>
          ) : (
            <div ref={ordersListRef} style={{ scrollMarginTop: 80 }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                  {[{ val: 'all', label: 'All' }, { val: 'active', label: 'Active' }, { val: 'completed', label: 'Completed' }, { val: 'cancelled', label: 'Cancelled' }, ...(isOwner ? [{ val: 'deleted', label: 'Deleted' }] : [])].map(function(opt, i) {
                    var active = ordersStatusFilter === opt.val;
                    var isDelTab = opt.val === 'deleted';
                    return (
                      <button key={opt.val} onClick={function() {
                        setOrdersStatusFilter(opt.val); setOrdersVisible(10);
                        if (opt.val === 'deleted') fetchDeleted();
                      }}
                        style={{ padding: '8px 13px', background: active ? (isDelTab ? '#dc2626' : '#1A7A4A') : '#fff', color: active ? '#fff' : (isDelTab ? '#dc2626' : '#374151'), border: 'none', borderLeft: i > 0 ? '1px solid #e5e7eb' : 'none', fontSize: 13, fontWeight: active ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  placeholder="Search by TMB # or client name..."
                  value={orderSearch}
                  onChange={function(e) { setOrderSearch(e.target.value); setOrdersVisible(10); }}
                  style={{ flex: 1, minWidth: 180, padding: '9px 13px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            {ordersStatusFilter === 'deleted' ? (
              /* ── Deleted tab (owner only) ── */
              <div>
                {deletedLoading ? (
                  <p style={{ color: '#6b7280' }}>Loading...</p>
                ) : deletedItems.length === 0 ? (
                  <p style={{ color: '#6b7280' }}>No deleted orders or invoices.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          {['Reference #', 'Client', 'Type', 'Amount', 'Deleted', 'Actions'].map(function(h) {
                            return <th key={h} style={s.th}>{h}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {deletedItems.map(function(item) {
                          var isDelOrder = item._type === 'deleted_order';
                          var o = item.order;
                          var inv = item.invoice;
                          var isPermDelO = orderPermDeleteTarget && orderPermDeleteTarget.id === (o && o.id);
                          var isPermDelI = invPermDeleteTarget && invPermDeleteTarget.id === (inv && inv.id);
                          return (
                            <tr key={item._key} style={s.row}>
                              <td style={{ ...s.td, fontWeight: 600, color: '#dc2626' }}>{item.ref}</td>
                              <td style={s.td}>{item.clientName}</td>
                              <td style={s.td}>
                                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: isDelOrder ? '#eff6ff' : '#f0fdf4', color: isDelOrder ? '#1d4ed8' : '#15803d' }}>
                                  {isDelOrder ? 'Order' : 'Invoice'}
                                </span>
                              </td>
                              <td style={{ ...s.td, fontWeight: 600 }}>{fmtMoney(item.amount)}</td>
                              <td style={{ ...s.td, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(item._date)}</td>
                              <td style={s.td} onClick={function(e) { e.stopPropagation(); }}>
                                {isDelOrder && isPermDelO ? (
                                  /* Perm-delete order confirmation inline */
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                                    <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Type order # to confirm permanent delete:</span>
                                    <input
                                      value={orderPermDeleteConfirm}
                                      onChange={function(e) { setOrderPermDeleteConfirm(e.target.value); setOrderPermDeleteError(''); }}
                                      placeholder={o.order_number}
                                      style={{ padding: '6px 10px', border: '1.5px solid #dc2626', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                                    />
                                    {orderPermDeleteError && <span style={{ fontSize: 11, color: '#dc2626' }}>{orderPermDeleteError}</span>}
                                    <div style={{ display: 'flex', gap: 6 }}>
                                      <button
                                        onClick={function() { permDeleteOrder(o); }}
                                        disabled={orderPermDeleteLoading}
                                        style={{ ...s.btnSmRed, fontWeight: 700 }}
                                      >{orderPermDeleteLoading ? '...' : 'Delete Forever'}</button>
                                      <button
                                        onClick={function() { setOrderPermDeleteTarget(null); setOrderPermDeleteConfirm(''); setOrderPermDeleteError(''); }}
                                        style={s.btnSm}
                                      >Cancel</button>
                                    </div>
                                  </div>
                                ) : !isDelOrder && isPermDelI ? (
                                  /* Perm-delete invoice confirmation inline */
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                                    <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Type invoice # to confirm:</span>
                                    <input
                                      value={invPermDeleteConfirm}
                                      onChange={function(e) { setInvPermDeleteConfirm(e.target.value); setInvPermDeleteError(''); }}
                                      placeholder={inv.invoice_ref}
                                      style={{ padding: '6px 10px', border: '1.5px solid #dc2626', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                                    />
                                    {invPermDeleteError && <span style={{ fontSize: 11, color: '#dc2626' }}>{invPermDeleteError}</span>}
                                    <div style={{ display: 'flex', gap: 6 }}>
                                      <button
                                        onClick={function() { permDeleteInvoice(inv); }}
                                        disabled={invPermDeleteLoading}
                                        style={{ ...s.btnSmRed, fontWeight: 700 }}
                                      >{invPermDeleteLoading ? '...' : 'Delete Forever'}</button>
                                      <button
                                        onClick={function() { setInvPermDeleteTarget(null); setInvPermDeleteConfirm(''); setInvPermDeleteError(''); }}
                                        style={s.btnSm}
                                      >Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                    <button
                                      onClick={function() { isDelOrder ? restoreOrder(o) : restoreInvoice(inv); }}
                                      style={{ ...s.btnSmGreen }}
                                    >Restore</button>
                                    <button
                                      onClick={function() { isDelOrder ? setOrderPermDeleteTarget(o) : setInvPermDeleteTarget(inv); setOrderPermDeleteConfirm(''); setOrderPermDeleteError(''); setInvPermDeleteConfirm(''); setInvPermDeleteError(''); }}
                                      style={{ ...s.btnSmRed }}
                                    >Delete Permanently</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
            <>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Reference #', 'Client', 'Type', 'Size / Description', 'Amount', 'Balance Due', 'Status', 'Date', 'Actions'].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr><td colSpan={9} style={{ ...s.td, textAlign: 'center', color: '#9ca3af' }}>
                      {orderSearch.trim() ? `No results for "${orderSearch}"` : ordersStatusFilter !== 'all' ? `No ${ordersStatusFilter} orders or invoices.` : 'No orders or invoices yet.'}
                    </td></tr>
                  ) : filteredItems.slice(0, ordersVisible).map(function(item) {
                    const isOrder = item._type === 'order';
                    const orderData = item.order;
                    const invData = item.invoice;
                    const isActing = invData && invActionId && invActionId.startsWith(invData.id);
                    return (
                      <tr
                        key={item._key}
                        style={{ ...s.row, cursor: 'pointer', background: hoveredRowKey === item._key ? '#f0f9ff' : undefined }}
                        onMouseEnter={function() { setHoveredRowKey(item._key); }}
                        onMouseLeave={function() { setHoveredRowKey(null); }}
                        onClick={function() {
                          if (isOrder) { setSelectedOrder(orderData); setSelectedInv(invData); }
                          else { setSelectedInv(invData); setSelectedOrder(null); }
                        }}
                      >
                        <td style={{ ...s.td, fontWeight: 600, color: '#1A7A4A', whiteSpace: 'nowrap' }}>{item.ref}</td>
                        <td style={s.td}>{item.clientName}</td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: isOrder ? '#eff6ff' : '#f0fdf4', color: isOrder ? '#1d4ed8' : '#15803d', whiteSpace: 'nowrap' }}>
                              {isOrder ? 'Online Order' : 'Invoice'}
                            </span>
                            {isOrder && item.invStatus && <InvoiceBadge status={item.invStatus} />}
                          </div>
                        </td>
                        <td style={{ ...s.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sizeDesc}</td>
                        <td style={{ ...s.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(item.amount)}</td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                          {invData && invData.status !== 'void' ? (function() {
                            const ents = invData.payment_log_entries || [];
                            const paid = ents.reduce(function(s, p) { return s + parseFloat(p.amount || 0); }, 0);
                            const bal = (invData.status === 'paid' && paid === 0) ? 0 : Math.max(0, Math.round((parseFloat(invData.total || 0) - paid) * 100) / 100);
                            const ds2 = invDisplayStatus(invData);
                            return <span style={{ fontWeight: 700, color: ds2 === 'paid' ? '#10b981' : ds2 === 'overdue' ? '#ef4444' : '#0D0D0D' }}>{fmtMoney(bal)}</span>;
                          })() : <span style={{ color: '#d1d5db' }}>—</span>}
                        </td>
                        <td style={s.td}>
                          {isOrder ? (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                              <StatusBadge status={orderData.status} />
                              {orderData.needs_attention && (
                                <span style={{ background: '#dc2626', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>NEEDS ATTN</span>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <InvoiceBadge status={item.invStatus} />
                              {item.invStatus !== 'paid' && item.invStatus !== 'draft' && item.invoice && (
                                <ViewedBadge inv={item.invoice} />
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap', color: '#6b7280' }}>{fmtDate(item._date)}</td>
                        <td style={s.td} onClick={function(e) { e.stopPropagation(); }}>
                          {isOrder ? (
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              <a href={`/order/${orderData.id}`} target="_blank" rel="noopener noreferrer"
                                 style={{ ...s.btnSm, textDecoration: 'none', display: 'inline-block' }}>
                                Track ↗
                              </a>
                              {invData && invData.status !== 'paid' && (
                                <button onClick={function() { markPaid(invData); }} disabled={!!isActing} style={s.btnSmGreen}>
                                  {invActionId === invData.id + '_pay' ? '...' : 'Paid'}
                                </button>
                              )}
                              {parseFloat(orderData.locked_hauler_payout || orderData.hauler_payout || 0) > 0
                                && orderData.hauler_id
                                && !orderData.payout_id && (
                                <button
                                  onClick={function() { createPayout(orderData.id); }}
                                  disabled={createPayoutLoading === orderData.id}
                                  style={{ ...s.btnSm, background: '#f59e0b', color: '#fff', border: 'none', cursor: 'pointer' }}
                                  title="Create missing hauler payout row"
                                >
                                  {createPayoutLoading === orderData.id ? '...' : 'Create Payout'}
                                </button>
                              )}
                              {isOwner && (
                                <button
                                  onClick={function() { setOrderDeleteTarget(orderData); setOrderDeleteTextHauler(false); setOrderDeleteError(''); setOrderDeleteWarnPaid(false); setOrderDeleteWarnOnSite(false); }}
                                  style={{ ...s.btnSmRed, fontSize: 11 }}
                                  title="Delete order (owner only)"
                                >Del</button>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              {invData.status !== 'paid' && (
                                <button
                                  onClick={function() { sendInvoice(invData); }}
                                  disabled={!!isActing || !invData.guest_email}
                                  style={{ ...s.btnSmBlue, opacity: (!!isActing || !invData.guest_email) ? 0.45 : 1, cursor: (!!isActing || !invData.guest_email) ? 'not-allowed' : 'pointer' }}
                                  title={!invData.guest_email ? 'Add an email to send this invoice' : ''}
                                >
                                  {invActionId === invData.id + '_send' ? '...' : invData.status === 'sent' ? 'Resend' : 'Send'}
                                </button>
                              )}
                              {invData.status !== 'paid' && (
                                <button onClick={function() { markPaid(invData); }} disabled={!!isActing} style={{ ...s.btnSmGreen, opacity: isActing ? 0.45 : 1, cursor: isActing ? 'not-allowed' : 'pointer' }}>
                                  {invActionId === invData.id + '_pay' ? '...' : 'Paid'}
                                </button>
                              )}
                              {invData.status === 'draft' && !isOwner && (
                                <button onClick={function() { deleteInvoice(invData); }} disabled={!!isActing} style={s.btnSmRed}>
                                  {invActionId === invData.id + '_del' ? '...' : 'Del'}
                                </button>
                              )}
                              {isOwner && (
                                <button
                                  onClick={function() { setInvOwnerDeleteTarget(invData); }}
                                  disabled={!!isActing}
                                  style={{ ...s.btnSmRed, fontSize: 11 }}
                                  title="Delete invoice (owner only)"
                                >{invOwnerDeleteLoading && invOwnerDeleteTarget && invOwnerDeleteTarget.id === invData.id ? '...' : 'Del'}</button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredItems.length > 0 && (
              <div style={{ textAlign: 'center', padding: '16px 0 6px' }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                  Showing {Math.min(ordersVisible, filteredItems.length)} of {filteredItems.length}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {filteredItems.length > ordersVisible && (
                    <button
                      onClick={function() { setOrdersVisible(function(v) { return v + 10; }); }}
                      style={{ background: '#1A7A4A', color: 'white', border: 'none', borderRadius: 7, padding: '9px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Show More
                    </button>
                  )}
                  {ordersVisible > 10 && (
                    <button
                      onClick={function() { setOrdersVisible(10); ordersListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                      style={{ background: 'transparent', color: '#1A7A4A', border: '2px solid #1A7A4A', borderRadius: 7, padding: '7px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Show Less
                    </button>
                  )}
                </div>
              </div>
            )}
            </>
            )}
            </div>
          )}
        </>
      )}

      {/* ── Order soft-delete confirmation dialog ── */}
      {orderDeleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#111' }}>Delete order {orderDeleteTarget.order_number}?</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
              This order and its linked invoice will be hidden from all views. You can restore it from the Deleted tab.
            </p>
            {orderDeleteWarnPaid && (
              <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 7, padding: '10px 14px', marginBottom: 10, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                Paid order — refund it in Stripe separately.
              </div>
            )}
            {orderDeleteWarnOnSite && (
              <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 7, padding: '10px 14px', marginBottom: 10, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                Dumpster is on site — inventory not restored. Update it in the Haulers tab after pickup.
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 18, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={orderDeleteTextHauler}
                onChange={function(e) { setOrderDeleteTextHauler(e.target.checked); }}
                style={{ width: 15, height: 15 }}
              />
              Text the hauler that this job is canceled
            </label>
            {orderDeleteError && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{orderDeleteError}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={function() { softDeleteOrder(orderDeleteTarget, orderDeleteTextHauler); }}
                disabled={orderDeleteLoading}
                style={{ ...s.btnSmRed, padding: '9px 20px', fontSize: 14, fontWeight: 700 }}
              >{orderDeleteLoading ? 'Deleting...' : 'Delete'}</button>
              <button
                onClick={function() { setOrderDeleteTarget(null); setOrderDeleteError(''); setOrderDeleteWarnPaid(false); setOrderDeleteWarnOnSite(false); }}
                style={{ ...s.btnSm, padding: '9px 20px', fontSize: 14 }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Standalone invoice soft-delete confirmation (owner) ── */}
      {invOwnerDeleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 400, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#111' }}>Delete invoice {invOwnerDeleteTarget.invoice_ref || invOwnerDeleteTarget.invoice_display}?</h3>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>
              This invoice will be hidden. You can restore it from the Deleted tab.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={function() { softDeleteInvoiceOwner(invOwnerDeleteTarget); }}
                disabled={invOwnerDeleteLoading}
                style={{ ...s.btnSmRed, padding: '9px 20px', fontSize: 14, fontWeight: 700 }}
              >{invOwnerDeleteLoading ? 'Deleting...' : 'Delete'}</button>
              <button
                onClick={function() { setInvOwnerDeleteTarget(null); }}
                style={{ ...s.btnSm, padding: '9px 20px', fontSize: 14 }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Payouts tab ── */}
      {tab === 'payouts' && (
        <div>
          {payoutDetailPayout ? (
            /* ── Payout detail view ── */
            <div>
              <button
                onClick={function() { setPayoutDetailPayout(null); setPayoutDetailOrder(null); setPayoutDetailInv(null); setInvError(''); }}
                style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '0 0 16px', display: 'block' }}
              >← Back to Payouts</button>

              {/* Payout status banner */}
              <div style={{ background: payoutDetailPayout.status === 'paid' ? '#f0fdf4' : '#fffbeb', border: '1px solid ' + (payoutDetailPayout.status === 'paid' ? '#bbf7d0' : '#fde68a'), borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: 5 }}>Hauler Payout</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: payoutDetailPayout.status === 'paid' ? '#1A7A4A' : '#f59e0b' }}>{fmtMoney(payoutDetailPayout.amount)}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: payoutDetailPayout.status === 'paid' ? '#bbf7d0' : '#fde68a', color: payoutDetailPayout.status === 'paid' ? '#166534' : '#92400e' }}>
                      {payoutDetailPayout.status === 'paid' ? '✓ Paid' : '● Pending'}
                    </span>
                    {payoutDetailPayout.status === 'paid' && payoutDetailPayout.paid_at && (
                      <span style={{ fontSize: 11, color: '#6b7280' }}>on {fmtDate(payoutDetailPayout.paid_at)}{payoutDetailPayout.payment_method ? ' via ' + payoutDetailPayout.payment_method : ''}{payoutDetailPayout.payment_reference ? ' · Ref: ' + payoutDetailPayout.payment_reference : ''}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{payoutDetailPayout.hauler_name}{payoutDetailPayout.hauler_phone ? ' · ' + payoutDetailPayout.hauler_phone : ''}</div>
                </div>
                {payoutDetailPayout.status !== 'paid' && (
                  <button
                    onClick={function() { setPayModal({ mode: 'single', haulerName: payoutDetailPayout.hauler_name, payoutIds: [payoutDetailPayout.id], totalAmount: parseFloat(payoutDetailPayout.amount) }); setPayForm({ payment_reference: '', payment_method: 'ACH', paid_at: new Date().toISOString().split('T')[0], notes: '' }); setPayError(''); }}
                    style={{ padding: '9px 18px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >Mark Payout Paid</button>
                )}
              </div>

              {payoutDetailOrder ? (
                <>
                  <OrderDetail
                    order={payoutDetailOrder}
                    onMarketChanged={function(id, mkt) { setPayoutDetailOrder(function(p) { return { ...p, market_name: mkt }; }); }}
                    onClientUpdated={function(fields) { setPayoutDetailOrder(function(p) { return { ...p, ...fields }; }); }}
                  />
                  {payoutDetailInvLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>Loading invoice…</div>
                  ) : payoutDetailInv ? (
                    <div style={{ marginTop: 28 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span>Billing</span>
                        <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                      </div>
                      <InvoiceDetail
                        inv={payoutDetailInv}
                        actionId={invActionId}
                        error={invError}
                        isOwner={isOwner}
                        onSend={function() { pdSendInvoice(payoutDetailInv); }}
                        onMarkPaid={function() { pdMarkPaid(payoutDetailInv); }}
                        onUnmarkPaid={function() { pdUnmarkPaid(payoutDetailInv); }}
                        onDelete={function() { pdDeleteInvoice(payoutDetailInv); }}
                        onRecordPayment={function() { openRecordPayment(payoutDetailInv); }}
                        onRemovePayment={function(pid) { removePaymentEntry(payoutDetailInv, pid); }}
                        onVoid={function() { voidInvoice(payoutDetailInv); }}
                        onDuplicate={function() { duplicateInvoice(payoutDetailInv); }}
                        onDownloadPdf={function() { downloadInvoicePdf(payoutDetailInv); }}
                      />
                    </div>
                  ) : null}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 14 }}>Loading order…</div>
              )}
            </div>
          ) : (
            /* ── Payouts list view ── */
            <>
              {/* Summary cards */}
              {payoutsSummary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 28 }}>
                  {[
                    { label: 'Total Pending', value: fmtMoney(payoutsSummary.totals?.total_pending), color: '#f59e0b', bg: '#fffbeb' },
                    { label: 'Paid This Week', value: fmtMoney(payoutsSummary.totals?.paid_this_week), color: '#1A7A4A', bg: '#f0fdf4' },
                    { label: 'Paid This Month', value: fmtMoney(payoutsSummary.totals?.paid_this_month), color: '#1d4ed8', bg: '#eff6ff' },
                    { label: 'All-Time Paid', value: fmtMoney(payoutsSummary.totals?.total_paid_all_time), color: '#374151', bg: '#fff' },
                  ].map(function(c) {
                    return (
                      <div key={c.label} style={{ background: c.bg, border: '1px solid #f0f0ef', borderRadius: 12, padding: '20px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: 8 }}>{c.label}</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: c.color }}>{c.value}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Filter bar */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Status toggle buttons */}
                <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  {[{ val: 'all', label: 'All' }, { val: 'pending', label: 'Pending' }, { val: 'paid', label: 'Paid' }].map(function(opt, i) {
                    var active = payoutsStatusFilter === opt.val;
                    return (
                      <button key={opt.val} onClick={function() { setPayoutsStatusFilter(opt.val); setPayoutsVisible(10); }}
                        style={{ padding: '8px 16px', background: active ? '#1A7A4A' : '#fff', color: active ? '#fff' : '#374151', border: 'none', borderLeft: i > 0 ? '1px solid #e5e7eb' : 'none', fontSize: 13, fontWeight: active ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <select value={payoutsMarketFilter} onChange={function(e) { setPayoutsMarketFilter(e.target.value); setPayoutsVisible(10); }} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                  <option value="all">All Markets</option>
                  {markets.filter(function(m) { return m.market_name !== 'Default' && m.market_name !== 'Unassigned'; }).map(function(m) {
                    return <option key={m.market_name} value={m.market_name}>{m.market_name}</option>;
                  })}
                </select>
                <button onClick={function() { fetchPayouts(); fetchPayoutsSummary(); setPayoutsVisible(10); }} style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>Refresh</button>
              </div>

              {payoutsError && <div style={{ color: '#ef4444', marginBottom: 14, fontSize: 13 }}>{payoutsError}</div>}

              {payoutsLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 14 }}>Loading payouts…</div>
              ) : payoutsSorted.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '70px 0', color: '#9ca3af', fontSize: 14 }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>💸</div>
                  <div style={{ fontWeight: 700, color: '#374151', marginBottom: 6 }}>No {payoutsStatusFilter === 'pending' ? 'pending' : ''} payouts</div>
                  <div>Haulers earn payout records automatically when they text DONE.</div>
                </div>
              ) : payoutsStatusFilter === 'pending' ? (
                /* Grouped-by-hauler view for pending — paginated */
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {Object.entries(
                      payoutsSliced.reduce(function(acc, p) {
                        if (!acc[p.hauler_id]) acc[p.hauler_id] = { haulerName: p.hauler_name, haulerPhone: p.hauler_phone, payouts: [] };
                        acc[p.hauler_id].payouts.push(p);
                        return acc;
                      }, {})
                    ).map(function(entry) {
                      var haulerId = entry[0]; var group = entry[1];
                      var totalAmt = group.payouts.reduce(function(s, p) { return s + parseFloat(p.amount); }, 0);
                      return (
                        <div key={haulerId} style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0ef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#fafafa', borderBottom: '1px solid #f0f0ef', flexWrap: 'wrap', gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>🚛 {group.haulerName}</div>
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{group.payouts.length} unpaid job{group.payouts.length !== 1 ? 's' : ''}{group.haulerPhone ? ' · ' + group.haulerPhone : ''}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                              <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>{fmtMoney(totalAmt)}</div>
                              <button
                                onClick={function(e) { e.stopPropagation(); setPayModal({ mode: 'batch', haulerName: group.haulerName, payoutIds: group.payouts.map(function(p) { return p.id; }), totalAmount: totalAmt }); setPayForm({ payment_reference: '', payment_method: 'ACH', paid_at: new Date().toISOString().split('T')[0], notes: '' }); setPayError(''); }}
                                style={{ padding: '9px 18px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                              >Pay All</button>
                            </div>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                              <thead>
                                <tr style={{ background: '#f9fafb' }}>
                                  {['Order #', 'Address', 'Size', 'Market', 'Earned', 'Amount', ''].map(function(col) {
                                    return <th key={col} style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', textAlign: col === 'Amount' || col === '' ? 'right' : 'left', whiteSpace: 'nowrap' }}>{col}</th>;
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {group.payouts.map(function(p) {
                                  return (
                                    <tr key={p.id} onClick={function() { openPayoutOrder(p); }} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }} onMouseEnter={function(e) { e.currentTarget.style.background = '#f9fafb'; }} onMouseLeave={function(e) { e.currentTarget.style.background = ''; }}>
                                      <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: '#1A7A4A' }}>#{p.order_number}</td>
                                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#374151', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.delivery_address || '—'}</td>
                                      <td style={{ padding: '12px 16px', fontSize: 13 }}>{p.size || '—'}</td>
                                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#6b7280' }}>{p.market_name || '—'}</td>
                                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(p.earned_at)}</td>
                                      <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 800, color: '#111', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(p.amount)}</td>
                                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                        <button
                                          onClick={function(e) { e.stopPropagation(); setPayModal({ mode: 'single', haulerName: group.haulerName, payoutIds: [p.id], totalAmount: parseFloat(p.amount) }); setPayForm({ payment_reference: '', payment_method: 'ACH', paid_at: new Date().toISOString().split('T')[0], notes: '' }); setPayError(''); }}
                                          style={{ padding: '5px 12px', background: '#f0fdf4', color: '#1A7A4A', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        >Mark Paid</button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Pagination footer */}
                  <div style={{ textAlign: 'center', padding: '20px 0 6px' }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Showing {Math.min(payoutsVisible, payoutsSorted.length)} of {payoutsSorted.length} payout{payoutsSorted.length !== 1 ? 's' : ''}</div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      {payoutsSorted.length > payoutsVisible && (
                        <button onClick={function() { setPayoutsVisible(function(v) { return v + 10; }); }} style={{ background: '#1A7A4A', color: 'white', border: 'none', borderRadius: 7, padding: '9px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Show More</button>
                      )}
                      {payoutsVisible > 10 && (
                        <button onClick={function() { setPayoutsVisible(10); }} style={{ background: 'transparent', color: '#1A7A4A', border: '2px solid #1A7A4A', borderRadius: 7, padding: '7px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Show Less</button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                /* Flat list for paid / all — paginated */
                <>
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0ef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                        <thead>
                          <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0ef' }}>
                            {['Hauler', 'Order #', 'Address', 'Size', 'Amount', 'Status', 'Earned', 'Paid', 'Method', 'Reference'].map(function(col) {
                              return <th key={col} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', textAlign: 'left', whiteSpace: 'nowrap' }}>{col}</th>;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {payoutsSliced.map(function(p) {
                            return (
                              <tr key={p.id} onClick={function() { openPayoutOrder(p); }} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }} onMouseEnter={function(e) { e.currentTarget.style.background = '#f9fafb'; }} onMouseLeave={function(e) { e.currentTarget.style.background = ''; }}>
                                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600 }}>{p.hauler_name}</td>
                                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 700, color: '#1A7A4A' }}>#{p.order_number}</td>
                                <td style={{ padding: '11px 14px', fontSize: 12, color: '#374151', maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.delivery_address || '—'}</td>
                                <td style={{ padding: '11px 14px', fontSize: 13 }}>{p.size || '—'}</td>
                                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 800 }}>{fmtMoney(p.amount)}</td>
                                <td style={{ padding: '11px 14px' }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: p.status === 'paid' ? '#f0fdf4' : '#fffbeb', color: p.status === 'paid' ? '#1A7A4A' : '#f59e0b', border: '1px solid ' + (p.status === 'paid' ? '#bbf7d0' : '#fde68a') }}>
                                    {p.status === 'paid' ? '✓ Paid' : '● Pending'}
                                  </span>
                                </td>
                                <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(p.earned_at)}</td>
                                <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{p.paid_at ? fmtDate(p.paid_at) : '—'}</td>
                                <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280' }}>{p.payment_method || '—'}</td>
                                <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280' }}>{p.payment_reference || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Pagination footer */}
                  <div style={{ textAlign: 'center', padding: '20px 0 6px' }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Showing {Math.min(payoutsVisible, payoutsSorted.length)} of {payoutsSorted.length} payout{payoutsSorted.length !== 1 ? 's' : ''}</div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      {payoutsSorted.length > payoutsVisible && (
                        <button onClick={function() { setPayoutsVisible(function(v) { return v + 10; }); }} style={{ background: '#1A7A4A', color: 'white', border: 'none', borderRadius: 7, padding: '9px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Show More</button>
                      )}
                      {payoutsVisible > 10 && (
                        <button onClick={function() { setPayoutsVisible(10); }} style={{ background: 'transparent', color: '#1A7A4A', border: '2px solid #1A7A4A', borderRadius: 7, padding: '7px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Show Less</button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Mark Paid modal — visible in both list and detail views ── */}
          {payModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}
              onClick={function(e) { if (e.target === e.currentTarget) setPayModal(null); }}
            >
              <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>{payModal.mode === 'batch' ? 'Pay All Pending Jobs' : 'Mark as Paid'}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{payModal.haulerName} — {fmtMoney(payModal.totalAmount)}</div>
                    {payModal.mode === 'batch' && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{payModal.payoutIds.length} job{payModal.payoutIds.length !== 1 ? 's' : ''} in this batch</div>}
                  </div>
                  <button onClick={function() { setPayModal(null); }} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>✕</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { label: 'Payment Method *', field: 'payment_method', type: 'select', options: ['ACH', 'Check', 'Zelle', 'Cash', 'Other'] },
                    { label: 'Payment Reference', field: 'payment_reference', type: 'text', placeholder: 'ACH ref, check #, transaction ID…' },
                    { label: 'Date Paid', field: 'paid_at', type: 'date' },
                    { label: 'Notes (optional)', field: 'notes', type: 'text', placeholder: 'Optional notes…' },
                  ].map(function(f) {
                    return (
                      <div key={f.field}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.label}</label>
                        {f.type === 'select' ? (
                          <select value={payForm[f.field]} onChange={function(e) { setPayForm(function(p) { return { ...p, [f.field]: e.target.value }; }); }} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13 }}>
                            {f.options.map(function(o) { return <option key={o} value={o}>{o}</option>; })}
                          </select>
                        ) : (
                          <input type={f.type || 'text'} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} placeholder={f.placeholder} value={payForm[f.field]} onChange={function(e) { setPayForm(function(p) { return { ...p, [f.field]: e.target.value }; }); }} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {payError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{payError}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button onClick={function() { setPayModal(null); }} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
                  <button onClick={markPayoutPaid} disabled={payLoading} style={{ flex: 2, padding: '10px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: payLoading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: payLoading ? 0.7 : 1 }}>
                    {payLoading ? 'Saving…' : payModal.mode === 'batch' ? 'Confirm Batch Payment' : 'Confirm Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ── Invoice Mark Paid modal — rendered outside all tabs so it works from any tab ── */}
      {invPayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}
          onClick={function(e) { if (e.target === e.currentTarget && !invPaySaving) setInvPayModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>Mark Invoice Paid</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{displayNum(invPayModal.inv)} — {fmtMoney(invPayModal.inv.total)}</div>
              </div>
              <button onClick={function() { setInvPayModal(null); }} disabled={invPaySaving} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Payment Method *</label>
                <select value={invPayMethod} onChange={function(e) { setInvPayMethod(e.target.value); }} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13 }}>
                  {['cash', 'zelle', 'check', 'other'].map(function(m) { return <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>; })}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reason / Note *</label>
                <textarea value={invPayReason} onChange={function(e) { setInvPayReason(e.target.value); }} rows={3} placeholder="e.g., Client paid via Zelle on 7/2/2026" style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            {invPayError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{invPayError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={function() { setInvPayModal(null); }} disabled={invPaySaving} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={submitInvMarkPaid} disabled={invPaySaving} style={{ flex: 2, padding: '10px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: invPaySaving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: invPaySaving ? 0.7 : 1 }}>
                {invPaySaving ? 'Saving…' : 'Mark as Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Record Payment modal — rendered outside all tabs ── */}
      {invRecordPayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}
          onClick={function(e) { if (e.target === e.currentTarget && !invRecordPaySaving) setInvRecordPayModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Record Payment</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{displayNum(invRecordPayModal)}</div>
              </div>
              <button onClick={function() { setInvRecordPayModal(null); }} disabled={invRecordPaySaving} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Amount *</label>
                <input type="number" step="0.01" min="0.01" value={invRecordPayForm.amount}
                  onChange={function(e) { setInvRecordPayForm(function(p) { return { ...p, amount: e.target.value }; }); }}
                  style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 14, fontWeight: 700, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Method *</label>
                <select value={invRecordPayForm.method}
                  onChange={function(e) { setInvRecordPayForm(function(p) { return { ...p, method: e.target.value }; }); }}
                  style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13 }}>
                  {[['cash','Cash'],['check','Check'],['zelle','Zelle'],['card_in_person','Card in person'],['other','Other']].map(function([v,l]) {
                    return <option key={v} value={v}>{l}</option>;
                  })}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reference # (check #, Zelle confirmation…)</label>
                <input type="text" value={invRecordPayForm.reference}
                  onChange={function(e) { setInvRecordPayForm(function(p) { return { ...p, reference: e.target.value }; }); }}
                  placeholder="Optional" style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date</label>
                <input type="date" value={invRecordPayForm.date}
                  onChange={function(e) { setInvRecordPayForm(function(p) { return { ...p, date: e.target.value }; }); }}
                  style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Note</label>
                <input type="text" value={invRecordPayForm.note}
                  onChange={function(e) { setInvRecordPayForm(function(p) { return { ...p, note: e.target.value }; }); }}
                  placeholder="Optional" style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>
            {invRecordPayError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{invRecordPayError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button onClick={function() { setInvRecordPayModal(null); }} disabled={invRecordPaySaving}
                style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitRecordPayment} disabled={invRecordPaySaving}
                style={{ flex: 2, padding: '10px 0', background: '#0D0D0D', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: invRecordPaySaving ? 'not-allowed' : 'pointer', opacity: invRecordPaySaving ? 0.7 : 1 }}>
                {invRecordPaySaving ? 'Saving…' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Generic confirm modal — replaces all window.confirm() calls ── */}
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <p style={{ fontSize: 15, color: '#111', marginBottom: 24, lineHeight: 1.55, margin: '0 0 24px' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={function() { setConfirmModal(null); }} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={function() { var fn = confirmModal.onConfirm; setConfirmModal(null); fn(); }} style={{ flex: 1, padding: '10px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>{confirmModal.label || 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject client modal with optional reason ── */}
      {rejectClientModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <p style={{ fontSize: 15, color: '#111', fontWeight: 700, margin: '0 0 6px' }}>Reject {rejectClientModal.name || 'this account'}?</p>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>The client will receive an email. You can include an optional reason below.</p>
            <textarea
              value={rejectReason}
              onChange={function(e) { setRejectReason(e.target.value); }}
              placeholder="Optional: reason for rejection (visible to the client)"
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#111', resize: 'vertical', marginBottom: 18 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={function() { setRejectClientModal(null); setRejectReason(''); }} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button
                onClick={function() { var id = rejectClientModal.id; var reason = rejectReason; setRejectClientModal(null); setRejectReason(''); doRejectClient(id, reason); }}
                style={{ flex: 1, padding: '10px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
              >
                Reject Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Haulers tab ── */}
      {tab === 'haulers' && (
        <>
          {mktReturnCtx && (
            <button
              onClick={function() { setTab('market-portal'); setMarketPortalView('detail'); setSelectedMarketName(mktReturnCtx.marketName); setMktReturnCtx(null); setExpandedHaulerId(null); }}
              style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '0 0 14px', display: 'block' }}
            >
              ← Back to {mktReturnCtx.marketName} Market
            </button>
          )}
          <div style={s.stats}>
            <StatCard label="Total Haulers" value={haulers.length} />
            <StatCard label="Pending Review" value={haulers.filter(h => h.status === 'pending').length} />
            <StatCard label="Approved" value={haulers.filter(h => h.status === 'approved').length} />
            <StatCard label="Suspended" value={haulers.filter(h => h.status === 'suspended').length} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 10 }}>
            <button onClick={function() { setAddHaulerForm(emptyHaulerForm); setAddHaulerError(''); setAddHaulerResult(null); if (markets.length === 0) fetchMarkets(); setShowAddHauler(true); }} style={{ padding: '8px 18px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Add Hauler</button>
            <button onClick={fetchHaulers} style={s.refreshBtn}>Refresh</button>
          </div>
          {haulersError && <p style={s.error}>{haulersError}</p>}
          {actionError && <p style={{ ...s.error, marginBottom: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 6 }}>{actionError}</p>}
          {haulersLoading ? (
            <p style={{ color: '#6b7280' }}>Loading haulers...</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Company', 'Email', 'Phone', 'Status', 'Date Applied', 'Actions'].map(col => (
                      <th key={col} style={s.th}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {haulers.length === 0 ? (
                    <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#9ca3af' }}>No haulers yet.</td></tr>
                  ) : haulers.map(h => {
                    const expanded = expandedHaulerId === h.id;
                    const isPending = h.status === 'pending';
                    return (
                      <React.Fragment key={h.id}>
                        <tr
                          style={{ ...s.row, cursor: 'pointer', background: isPending ? '#fffbeb' : expanded ? '#f9fafb' : undefined }}
                          onClick={() => setExpandedHaulerId(expanded ? null : h.id)}
                        >
                          <td style={{ ...s.td, fontWeight: 600 }}>
                            <span style={{ marginRight: 6, fontSize: 10, color: '#9ca3af' }}>{expanded ? '▼' : '▶'}</span>
                            {h.company_name || '—'}
                          </td>
                          <td style={s.td}>{h.email || '—'}</td>
                          <td style={s.td}>{h.phone || '—'}</td>
                          <td style={s.td}><HaulerStatusBadge status={h.status} /></td>
                          <td style={s.td}>{h.created_at ? new Date(h.created_at).toLocaleDateString() : '—'}</td>
                          <td style={s.td} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {h.status === 'pending' && (
                                <>
                                  <button onClick={() => approveHauler(h.id)} disabled={actionLoading === h.id} style={s.btnApprove}>
                                    {actionLoading === h.id ? '...' : '✓ Approve'}
                                  </button>
                                  <button onClick={() => rejectHauler(h.id)} disabled={actionLoading === h.id} style={s.btnReject}>
                                    {actionLoading === h.id ? '...' : '✕ Reject'}
                                  </button>
                                </>
                              )}
                              {h.status === 'approved' && (
                                <button onClick={() => suspendHauler(h.id)} disabled={suspendLoading === h.id} style={s.btnSuspend}>
                                  {suspendLoading === h.id ? '...' : 'Suspend'}
                                </button>
                              )}
                              {h.status === 'suspended' && (
                                <button onClick={() => unsuspendHauler(h.id)} disabled={suspendLoading === h.id} style={s.btnApprove}>
                                  {suspendLoading === h.id ? '...' : 'Unsuspend'}
                                </button>
                              )}
                              {h.status !== 'pending' && (
                                <button onClick={() => removeHauler(h.id, h.company_name)} disabled={removeLoading === h.id} style={s.btnRemove}>
                                  {removeLoading === h.id ? '...' : 'Remove'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0, borderBottom: '2px solid #e5e7eb' }}>
                              <div style={s.expandPanel}>
                                <div style={s.expandGrid}>
                                  <DetailField label="Company Name" value={h.company_name} />
                                  <DetailField label="Market" value={h.market_name || null} />
                                  <DetailField label="Owner" value={[h.owner_first_name, h.owner_last_name].filter(Boolean).join(' ') || null} />
                                  <DetailField label="Login Email" value={h.email} />
                                  <DetailField label="Phone" value={h.phone} />
                                  <DetailField label="EIN" value={h.ein} />
                                  <DetailField label="Status" value={<HaulerStatusBadge status={h.status} />} />
                                  <DetailField label="Date Applied" value={h.created_at ? new Date(h.created_at).toLocaleDateString() : null} />
                                  <DetailField label="Shop Address" value={h.shop_address} />
                                  <DetailField label="Years in Business" value={h.years_in_business != null ? String(h.years_in_business) : null} />
                                  <DetailField label="Dumpster Count" value={h.dumpster_count != null ? String(h.dumpster_count) : null} />
                                  <DetailField label="Counties Served" value={h.service_counties} />
                                  <DetailField label="Availability" value={h.availability_type ? { full_time: 'Full-time', part_time: 'Part-time', weekends: 'Weekends only' }[h.availability_type] || h.availability_type : null} />
                                  <DetailField label="General Liability" value={h.has_general_liability === true ? '✓ Yes' : h.has_general_liability === false ? 'No' : null} />
                                  <DetailField label="Other Insurance" value={h.has_other_insurance === true ? '✓ Yes' : h.has_other_insurance === false ? 'No' : null} />
                                  <DetailField label="Service ZIPs" value={(h.service_zips || []).filter(Boolean).join(', ') || null} />
                                </div>
                                {/* Market assignment */}
                                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 8 }}>Market Assignment</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <select
                                      value={inlineMarketDraft}
                                      onChange={function(e) { setInlineMarketDraft(e.target.value); setInlineMarketMsg(''); }}
                                      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, background: '#fff', color: '#111', cursor: 'pointer', minWidth: 160 }}
                                    >
                                      <option value="">— None —</option>
                                      {markets.filter(function(m) { return m.market_name !== 'Default' && m.market_name !== 'Unassigned'; }).map(function(m) {
                                        return <option key={m.market_name} value={m.market_name}>{m.market_name}</option>;
                                      })}
                                    </select>
                                    <button
                                      onClick={function() { saveInlineMarket(h.id); }}
                                      disabled={inlineMarketSaving || !inlineMarketDraft || inlineMarketDraft === h.market_name}
                                      style={{ padding: '7px 14px', background: (!inlineMarketSaving && inlineMarketDraft && inlineMarketDraft !== h.market_name) ? '#1A7A4A' : '#e5e7eb', color: (!inlineMarketSaving && inlineMarketDraft && inlineMarketDraft !== h.market_name) ? '#fff' : '#9ca3af', border: 'none', borderRadius: 7, cursor: (!inlineMarketSaving && inlineMarketDraft && inlineMarketDraft !== h.market_name) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}
                                    >
                                      {inlineMarketSaving ? 'Saving…' : 'Update Market'}
                                    </button>
                                    {inlineMarketMsg && (
                                      <span style={{ fontSize: 12, color: inlineMarketMsg === 'Saved' ? '#1A7A4A' : '#dc2626', fontWeight: 600 }}>{inlineMarketMsg}</span>
                                    )}
                                  </div>
                                </div>
                                {/* Document viewer */}
                                {(h.coi_url || h.w9_url) && (
                                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 10 }}>Documents</div>
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                      {h.coi_url && (
                                        <button
                                          disabled={docUrlsLoading[h.id]}
                                          onClick={async function() {
                                            await loadDocUrls(h.id);
                                            const url = docUrls[h.id]?.coi_signed_url;
                                            if (url) window.open(url, '_blank', 'noopener');
                                          }}
                                          style={{ padding: '7px 16px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                                        >
                                          {docUrlsLoading[h.id] ? '...' : '📄 View COI'}
                                        </button>
                                      )}
                                      {h.w9_url && (
                                        <button
                                          disabled={docUrlsLoading[h.id]}
                                          onClick={async function() {
                                            await loadDocUrls(h.id);
                                            const url = docUrls[h.id]?.w9_signed_url;
                                            if (url) window.open(url, '_blank', 'noopener');
                                          }}
                                          style={{ padding: '7px 16px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                                        >
                                          {docUrlsLoading[h.id] ? '...' : '📋 View W-9'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {/* Setup link section */}
                                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 8 }}>Account Setup</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <button
                                      onClick={function() { sendSetupLink(h); }}
                                      disabled={setupLinkLoading === h.id}
                                      style={{ padding: '7px 16px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 6, cursor: setupLinkLoading === h.id ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, opacity: setupLinkLoading === h.id ? 0.7 : 1 }}
                                    >
                                      {setupLinkLoading === h.id ? 'Sending…' : '📧 Resend Welcome Email'}
                                    </button>
                                    {(setupLinkSent[h.id] || h.setup_link_sent_at) && (
                                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                                        Last sent: {new Date(setupLinkSent[h.id] || h.setup_link_sent_at).toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12, marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
                                  <button
                                    onClick={function() {
                                      setEditingHauler(h);
                                      setHaulerDraft({
                                        company_name: h.company_name || '',
                                        ein: h.ein || '',
                                        phone: h.phone || '',
                                        email: h.hauler_email || '',
                                        owner_first_name: h.owner_first_name || '',
                                        owner_last_name: h.owner_last_name || '',
                                        shop_address: h.shop_address || '',
                                        years_in_business: h.years_in_business ?? '',
                                        dumpster_count: h.dumpster_count ?? '',
                                        service_counties: h.service_counties || '',
                                        availability_type: h.availability_type || '',
                                        has_general_liability: h.has_general_liability ?? null,
                                        has_other_insurance: h.has_other_insurance ?? null,
                                        market_name: h.market_name || '',
                                      });
                                      setHaulerEditError(''); setHaulerEditSuccess(false);
                                    }}
                                    style={{ padding: '9px 24px', background: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}
                                  >
                                    ✎ Edit
                                  </button>
                                  {h.status === 'pending' && (
                                    <>
                                      <button onClick={() => approveHauler(h.id)} disabled={actionLoading === h.id} style={{ ...s.btnApprove, padding: '9px 28px', fontSize: 14 }}>
                                        {actionLoading === h.id ? 'Processing...' : '✓ Approve Hauler'}
                                      </button>
                                      <button onClick={() => rejectHauler(h.id)} disabled={actionLoading === h.id} style={{ ...s.btnReject, padding: '9px 28px', fontSize: 14 }}>
                                        {actionLoading === h.id ? 'Processing...' : '✕ Reject'}
                                      </button>
                                    </>
                                  )}
                                  {h.status === 'approved' && (
                                    <button onClick={() => suspendHauler(h.id)} disabled={suspendLoading === h.id} style={{ ...s.btnSuspend, padding: '9px 28px', fontSize: 14 }}>
                                      {suspendLoading === h.id ? 'Suspending...' : 'Suspend Hauler'}
                                    </button>
                                  )}
                                  {h.status === 'suspended' && (
                                    <button onClick={() => unsuspendHauler(h.id)} disabled={suspendLoading === h.id} style={{ ...s.btnApprove, padding: '9px 28px', fontSize: 14 }}>
                                      {suspendLoading === h.id ? 'Reactivating...' : 'Reactivate Hauler'}
                                    </button>
                                  )}
                                  <button onClick={() => removeHauler(h.id, h.company_name)} disabled={removeLoading === h.id} style={{ ...s.btnRemove, padding: '9px 28px', fontSize: 14 }}>
                                    {removeLoading === h.id ? 'Removing...' : 'Permanently Remove'}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}


      {/* ── Clients tab ── */}
      {tab === 'clients' && (
        <>
          {mktReturnCtx && (
            <button
              onClick={function() { setTab('market-portal'); setMarketPortalView('detail'); setSelectedMarketName(mktReturnCtx.marketName); setMktReturnCtx(null); setExpandedClientId(null); }}
              style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '0 0 14px', display: 'block' }}
            >
              ← Back to {mktReturnCtx.marketName} Market
            </button>
          )}
          <div style={s.stats}>
            <StatCard label="Total Clients" value={clients.length} />
            <StatCard label="Pending Review" value={clients.filter(c => c.client_status === 'pending').length} />
            <StatCard label="Approved" value={clients.filter(c => c.client_status === 'approved').length} />
            <StatCard label="Pay Later Enabled" value={clients.filter(c => c.pay_later_approved).length} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button onClick={fetchClients} style={s.refreshBtn}>Refresh</button>
          </div>

          {clientsError && <p style={s.error}>{clientsError}</p>}
          {clientActionError && <p style={{ ...s.error, marginBottom: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 6 }}>{clientActionError}</p>}

          {clientsLoading ? (
            <p style={{ color: '#6b7280' }}>Loading clients...</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Name', 'Company', 'Email', 'Phone', 'Status', 'Card', 'Pay Later', 'Rating', 'Applied', 'Actions'].map(col => (
                      <th key={col} style={s.th}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr><td colSpan={10} style={{ ...s.td, textAlign: 'center', color: '#9ca3af' }}>No clients yet.</td></tr>
                  ) : clients.map(function(c, idx) {
                    const expanded = expandedClientId === c.id;
                    const isPending = c.client_status === 'pending';
                    const prevIsPending = idx > 0 && clients[idx - 1].client_status === 'pending';
                    const showPendingHeader = isPending && idx === 0;
                    const showApprovedHeader = !isPending && (idx === 0 || prevIsPending);
                    const detail = clientDetails[c.id];
                    const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
                    const clientInvoices = invoices.filter(function(i) { return i.guest_email && i.guest_email === c.email; });
                    return (
                      <React.Fragment key={c.id}>
                        {showPendingHeader && (
                          <tr>
                            <td colSpan={10} style={{ padding: '10px 16px 6px', background: '#fffbeb', borderBottom: '1px solid #fcd34d' }}>
                              <span style={{ fontSize: 11, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Pending approval — {pendingClients} account{pendingClients !== 1 ? 's' : ''}
                              </span>
                            </td>
                          </tr>
                        )}
                        {showApprovedHeader && (
                          <tr>
                            <td colSpan={10} style={{ padding: '10px 16px 6px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                              <span style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Approved clients
                              </span>
                            </td>
                          </tr>
                        )}
                        <tr
                          style={{ ...s.row, cursor: 'pointer', background: isPending ? '#fffbeb' : expanded ? '#f9fafb' : undefined }}
                          onClick={function() {
                            const next = expanded ? null : c.id;
                            setExpandedClientId(next);
                            if (next && !clientDetails[next]) fetchClientDetail(next);
                          }}
                        >
                          <td style={{ ...s.td, fontWeight: 600 }}>
                            <span style={{ marginRight: 6, fontSize: 10, color: '#9ca3af' }}>{expanded ? '▼' : '▶'}</span>
                            {fullName}
                          </td>
                          <td style={s.td}>{c.company_name || '—'}</td>
                          <td style={s.td}>{c.email || '—'}</td>
                          <td style={s.td}>{c.phone || '—'}</td>
                          <td style={s.td}><ClientStatusBadge status={c.client_status} /></td>
                          <td style={s.td}>
                            <span style={{ color: c.has_card_on_file ? '#10b981' : '#d1d5db', fontWeight: 600, fontSize: 12 }}>
                              {c.has_card_on_file ? '✓ Yes' : '—'}
                            </span>
                          </td>
                          <td style={s.td}>
                            <span style={{ color: c.pay_later_approved ? '#10b981' : '#d1d5db', fontWeight: 600, fontSize: 12 }}>
                              {c.pay_later_approved ? '✓ Yes' : '—'}
                            </span>
                          </td>
                          <td style={s.td}>
                            {c.avg_rating
                              ? <span style={{ fontWeight: 700, fontSize: 12, color: c.avg_rating >= 4 ? '#10b981' : c.avg_rating >= 3 ? '#f59e0b' : '#DC2626' }}>
                                  ★ {Number(c.avg_rating).toFixed(1)}
                                  {c.total_reviews > 0 && <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400, marginLeft: 3 }}>({c.total_reviews})</span>}
                                </span>
                              : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ ...s.td, color: '#6b7280' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                          <td style={s.td} onClick={function(e) { e.stopPropagation(); }}>
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              {isPending && (
                                <>
                                  <button onClick={function() { approveClient(c.id, [c.first_name, c.last_name].filter(Boolean).join(' ')); }} disabled={clientActionLoading === c.id + '_approve'} style={s.btnApprove}>
                                    {clientActionLoading === c.id + '_approve' ? '...' : '✓'}
                                  </button>
                                  <button onClick={function() { rejectClient(c.id, [c.first_name, c.last_name].filter(Boolean).join(' ')); }} disabled={clientActionLoading === c.id + '_reject'} style={s.btnReject}>
                                    {clientActionLoading === c.id + '_reject' ? '...' : '✕'}
                                  </button>
                                </>
                              )}
                              <button
                                onClick={function() { togglePayLater(c.id, c.pay_later_approved); }}
                                disabled={clientActionLoading === c.id + '_paylater'}
                                style={{ ...s.btnSm, color: c.pay_later_approved ? '#b91c1c' : '#059669', borderColor: c.pay_later_approved ? '#fca5a5' : '#6ee7b7', whiteSpace: 'nowrap' }}
                              >
                                {clientActionLoading === c.id + '_paylater' ? '...' : c.pay_later_approved ? 'Revoke PL' : 'Allow PL'}
                              </button>
                              <button
                                onClick={function() { removeClient(c.id, [c.first_name, c.last_name].filter(Boolean).join(' ')); }}
                                disabled={clientRemoveLoading === c.id}
                                style={s.btnRemove}
                              >
                                {clientRemoveLoading === c.id ? '...' : 'Remove'}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expanded && (
                          <tr>
                            <td colSpan={10} style={{ padding: 0, borderBottom: '2px solid #e5e7eb' }}>
                              <div style={{ ...s.expandPanel, borderLeftColor: isPending ? '#f59e0b' : '#1A7A4A' }}>

                                {/* Profile grid */}
                                <div style={s.expandGrid}>
                                  <DetailField label="Full Name" value={fullName} />
                                  <DetailField label="Company" value={c.company_name} />
                                  <DetailField label="Email" value={c.email} />
                                  <DetailField label="Phone" value={c.phone} />
                                  <DetailField label="Billing Address" value={c.billing_address} />
                                  <DetailField label="Status" value={<ClientStatusBadge status={c.client_status} />} />
                                  <DetailField label="Card on File" value={c.has_card_on_file ? '✓ Yes' : 'No'} />
                                  <DetailField label="Pay Later" value={c.pay_later_approved ? '✓ Approved' : 'Not approved'} />
                                  <DetailField label="Rating" value={
                                    c.avg_rating
                                      ? <span style={{ color: c.avg_rating >= 4 ? '#10b981' : c.avg_rating >= 3 ? '#f59e0b' : '#DC2626', fontWeight: 700 }}>
                                          ★ {Number(c.avg_rating).toFixed(1)} <span style={{ fontWeight: 400, color: '#6b7280' }}>({c.total_reviews} review{c.total_reviews !== 1 ? 's' : ''})</span>
                                        </span>
                                      : <span style={{ color: '#9ca3af' }}>No reviews yet</span>
                                  } />
                                  <DetailField label="Member Since" value={c.created_at ? new Date(c.created_at).toLocaleDateString() : null} />
                                </div>

                                {/* Edit + Approve/Reject + Remove */}
                                <div style={{ display: 'flex', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', alignItems: 'center' }}>
                                  <button
                                    onClick={function() {
                                      setEditingClient(c);
                                      setClientDraft({
                                        first_name: c.first_name || '',
                                        last_name: c.last_name || '',
                                        phone: c.phone || '',
                                        company_name: c.company_name || '',
                                        billing_address: c.billing_address || '',
                                      });
                                      setClientEditError(''); setClientEditSuccess(false);
                                    }}
                                    style={{ padding: '9px 24px', background: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}
                                  >
                                    ✎ Edit
                                  </button>
                                  {isPending && (
                                    <>
                                      <button onClick={function() { approveClient(c.id, [c.first_name, c.last_name].filter(Boolean).join(' ')); }} disabled={clientActionLoading === c.id + '_approve'} style={{ ...s.btnApprove, padding: '9px 28px', fontSize: 14 }}>
                                        {clientActionLoading === c.id + '_approve' ? 'Processing...' : '✓ Approve Client'}
                                      </button>
                                      <button onClick={function() { rejectClient(c.id, [c.first_name, c.last_name].filter(Boolean).join(' ')); }} disabled={clientActionLoading === c.id + '_reject'} style={{ ...s.btnReject, padding: '9px 28px', fontSize: 14 }}>
                                        {clientActionLoading === c.id + '_reject' ? 'Processing...' : '✕ Reject'}
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={function() { removeClient(c.id, [c.first_name, c.last_name].filter(Boolean).join(' ')); }}
                                    disabled={clientRemoveLoading === c.id}
                                    style={{ ...s.btnRemove, padding: '9px 28px', fontSize: 14 }}
                                  >
                                    {clientRemoveLoading === c.id ? 'Removing...' : 'Permanently Remove'}
                                  </button>
                                </div>

                                {/* Pay Later toggle for approved clients */}
                                {c.client_status === 'approved' && (
                                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Invoice Terms (Pay Later):</span>
                                    <button
                                      onClick={function() { togglePayLater(c.id, c.pay_later_approved); }}
                                      disabled={clientActionLoading === c.id + '_paylater'}
                                      style={c.pay_later_approved ? { ...s.btnReject, padding: '7px 18px' } : { ...s.btnApprove, padding: '7px 18px' }}
                                    >
                                      {clientActionLoading === c.id + '_paylater' ? '...' : c.pay_later_approved ? 'Revoke Pay Later' : 'Enable Pay Later'}
                                    </button>
                                    {c.pay_later_approved && (
                                      <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>✓ Client can pay via invoice after delivery</span>
                                    )}
                                  </div>
                                )}

                                {/* Order history */}
                                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 10 }}>Order History</div>
                                  {detail?.loading ? (
                                    <span style={{ fontSize: 13, color: '#6b7280' }}>Loading...</span>
                                  ) : detail?.orders?.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                      <thead>
                                        <tr>
                                          {['Order #', 'Size', 'Delivery Address', 'Date', 'Total', 'Status'].map(function(h) {
                                            return <th key={h} style={{ ...s.th, padding: '7px 10px', fontSize: 11 }}>{h}</th>;
                                          })}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detail.orders.map(function(o) {
                                          return (
                                            <tr key={o.id}>
                                              <td style={{ ...s.td, padding: '7px 10px' }}>
                                                <a href={`/order/${o.id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1A7A4A', fontWeight: 600, textDecoration: 'none' }}>
                                                  {o.order_number}
                                                </a>
                                              </td>
                                              <td style={{ ...s.td, padding: '7px 10px' }}>{o.size || '—'}</td>
                                              <td style={{ ...s.td, padding: '7px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.delivery_address || '—'}</td>
                                              <td style={{ ...s.td, padding: '7px 10px', whiteSpace: 'nowrap' }}>{fmtDate(o.requested_delivery_date)}</td>
                                              <td style={{ ...s.td, padding: '7px 10px', fontWeight: 600 }}>{fmtMoney(o.total_charged)}</td>
                                              <td style={{ ...s.td, padding: '7px 10px' }}><StatusBadge status={o.status} /></td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <span style={{ fontSize: 13, color: '#9ca3af' }}>No orders yet.</span>
                                  )}
                                </div>

                                {/* Invoices */}
                                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 10 }}>Invoices</div>
                                  {clientInvoices.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                      <thead>
                                        <tr>
                                          {['Invoice #', 'Amount', 'Due Date', 'Status', 'Link'].map(function(h) {
                                            return <th key={h} style={{ ...s.th, padding: '7px 10px', fontSize: 11 }}>{h}</th>;
                                          })}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {clientInvoices.map(function(inv) {
                                          const ds = invDisplayStatus(inv);
                                          return (
                                            <tr key={inv.id}>
                                              <td style={{ ...s.td, padding: '7px 10px' }}>
                                                <a href={`/invoice/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 600, textDecoration: 'none' }}>
                                                  {displayNum(inv)}
                                                </a>
                                              </td>
                                              <td style={{ ...s.td, padding: '7px 10px', fontWeight: 600 }}>{fmtMoney(inv.total)}</td>
                                              <td style={{ ...s.td, padding: '7px 10px' }}>{fmtDate(inv.due_date)}</td>
                                              <td style={{ ...s.td, padding: '7px 10px' }}><InvoiceBadge status={ds} /></td>
                                              <td style={{ ...s.td, padding: '7px 10px' }}>
                                                {inv.stripe_payment_link
                                                  ? <a href={inv.stripe_payment_link} target="_blank" rel="noopener noreferrer" style={{ color: '#1A7A4A', fontWeight: 600, fontSize: 11, textDecoration: 'none' }}>Pay Link ↗</a>
                                                  : <span style={{ color: '#d1d5db' }}>—</span>}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <span style={{ fontSize: 13, color: '#9ca3af' }}>No invoices for this client email.</span>
                                  )}
                                </div>

                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Markets tab ── */}
      {tab === 'markets' && (
        <div style={{ maxWidth: 860 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: '#111' }}>Market Fee Settings</h2>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Fee % is the Tambo markup applied to hauler rates at quote time. Changes apply to new quotes immediately — existing orders are unaffected.
              </p>
            </div>
            <button onClick={fetchMarkets} style={s.refreshBtn} disabled={marketsLoading}>Refresh</button>
          </div>

          {marketsError && (
            <div style={{ color: '#ef4444', fontSize: 13, background: '#fef2f2', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>{marketsError}</div>
          )}

          {marketsLoading ? (
            <p style={{ color: '#6b7280' }}>Loading markets...</p>
          ) : markets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🗺</div>
              <div style={{ fontWeight: 700, color: '#374151', marginBottom: 6 }}>No markets configured</div>
              <div style={{ fontSize: 13 }}>Run the SQL migration in Supabase to create the market_settings table.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
              {markets.map(function(m) {
                const draft = marketDrafts[m.id] || {};
                const isSaving = marketSaving === m.id;
                const saved = marketSaveSuccess === m.id;
                const isDefault = m.market_name === 'Default';
                return (
                  <div key={m.id} style={{ background: '#fff', border: `2px solid ${isDefault ? '#e5e7eb' : '#d1fae5'}`, borderRadius: 10, padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{m.market_name}</div>
                        {m.updated_at && (
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                            Updated {new Date(m.updated_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: draft.is_active ? '#1A7A4A' : '#9ca3af' }}>
                        <input
                          type="checkbox"
                          checked={!!draft.is_active}
                          onChange={function(e) {
                            setMarketDrafts(function(p) { return { ...p, [m.id]: { ...p[m.id], is_active: e.target.checked } }; });
                          }}
                          style={{ width: 15, height: 15, accentColor: '#1A7A4A' }}
                        />
                        {draft.is_active ? 'Active' : 'Inactive'}
                      </label>
                    </div>

                    {/* Fee % — the main control, large and prominent */}
                    <div style={{ background: '#f9fafb', borderRadius: 8, padding: '14px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                          Tambo Fee %
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={draft.fee_percent ?? ''}
                            onChange={function(e) {
                              setMarketDrafts(function(p) { return { ...p, [m.id]: { ...p[m.id], fee_percent: e.target.value } }; });
                            }}
                            style={{ width: 80, padding: '8px 10px', borderRadius: 6, border: '2px solid #1A7A4A', fontSize: 22, fontWeight: 800, color: '#1A7A4A', textAlign: 'center', outline: 'none' }}
                          />
                          <span style={{ fontSize: 22, fontWeight: 800, color: '#1A7A4A' }}>%</span>
                          <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 4 }}>
                            = ×{draft.fee_percent ? (1 + parseFloat(draft.fee_percent) / 100).toFixed(2) : '—'} multiplier
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Environmental fee — flat dollar amount added to each order */}
                    <div style={{ background: '#f9fafb', borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                        Environmental Fee ($)
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={draft.environmental_fee ?? 0}
                          onChange={function(e) {
                            setMarketDrafts(function(p) { return { ...p, [m.id]: { ...p[m.id], environmental_fee: e.target.value } }; });
                          }}
                          style={{ width: 80, padding: '8px 10px', borderRadius: 6, border: '1.5px solid #d1d5db', fontSize: 16, fontWeight: 700, color: '#374151', textAlign: 'center', outline: 'none' }}
                        />
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>flat fee added per order (0 = disabled)</span>
                      </div>
                    </div>

                    {/* ZIP prefixes and counties */}
                    {!isDefault && (
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>ZIP Prefixes (comma-separated)</label>
                          <input
                            style={{ ...s.input, width: '100%', boxSizing: 'border-box', fontSize: 12 }}
                            placeholder="785,786"
                            value={draft.zip_prefixes || ''}
                            onChange={function(e) {
                              setMarketDrafts(function(p) { return { ...p, [m.id]: { ...p[m.id], zip_prefixes: e.target.value } }; });
                            }}
                          />
                          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>First 3 digits of ZIP codes in this market</div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Counties (comma-separated)</label>
                          <input
                            style={{ ...s.input, width: '100%', boxSizing: 'border-box', fontSize: 12 }}
                            placeholder="Cameron,Hidalgo,Willacy"
                            value={draft.counties || ''}
                            onChange={function(e) {
                              setMarketDrafts(function(p) { return { ...p, [m.id]: { ...p[m.id], counties: e.target.value } }; });
                            }}
                          />
                        </div>
                      </>
                    )}
                    {isDefault && (
                      <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14, fontStyle: 'italic' }}>
                        Fallback fee applied to any ZIP not matched by another market.
                      </div>
                    )}

                    {/* Save */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button
                        onClick={function() { saveMarket(m.id); }}
                        disabled={isSaving}
                        style={{ ...s.btnInvSend, padding: '8px 22px', fontSize: 13, opacity: isSaving ? 0.7 : 1 }}
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      {saved && <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>✓ Saved</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* SQL hint */}
          <div style={{ marginTop: 28, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px', fontSize: 12, color: '#6b7280' }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: '#374151' }}>Run this SQL in Supabase to initialise the markets table:</div>
            <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{
`CREATE TABLE IF NOT EXISTS market_settings (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  market_name TEXT NOT NULL UNIQUE,
  fee_percent NUMERIC(5,2) NOT NULL DEFAULT 20.00,
  environmental_fee NUMERIC(10,2) DEFAULT 0,
  zip_prefixes TEXT,
  counties TEXT,
  is_active BOOLEAN DEFAULT true,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

INSERT INTO market_settings (market_name, fee_percent, zip_prefixes, counties) VALUES
  ('Default', 20.00, '', ''),
  ('RGV (Rio Grande Valley)', 20.00, '785,786', 'Cameron,Hidalgo,Willacy,Starr'),
  ('San Antonio', 20.00, '782,781', 'Bexar'),
  ('Houston', 20.00, '770,771,772', 'Harris'),
  ('Dallas', 20.00, '752,753', 'Dallas')
ON CONFLICT (market_name) DO NOTHING;`
            }</pre>
          </div>
        </div>
      )}

      {/* ── Processes tab ── */}
      {tab === 'processes' && (
        <div>
          <div style={{display:'flex',alignItems:'center',gap:14,padding:'16px 0 20px',flexWrap:'wrap'}}>
            <button
              onClick={sendCheckIn}
              disabled={checkInLoading}
              style={{background:'#1A7A4A',color:'white',border:'none',borderRadius:8,padding:'9px 18px',fontSize:13,fontWeight:700,cursor:checkInLoading?'not-allowed':'pointer',opacity:checkInLoading?0.7:1}}
            >
              {checkInLoading ? 'Sending...' : '📲 Send Availability Check-In'}
            </button>
            {checkInResult && (
              <span style={{fontSize:13,fontWeight:600,color:checkInResult.startsWith('✅')?'#1A7A4A':'#DC2626'}}>
                {checkInResult}
              </span>
            )}
            <button
              onClick={sendDeliveryReminders}
              disabled={reminderLoading}
              style={{background:'#1A7A4A',color:'white',border:'none',borderRadius:8,padding:'9px 18px',fontSize:13,fontWeight:700,cursor:reminderLoading?'not-allowed':'pointer',opacity:reminderLoading?0.7:1}}
            >
              {reminderLoading ? 'Sending...' : '🚛 Send Delivery Reminders'}
            </button>
            {reminderResult && (
              <span style={{fontSize:13,fontWeight:600,color:reminderResult.startsWith('✅')?'#1A7A4A':'#DC2626'}}>
                {reminderResult}
              </span>
            )}
          </div>
          <ProcessesTab />
        </div>
      )}

      {/* ── Requests tab ── */}
      {tab === 'requests' && (
        <div style={{maxWidth: 760}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <div>
              <h2 style={{margin:'0 0 4px',fontSize:20,fontWeight:800,color:'#111'}}>Pending Requests</h2>
              <p style={{margin:0,fontSize:13,color:'#6b7280'}}>
                {requestsLoading ? 'Loading...' : `${requests.length} pending review`}
              </p>
            </div>
            <button onClick={fetchRequests} style={s.refreshBtn} disabled={requestsLoading}>Refresh</button>
          </div>

          {requestsError && <div style={{color:'#ef4444',marginBottom:14,fontSize:13,background:'#fef2f2',padding:'10px 14px',borderRadius:8}}>{requestsError}</div>}

          {requestsLoading && (
            <div style={{textAlign:'center',padding:'60px 0',color:'#9ca3af',fontSize:14}}>Loading requests...</div>
          )}

          {!requestsLoading && requests.length === 0 && !requestsError && (
            <div style={{textAlign:'center',padding:'70px 0',color:'#9ca3af'}}>
              <div style={{fontSize:36,marginBottom:12}}>✅</div>
              <div style={{fontSize:16,fontWeight:700,color:'#374151',marginBottom:6}}>No pending requests</div>
              <div style={{fontSize:13}}>All cancel and reschedule requests have been handled.</div>
            </div>
          )}

          {requests.map(function(req) {
            const isCancel    = req.request_type === 'cancel';
            const orderStatus = req.order_status || '';
            const isDelivered = ['delivered', 'pickup_requested', 'completed'].includes(orderStatus);
            const approvingThis = requestActionId === req.id + '_approve';
            const denyingThis   = requestActionId === req.id + '_deny';
            const busy          = !!requestActionId;
            const succeeded     = requestSuccessId === req.id + '_approve';

            const fmtTime = function(t) {
              if (!t) return null;
              const [hh, mm] = t.slice(0, 5).split(':');
              const h = parseInt(hh, 10);
              return (h % 12 || 12) + (mm !== '00' ? ':' + mm : '') + (h < 12 ? 'am' : 'pm');
            };
            const windowStr = req.proposed_window_start
              ? [fmtTime(req.proposed_window_start), fmtTime(req.proposed_window_end)].filter(Boolean).join(' – ')
              : null;

            const statusColor =
              orderStatus === 'pending'  ? { bg: '#f3f4f6', text: '#6b7280' } :
              orderStatus === 'accepted' ? { bg: '#f0fdf4', text: '#15803d' } :
              isDelivered                ? { bg: '#fff7ed', text: '#c2410c' } :
                                           { bg: '#f3f4f6', text: '#6b7280' };

            return (
              <div key={req.id} style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:10,padding:'20px 24px',marginBottom:14,boxShadow:'0 1px 4px rgba(0,0,0,0.05)'}}>
                {/* Header */}
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,marginBottom:14,flexWrap:'wrap'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                    <span style={{
                      fontSize:11,fontWeight:800,padding:'3px 10px',borderRadius:20,letterSpacing:'0.04em',
                      background: isCancel ? '#fef2f2' : '#eff6ff',
                      color:      isCancel ? '#dc2626' : '#1d4ed8',
                      border:    `1px solid ${isCancel ? '#fca5a5' : '#93c5fd'}`,
                    }}>
                      {isCancel ? 'CANCEL REQUEST' : 'RESCHEDULE REQUEST'}
                    </span>
                    <span style={{fontSize:15,fontWeight:800,color:'#111'}}>#{req.order_number}</span>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <span style={{fontSize:11,fontWeight:700,padding:'2px 10px',borderRadius:20,background:statusColor.bg,color:statusColor.text}}>
                      {orderStatus}
                    </span>
                    <div style={{fontSize:11,color:'#9ca3af',marginTop:4}}>Requested {fmtDate(req.created_at)}</div>
                  </div>
                </div>

                {/* Details grid */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 24px',marginBottom:14,fontSize:13}}>
                  <div>
                    <span style={{color:'#9ca3af',fontWeight:600}}>Client: </span>
                    <span style={{color:'#111',fontWeight:600}}>{req.guest_name || req.client_email || '—'}</span>
                  </div>
                  {req.reason && (
                    <div>
                      <span style={{color:'#9ca3af',fontWeight:600}}>Reason: </span>
                      <span style={{color:'#374151'}}>{req.reason}</span>
                    </div>
                  )}
                  {!isCancel && req.proposed_date && (
                    <div>
                      <span style={{color:'#9ca3af',fontWeight:600}}>Proposed date: </span>
                      <span style={{color:'#111',fontWeight:700}}>{fmtDate(req.proposed_date)}</span>
                    </div>
                  )}
                  {!isCancel && windowStr && (
                    <div>
                      <span style={{color:'#9ca3af',fontWeight:600}}>Time window: </span>
                      <span style={{color:'#374151'}}>{windowStr}</span>
                    </div>
                  )}
                  <div>
                    <span style={{color:'#9ca3af',fontWeight:600}}>Dumpster: </span>
                    <span style={{color:'#374151'}}>{req.size || '—'}</span>
                  </div>
                  <div>
                    <span style={{color:'#9ca3af',fontWeight:600}}>Delivery address: </span>
                    <span style={{color:'#374151'}}>{req.delivery_address || '—'}</span>
                  </div>
                </div>

                {/* Cancel fee notice */}
                {isCancel && (
                  <div style={{
                    fontSize:12,fontWeight:600,padding:'8px 13px',borderRadius:8,marginBottom:14,
                    background: isDelivered ? '#fff7ed' : '#f0fdf4',
                    color:      isDelivered ? '#c2410c' : '#15803d',
                    border:    `1px solid ${isDelivered ? '#fed7aa' : '#bbf7d0'}`,
                  }}>
                    {isDelivered
                      ? '⚠ Dumpster already delivered — cancellation may incur a pickup fee. Confirm before approving.'
                      : '✓ Not yet delivered — cancellation is free of charge.'}
                  </div>
                )}

                {/* Success flash */}
                {succeeded && (
                  <div style={{fontSize:12,fontWeight:700,color:'#15803d',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:'8px 13px',marginBottom:14}}>
                    ✓ Approved — SMS sent to client{req.hauler_id ? ' and hauler' : ''}.
                  </div>
                )}

                {/* Actions */}
                <div style={{display:'flex',gap:10}}>
                  <button onClick={function(){ approveRequest(req.id); }} disabled={busy}
                    style={{...s.btnApprove, opacity: approvingThis ? 0.7 : 1}}>
                    {approvingThis ? 'Approving...' : '✓ Approve'}
                  </button>
                  <button onClick={function(){ denyRequest(req.id); }} disabled={busy}
                    style={{...s.btnReject, opacity: denyingThis ? 0.7 : 1}}>
                    {denyingThis ? 'Denying...' : '✗ Deny'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Reassign tab ── */}
      {tab === 'reassign' && (
        <div style={{ maxWidth: 820 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: '#111' }}>Needs Reassignment</h2>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                {reassignQueueLoading ? 'Loading...' : `${reassignQueue.length} order${reassignQueue.length !== 1 ? 's' : ''} awaiting reassignment`}
              </p>
            </div>
            <button onClick={fetchReassignQueue} style={s.refreshBtn} disabled={reassignQueueLoading}>Refresh</button>
          </div>

          {reassignQueueError && (
            <div style={{ color: '#ef4444', marginBottom: 14, fontSize: 13, background: '#fef2f2', padding: '10px 14px', borderRadius: 8 }}>
              {reassignQueueError}
            </div>
          )}

          {reassignQueueLoading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 14 }}>Loading reassignment queue...</div>
          )}

          {!reassignQueueLoading && reassignQueue.length === 0 && !reassignQueueError && (
            <div style={{ textAlign: 'center', padding: '70px 0', color: '#9ca3af' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 6 }}>No orders need reassignment</div>
              <div style={{ fontSize: 13 }}>All declined orders have been handled.</div>
            </div>
          )}

          {reassignQueue.map(function(order) {
            const haulers = reassignQueueHaulers[order.id];
            const haulersLoading = reassignQueueHaulersLoading[order.id];
            const selectedId = reassignQueueSelected[order.id] || '';
            const isAssigning = reassignQueueAssigning === order.id;
            const msg = reassignQueueMsg[order.id] || '';
            const eligibleHaulers = Array.isArray(haulers) ? haulers.filter(function(h) { return !h.already_rejected; }) : [];

            const fmtDate = function(d) {
              if (!d) return 'TBD';
              return new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            };
            const fmtTime = function(t) {
              if (!t) return null;
              const [hh, mm] = t.slice(0, 5).split(':');
              const h = parseInt(hh, 10);
              return (h % 12 || 12) + (mm !== '00' ? ':' + mm : '') + (h < 12 ? 'am' : 'pm');
            };

            const declinedNames = Array.isArray(order.declined_haulers)
              ? order.declined_haulers.map(function(h) { return h.company_name; }).join(', ')
              : '—';

            return (
              <div key={order.id} style={{ background: '#fff', border: '1px solid #fca5a5', borderRadius: 10, padding: '20px 24px', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#111' }}>#{order.order_number}</span>
                  <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 6, fontSize: 12, fontWeight: 700, padding: '2px 8px' }}>
                    {order.size.replace('yd', '-Yard')}
                  </span>
                  {order.market_name && (
                    <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, fontSize: 12, fontWeight: 600, padding: '2px 8px' }}>
                      {order.market_name}
                    </span>
                  )}
                  <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 6, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>
                    NEEDS REASSIGNMENT
                  </span>
                </div>

                {/* Details */}
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>Address:</span> {order.delivery_address}
                </div>
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>Deliver:</span> {fmtDate(order.requested_delivery_date)}
                  {order.delivery_window_start && (
                    <span style={{ color: '#6b7280' }}> · {fmtTime(order.delivery_window_start)}{order.delivery_window_end ? ' – ' + fmtTime(order.delivery_window_end) : ''}</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                  <span style={{ fontWeight: 600, color: '#dc2626' }}>Declined by:</span> {declinedNames}
                </div>

                {/* Hauler assignment */}
                {!haulers && !haulersLoading && (
                  <button
                    onClick={function() { loadHaulersForReassign(order.id); }}
                    style={{ padding: '7px 14px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Find Available Haulers
                  </button>
                )}

                {haulersLoading && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Loading haulers...</div>
                )}

                {haulers && !haulersLoading && eligibleHaulers.length === 0 && (
                  <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 600, background: '#fef2f2', padding: '8px 12px', borderRadius: 7 }}>
                    No eligible haulers available for this size/area.
                  </div>
                )}

                {haulers && !haulersLoading && eligibleHaulers.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={selectedId}
                      onChange={function(e) { setReassignQueueSelected(function(prev) { return { ...prev, [order.id]: e.target.value }; }); }}
                      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, background: '#fff', color: '#111', cursor: 'pointer', minWidth: 220 }}
                    >
                      <option value="">Select a hauler...</option>
                      {eligibleHaulers.map(function(h) {
                        return (
                          <option key={h.id} value={h.id}>
                            {h.company_name} — ${(h.hauler_rate || 0).toFixed(2)}{h.distance_miles ? ` (${h.distance_miles} mi)` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      onClick={function() { assignFromReassignQueue(order.id, selectedId); }}
                      disabled={!selectedId || isAssigning}
                      style={{
                        padding: '7px 16px',
                        background: selectedId && !isAssigning ? '#1A7A4A' : '#e5e7eb',
                        color: selectedId && !isAssigning ? '#fff' : '#9ca3af',
                        border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700,
                        cursor: selectedId && !isAssigning ? 'pointer' : 'not-allowed',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isAssigning ? 'Assigning...' : 'Assign & SMS'}
                    </button>
                  </div>
                )}

                {msg && (
                  <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: msg.startsWith('Error') || msg.startsWith('Network') ? '#dc2626' : '#15803d' }}>
                    {msg}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Edit Hauler modal ── */}
      {editingHauler && (
        <div style={s.modalOverlay} onClick={function(e) { if (e.target === e.currentTarget) setEditingHauler(null); }}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Edit Hauler — {editingHauler.company_name || editingHauler.id}</h3>
              <button onClick={function() { setEditingHauler(null); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1 }}>×</button>
            </div>

            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Business Info</div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.label}>Company Name</label>
                  <input style={s.input} value={haulerDraft.company_name || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, company_name: e.target.value }; }); }} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>EIN</label>
                  <input style={s.input} value={haulerDraft.ein || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, ein: e.target.value }; }); }} />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.label}>Owner First Name</label>
                  <input style={s.input} value={haulerDraft.owner_first_name || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, owner_first_name: e.target.value }; }); }} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Owner Last Name</label>
                  <input style={s.input} value={haulerDraft.owner_last_name || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, owner_last_name: e.target.value }; }); }} />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.label}>Phone</label>
                  <input style={s.input} value={haulerDraft.phone || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, phone: e.target.value }; }); }} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Contact Email</label>
                  <input style={s.input} type="email" value={haulerDraft.email || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, email: e.target.value }; }); }} />
                </div>
              </div>
            </div>

            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Location</div>
              <div style={s.formRow}>
                <div style={{ ...s.formField, flex: 2 }}>
                  <label style={s.label}>Shop / Yard Address</label>
                  <input style={s.input} value={haulerDraft.shop_address || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, shop_address: e.target.value }; }); }} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Years in Business</label>
                  <input style={s.input} type="number" min="0" value={haulerDraft.years_in_business ?? ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, years_in_business: e.target.value }; }); }} />
                </div>
              </div>
            </div>

            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Fleet & Operations</div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.label}>Dumpster Count</label>
                  <input style={s.input} type="number" min="0" value={haulerDraft.dumpster_count ?? ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, dumpster_count: e.target.value }; }); }} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Counties Served</label>
                  <input style={s.input} value={haulerDraft.service_counties || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, service_counties: e.target.value }; }); }} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Availability</label>
                  <select style={s.input} value={haulerDraft.availability_type || ''} onChange={function(e) { setHaulerDraft(function(p) { return { ...p, availability_type: e.target.value }; }); }}>
                    <option value="">—</option>
                    <option value="full_time">Full-time</option>
                    <option value="part_time">Part-time</option>
                    <option value="weekends">Weekends only</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Market Assignment</div>
              <div style={s.formRow}>
                <div style={{ ...s.formField, flex: 1 }}>
                  <label style={s.label}>Market</label>
                  <select
                    style={s.input}
                    value={haulerDraft.market_name || ''}
                    onChange={function(e) { setHaulerDraft(function(p) { return { ...p, market_name: e.target.value }; }); }}
                  >
                    <option value="">— None —</option>
                    {markets.filter(function(m) { return m.market_name !== 'Default' && m.market_name !== 'Unassigned'; }).map(function(m) {
                      return <option key={m.market_name} value={m.market_name}>{m.market_name}</option>;
                    })}
                  </select>
                </div>
              </div>
            </div>

            <div style={{ ...s.formSection }}>
              <div style={s.formSectionLabel}>Insurance</div>
              <div style={s.formRow}>
                {[
                  { key: 'has_general_liability', label: 'General Liability' },
                  { key: 'has_other_insurance', label: 'Other Insurance' },
                ].map(function(ins) {
                  return (
                    <div key={ins.key} style={s.formField}>
                      <label style={s.label}>{ins.label}</label>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        {[true, false].map(function(val) {
                          const active = haulerDraft[ins.key] === val;
                          return (
                            <button
                              key={String(val)}
                              onClick={function() { setHaulerDraft(function(p) { return { ...p, [ins.key]: val }; }); }}
                              style={{ flex: 1, padding: '7px', borderRadius: 6, border: '1.5px solid', borderColor: active ? (val ? '#1A7A4A' : '#ef4444') : '#e0e0da', background: active ? (val ? '#e8f5ee' : '#fef2f2') : 'white', color: active ? (val ? '#1A7A4A' : '#ef4444') : '#6b7280', fontWeight: active ? 700 : 500, cursor: 'pointer', fontSize: 13 }}
                            >
                              {val ? 'Yes' : 'No'}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {haulerEditError && <p style={{ color: '#ef4444', fontSize: 13, background: '#fef2f2', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>{haulerEditError}</p>}
            {haulerEditSuccess && <p style={{ color: '#10b981', fontWeight: 600, fontSize: 13, background: '#f0fdf4', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>✓ Hauler updated successfully</p>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={function() { setEditingHauler(null); setHaulerEditSuccess(false); }} style={s.refreshBtn}>Cancel</button>
              <button onClick={saveHaulerEdit} disabled={haulerEditSaving} style={s.btnInvSend}>
                {haulerEditSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Client modal ── */}
      {editingClient && (
        <div style={s.modalOverlay} onClick={function(e) { if (e.target === e.currentTarget) setEditingClient(null); }}>
          <div style={{ ...s.modalBox, maxWidth: 520 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Edit Client — {[editingClient.first_name, editingClient.last_name].filter(Boolean).join(' ') || editingClient.email}</h3>
              <button onClick={function() { setEditingClient(null); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1 }}>×</button>
            </div>

            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Personal Info</div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.label}>First Name</label>
                  <input style={s.input} value={clientDraft.first_name || ''} onChange={function(e) { setClientDraft(function(p) { return { ...p, first_name: e.target.value }; }); }} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Last Name</label>
                  <input style={s.input} value={clientDraft.last_name || ''} onChange={function(e) { setClientDraft(function(p) { return { ...p, last_name: e.target.value }; }); }} />
                </div>
              </div>
              <div style={s.formField}>
                <label style={s.label}>Phone</label>
                <input style={s.input} value={clientDraft.phone || ''} onChange={function(e) { setClientDraft(function(p) { return { ...p, phone: e.target.value }; }); }} />
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: -8, marginBottom: 8 }}>Login email ({editingClient.email}) can only be changed via Supabase Auth.</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={s.formSectionLabel}>Company & Billing</div>
              <div style={s.formField}>
                <label style={s.label}>Company Name</label>
                <input style={s.input} value={clientDraft.company_name || ''} onChange={function(e) { setClientDraft(function(p) { return { ...p, company_name: e.target.value }; }); }} />
              </div>
              <div style={{ ...s.formField, marginTop: 10 }}>
                <label style={s.label}>Billing Address</label>
                <input style={s.input} value={clientDraft.billing_address || ''} onChange={function(e) { setClientDraft(function(p) { return { ...p, billing_address: e.target.value }; }); }} />
              </div>
            </div>

            {clientEditError && <p style={{ color: '#ef4444', fontSize: 13, background: '#fef2f2', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>{clientEditError}</p>}
            {clientEditSuccess && <p style={{ color: '#10b981', fontWeight: 600, fontSize: 13, background: '#f0fdf4', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>✓ Client updated successfully</p>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={function() { setEditingClient(null); setClientEditSuccess(false); }} style={s.refreshBtn}>Cancel</button>
              <button onClick={saveClientEdit} disabled={clientEditSaving} style={s.btnInvSend}>
                {clientEditSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Hauler modal ── */}
      {showAddHauler && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
          onClick={function(e) { if (e.target === e.currentTarget) { setShowAddHauler(false); } }}
        >
          <div style={{ background: '#fff', borderRadius: 14, padding: '32px 28px', width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            {addHaulerResult ? (
              <div>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ fontSize: 44, marginBottom: 10 }}>🎉</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 6 }}>Hauler Added!</div>
                  <div style={{ fontSize: 14, color: '#6b7280' }}><strong style={{ color: '#1A7A4A' }}>{addHaulerForm.first_name} {addHaulerForm.last_name}</strong> is now live on Fast Tambo.</div>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#166534', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Login Credentials — Save these now</div>
                  <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.8 }}>
                    <div>Email: <strong>{addHaulerForm.email}</strong></div>
                    <div>Temp Password: <strong style={{ fontFamily: 'monospace', background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>{addHaulerResult?.tempPassword}</strong></div>
                  </div>
                  {addHaulerForm.phone && <div style={{ marginTop: 8, fontSize: 12, color: '#166534' }}>✅ Credentials SMS sent to {addHaulerForm.phone}</div>}
                </div>
                <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                  A welcome email with these credentials has been sent to the hauler. Use <strong>Resend Welcome Email</strong> in the hauler's detail panel to send a fresh password at any time.
                </div>
                <button onClick={function() { setShowAddHauler(false); setAddHaulerResult(null); setAddHaulerForm(emptyHaulerForm); }} style={{ width: '100%', padding: '11px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Done</button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111' }}>Add Hauler</h3>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>Creates their account and sends a welcome SMS</p>
                  </div>
                  <button onClick={function() { setShowAddHauler(false); }} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 4px' }}>✕</button>
                </div>
                {[
                  { label: 'First Name *', field: 'first_name', placeholder: 'John' },
                  { label: 'Last Name', field: 'last_name', placeholder: 'Doe' },
                  { label: 'Email *', field: 'email', placeholder: 'hauler@example.com', type: 'email' },
                  { label: 'Phone', field: 'phone', placeholder: '(956) 555-0100' },
                  { label: 'Business Name', field: 'company_name', placeholder: 'Acme Hauling LLC' },
                ].map(function(f) {
                  return (
                    <div key={f.field} style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.label}</label>
                      <input
                        type={f.type || 'text'}
                        style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                        placeholder={f.placeholder}
                        value={addHaulerForm[f.field]}
                        onChange={function(e) { var v = e.target.value; setAddHaulerForm(function(p) { return { ...p, [f.field]: v }; }); }}
                      />
                    </div>
                  );
                })}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Market</label>
                  <select value={addHaulerForm.market_name} onChange={function(e) { var v = e.target.value; setAddHaulerForm(function(p) { return { ...p, market_name: v }; }); }} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13 }}>
                    <option value="">— Select Market —</option>
                    {markets.filter(function(m) { return m.market_name !== 'Default' && m.market_name !== 'Unassigned'; }).map(function(m) {
                      return <option key={m.market_name} value={m.market_name}>{m.market_name}</option>;
                    })}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dumpster Sizes Offered</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {HAULER_SIZES.map(function(sz) {
                      var checked = addHaulerForm.sizes.includes(sz);
                      return (
                        <label key={sz} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, border: '1px solid ' + (checked ? '#1A7A4A' : '#e5e7eb'), background: checked ? '#f0fdf4' : '#fff', fontSize: 13, fontWeight: checked ? 700 : 400, color: checked ? '#166534' : '#374151' }}>
                          <input type="checkbox" checked={checked} onChange={function() {
                            setAddHaulerForm(function(p) {
                              var next = checked ? p.sizes.filter(function(s) { return s !== sz; }) : [...p.sizes, sz];
                              return { ...p, sizes: next };
                            });
                          }} style={{ display: 'none' }} />
                          {sz === '7yd'
                            ? <span>{sz} <span style={{ fontSize: 10, fontWeight: 600, color: '#92400e', textTransform: 'none', letterSpacing: 0 }}>(concrete)</span></span>
                            : sz}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes (internal)</label>
                  <textarea rows={2} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} placeholder="Optional internal notes…" value={addHaulerForm.notes} onChange={function(e) { var v = e.target.value; setAddHaulerForm(function(p) { return { ...p, notes: v }; }); }} />
                </div>
                {addHaulerError && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{addHaulerError}</div>}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={function() { setShowAddHauler(false); }} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
                  <button onClick={submitAddHauler} disabled={addHaulerLoading} style={{ flex: 2, padding: '10px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, cursor: addHaulerLoading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: addHaulerLoading ? 0.7 : 1 }}>
                    {addHaulerLoading ? 'Creating…' : 'Create Hauler'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── New Invoice modal ── */}
      {showNewInv && (
        <div style={s.modalOverlay} onClick={e => { if (e.target === e.currentTarget) setShowNewInv(false); }}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New Invoice</h3>
              <button onClick={() => setShowNewInv(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1 }}>×</button>
            </div>

            {/* Address lookup + dumpster availability */}
            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Address Lookup</div>
              <label style={s.label}>Service Address</label>
              <input
                ref={invAddrInputRef}
                style={{ ...s.input, width: '100%', boxSizing: 'border-box', marginBottom: 10 }}
                placeholder="Start typing a service address..."
                type="text"
              />
              {invAvailLoading && (
                <div style={{ fontSize: 12, color: '#6b7280', padding: '6px 0' }}>Searching for available dumpsters...</div>
              )}
              {invAvailError && (
                <div style={{ fontSize: 12, color: '#ef4444', padding: '4px 0' }}>{invAvailError}</div>
              )}
              {invAvailable !== null && !invAvailLoading && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: invAvailable.length === 0 ? '#ef4444' : '#6b7280', marginBottom: 8 }}>
                    {invAvailable.length === 0 ? 'No dumpsters available in this area' : `${invAvailable.length} option${invAvailable.length !== 1 ? 's' : ''} available nearby`}
                  </div>
                  {invAvailable.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Duration:</span>
                        {DURATION_OPTS.map(function(d) {
                          const active = invDuration === d.key;
                          return (
                            <button
                              key={d.key}
                              onClick={function() { setInvDuration(d.key); }}
                              style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid', borderColor: active ? '#1A7A4A' : '#d1d5db', background: active ? '#1A7A4A' : '#fff', color: active ? '#fff' : '#374151', cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 500 }}
                            >
                              {d.label}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {invAvailable.map(function(result) {
                          const durOpt = DURATION_OPTS.find(function(d) { return d.key === invDuration; }) || DURATION_OPTS[0];
                          const price = result[durOpt.priceField] || result.price_3day;
                          const isSelected = invSelectedHauler && invSelectedHauler.hauler_id === result.hauler_id;
                          return (
                            <div key={(result.hauler_id || '') + '_' + result.size} style={{ border: isSelected ? '2px solid #1A7A4A' : '1px solid #d1fae5', borderRadius: 8, padding: '12px 14px', background: isSelected ? '#ecfdf5' : '#f0fdf4', minWidth: 130, flex: '1 1 130px' }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 2 }}>{result.label}</div>
                              {result.hauler_name && <div style={{ fontSize: 11, color: '#374151', fontWeight: 600, marginBottom: 3 }}>{result.hauler_name}</div>}
                              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, lineHeight: 1.4 }}>{result.desc}</div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#1A7A4A', marginBottom: 1 }}>${price} / {durOpt.label}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>{result.quantity} avail{result.distance_miles != null ? ` · ${result.distance_miles} mi` : ''}</div>
                              <button
                                onClick={function() { addDumpsterToInvoice(result); }}
                                style={{ ...s.btnSmGreen, fontSize: 11, width: '100%', padding: '5px 8px', cursor: 'pointer' }}
                              >
                                {isSelected ? '✓ Selected' : '+ Add to Invoice'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      {invSelectedHauler && (
                        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#1A7A4A', background: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 6, padding: '6px 12px' }}>
                          <span>Dispatching to: <strong>{invSelectedHauler.name}</strong></span>
                          <button onClick={function() { setInvSelectedHauler(null); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>×</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Client info */}
            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Client Info</div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.label}>Name</label>
                  <input style={s.input} placeholder="John Smith" value={newInv.guest_name} onChange={e => setNewInv(p => ({ ...p, guest_name: e.target.value }))} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Email</label>
                  <input style={s.input} type="email" placeholder="client@example.com" value={newInv.guest_email} onChange={e => setNewInv(p => ({ ...p, guest_email: e.target.value }))} />
                  {(newInv.extra_emails || []).map((em, i) => (
                    <div key={i} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <input style={{ ...s.input, flex: 1 }} type="email" placeholder="cc@example.com" value={em} onChange={e => setNewInv(p => { const a = [...p.extra_emails]; a[i] = e.target.value; return { ...p, extra_emails: a }; })} />
                      <button type="button" onClick={() => setNewInv(p => ({ ...p, extra_emails: p.extra_emails.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                    </div>
                  ))}
                  {(newInv.extra_emails || []).length < 5 && (
                    <button type="button" onClick={() => setNewInv(p => ({ ...p, extra_emails: [...(p.extra_emails || []), ''] }))} style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 12, padding: '4px 0 0', fontWeight: 600 }}>+ Add another email</button>
                  )}
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 4 }}>
                    Phone
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 5px' }}>SMS</span>
                  </label>
                  <input style={s.input} type="tel" placeholder="(956) 555-0100" value={newInv.guest_phone} onChange={e => setNewInv(p => ({ ...p, guest_phone: e.target.value }))} />
                  {(newInv.extra_phones || []).map((ph, i) => (
                    <div key={i} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <input style={{ ...s.input, flex: 1 }} type="tel" placeholder="(956) 555-0101" value={ph} onChange={e => setNewInv(p => { const a = [...p.extra_phones]; a[i] = e.target.value; return { ...p, extra_phones: a }; })} />
                      <button type="button" onClick={() => setNewInv(p => ({ ...p, extra_phones: p.extra_phones.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                    </div>
                  ))}
                  {(newInv.extra_phones || []).length < 5 && (
                    <button type="button" onClick={() => setNewInv(p => ({ ...p, extra_phones: [...(p.extra_phones || []), ''] }))} style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 12, padding: '4px 0 0', fontWeight: 600 }}>+ Add another phone</button>
                  )}
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Company</label>
                  <input style={s.input} placeholder="Company name" value={newInv.company_name} onChange={e => setNewInv(p => ({ ...p, company_name: e.target.value }))} />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={{ ...s.formField, flex: 2 }}>
                  <label style={s.label}>Billing Address</label>
                  <input ref={invAddrInputRef} style={s.input} placeholder="123 Main St, City, TX 78550" value={newInv.billing_address} onChange={e => setNewInv(p => ({ ...p, billing_address: e.target.value }))} />
                </div>
                <div style={{ ...s.formField, flex: 2 }}>
                  <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8 }}>
                    Service Address
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 400, color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={newInvSameAsBilling} onChange={e => { setNewInvSameAsBilling(e.target.checked); if (e.target.checked) setNewInv(p => ({ ...p, service_address: '' })); }} style={{ margin: 0 }} />
                      Same as billing
                    </label>
                  </label>
                  {!newInvSameAsBilling && (
                    <input ref={invSvcAddrInputRef} style={s.input} placeholder="123 Service St, City, TX 78550" value={newInv.service_address} onChange={e => setNewInv(p => ({ ...p, service_address: e.target.value }))} />
                  )}
                </div>
              </div>
            </div>

            {/* Line items */}
            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Line Items</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, padding: '6px 8px' }}>Description</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 60, textAlign: 'center' }}>Qty</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 90, textAlign: 'right' }}>Rate</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 90, textAlign: 'right' }}>Amount</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 28 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {newInv.line_items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '3px 3px' }}>
                        <select
                          style={{ ...s.input, padding: '6px 8px', width: '100%', boxSizing: 'border-box' }}
                          value={item.itemType}
                          onChange={e => updateLineItem(idx, 'itemType', e.target.value)}
                        >
                          <option value="">— Select item —</option>
                          {ITEM_OPTIONS.map(o => (
                            <option key={o.label} value={o.label}>{o.label}</option>
                          ))}
                        </select>
                        {item.itemType === 'Custom' && (
                          <input
                            style={{ ...s.input, padding: '6px 8px', width: '100%', boxSizing: 'border-box', marginTop: 4 }}
                            placeholder="Custom description"
                            value={item.description}
                            onChange={e => updateLineItem(idx, 'description', e.target.value)}
                          />
                        )}
                      </td>
                      <td style={{ padding: '3px 3px' }}>
                        <input
                          style={{ ...s.input, padding: '6px 8px', width: '100%', textAlign: 'center', boxSizing: 'border-box' }}
                          type="number" min="1" step="1"
                          value={item.quantity}
                          onChange={e => updateLineItem(idx, 'quantity', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '3px 3px' }}>
                        <input
                          style={{ ...s.input, padding: '6px 8px', width: '100%', textAlign: 'right', boxSizing: 'border-box' }}
                          type="number" min="0" step="0.01" placeholder="0.00"
                          value={item.rate}
                          onChange={e => updateLineItem(idx, 'rate', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: '#111827', fontSize: 13 }}>
                        {fmtMoney(item.amount)}
                      </td>
                      <td style={{ padding: '3px 3px', textAlign: 'center' }}>
                        {newInv.line_items.length > 1 && (
                          <button onClick={() => removeLineItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '2px 4px' }}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addLineItem} style={{ ...s.refreshBtn, fontSize: 12 }}>+ Add Line Item</button>
            </div>

            {/* Market + Due date */}
            <div style={{ ...s.formRow, marginBottom: 12 }}>
              <div style={s.formField}>
                <label style={s.label}>Market</label>
                {invLockedMarket ? (
                  <div style={{ ...s.input, background: '#f9fafb', color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11 }}>🔒</span>
                    <span style={{ fontWeight: 600 }}>{invLockedMarket}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>(auto-assigned)</span>
                  </div>
                ) : (
                  <select
                    style={{ ...s.input, width: '100%' }}
                    value={newInv.market_name}
                    onChange={e => setNewInv(p => ({ ...p, market_name: e.target.value }))}
                  >
                    <option value="Unassigned">Unassigned</option>
                    {markets.filter(function(m) { return m.market_name !== 'Default' && m.market_name !== 'Unassigned'; }).map(function(m) {
                      return <option key={m.market_name} value={m.market_name}>{m.market_name}</option>;
                    })}
                  </select>
                )}
              </div>
              <div style={s.formField}>
                <label style={s.label}>Payment Terms</label>
                <select style={{ ...s.input, width: '100%' }} value={newInv.payment_terms} onChange={e => setNewInv(p => ({ ...p, payment_terms: e.target.value }))}>
                  <option value="receipt">Due on receipt</option>
                  <option value="net7">Net 7</option>
                  <option value="net15">Net 15</option>
                  <option value="net30">Net 30</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 12 }}>
              <label style={s.label}>Notes (optional)</label>
              <input style={{ ...s.input, width: '100%', boxSizing: 'border-box' }} placeholder="Payment terms, special instructions..." value={newInv.notes} onChange={e => setNewInv(p => ({ ...p, notes: e.target.value }))} />
            </div>

            {/* Recurring + Tax */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={newInv.is_recurring}
                  onChange={e => setNewInv(p => ({ ...p, is_recurring: e.target.checked }))}
                  style={{ width: 15, height: 15 }}
                />
                Recurring Invoice
              </label>
              {newInv.is_recurring && (
                <select
                  style={{ ...s.input, width: 'auto', padding: '5px 10px' }}
                  value={newInv.recurring_interval}
                  onChange={e => setNewInv(p => ({ ...p, recurring_interval: e.target.value }))}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151', marginLeft: 'auto' }}>
                <input
                  type="checkbox"
                  checked={newInv.tax_enabled !== false}
                  onChange={e => setNewInv(p => ({ ...p, tax_enabled: e.target.checked }))}
                  style={{ width: 15, height: 15 }}
                />
                Charge sales tax (8.25%)
              </label>
            </div>

            {/* Terms & Conditions */}
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
              <label style={{ ...s.label, marginBottom: 6 }}>Terms &amp; Conditions</label>
              <textarea
                style={{ ...s.input, width: '100%', boxSizing: 'border-box', minHeight: 80, resize: 'vertical', fontFamily: 'inherit', fontSize: 12 }}
                value={newInv.terms_and_conditions}
                onChange={e => setNewInv(p => ({ ...p, terms_and_conditions: e.target.value }))}
              />
            </div>

            {/* Preview totals */}
            <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 16px', marginBottom: 16, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Preview</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: '#6b7280' }}>Subtotal</span><span>{fmtMoney(newSub)}</span>
              </div>
              {newInv.tax_enabled !== false && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#6b7280' }}>Sales Tax (8.25%)</span><span>{fmtMoney(newTax)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, borderTop: '1px solid #e5e7eb', paddingTop: 8, marginTop: 4 }}>
                <span>Total</span>
                <span style={{ color: '#1A7A4A' }}>{fmtMoney(newTotal)}</span>
              </div>
            </div>

            {newInvError && <p style={{ ...s.error, marginBottom: 12 }}>{newInvError}</p>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNewInv(false)} style={s.refreshBtn}>Cancel</button>
              <button onClick={() => createInvoice(false)} disabled={newInvSaving} style={s.btnInvDraft}>
                {newInvSaving ? 'Saving...' : 'Save as Draft'}
              </button>
              <button onClick={() => createInvoice(true)} disabled={newInvSaving} style={s.btnInvSend}>
                {newInvSaving ? 'Sending...' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Invoice modal ── */}
      {editingInv && editDraft && (
        <div style={s.modalOverlay} onClick={e => { if (e.target === e.currentTarget) { setEditingInv(null); setEditDraft(null); } }}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Edit {displayNum(editingInv)}</h3>
              <button onClick={() => { setEditingInv(null); setEditDraft(null); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1 }}>×</button>
            </div>

            {/* Invoice number */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
              <label style={{ ...s.label, marginBottom: 0, whiteSpace: 'nowrap' }}>Invoice #</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
                <span style={{ padding: '6px 10px', background: '#f3f4f6', color: '#6b7280', fontSize: 13, fontWeight: 700, borderRight: '1px solid #d1d5db', userSelect: 'none' }}>TMB-</span>
                <input
                  style={{ border: 'none', outline: 'none', padding: '6px 10px', fontSize: 13, fontWeight: 600, width: 90, background: '#fff' }}
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  placeholder="1817"
                  value={editDraft.invoice_ref_number}
                  onChange={e => setEditDraft(p => ({ ...p, invoice_ref_number: e.target.value.replace(/\D/g, '') }))}
                />
              </div>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>Changing the number does not reset the counter</span>
            </div>

            {/* Client info */}
            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Client Info</div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.label}>Name</label>
                  <input style={s.input} placeholder="John Smith" value={editDraft.guest_name} onChange={e => setEditDraft(p => ({ ...p, guest_name: e.target.value }))} />
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Email</label>
                  <input style={s.input} type="email" placeholder="client@example.com" value={editDraft.guest_email} onChange={e => setEditDraft(p => ({ ...p, guest_email: e.target.value }))} />
                  {(editDraft.extra_emails || []).map((em, i) => (
                    <div key={i} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <input style={{ ...s.input, flex: 1 }} type="email" placeholder="cc@example.com" value={em} onChange={e => setEditDraft(p => { const a = [...p.extra_emails]; a[i] = e.target.value; return { ...p, extra_emails: a }; })} />
                      <button type="button" onClick={() => setEditDraft(p => ({ ...p, extra_emails: p.extra_emails.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                    </div>
                  ))}
                  {(editDraft.extra_emails || []).length < 5 && (
                    <button type="button" onClick={() => setEditDraft(p => ({ ...p, extra_emails: [...(p.extra_emails || []), ''] }))} style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 12, padding: '4px 0 0', fontWeight: 600 }}>+ Add another email</button>
                  )}
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 4 }}>
                    Phone
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 5px' }}>SMS</span>
                  </label>
                  <input style={s.input} type="tel" placeholder="(956) 555-0100" value={editDraft.guest_phone} onChange={e => setEditDraft(p => ({ ...p, guest_phone: e.target.value }))} />
                  {(editDraft.extra_phones || []).map((ph, i) => (
                    <div key={i} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <input style={{ ...s.input, flex: 1 }} type="tel" placeholder="(956) 555-0101" value={ph} onChange={e => setEditDraft(p => { const a = [...p.extra_phones]; a[i] = e.target.value; return { ...p, extra_phones: a }; })} />
                      <button type="button" onClick={() => setEditDraft(p => ({ ...p, extra_phones: p.extra_phones.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                    </div>
                  ))}
                  {(editDraft.extra_phones || []).length < 5 && (
                    <button type="button" onClick={() => setEditDraft(p => ({ ...p, extra_phones: [...(p.extra_phones || []), ''] }))} style={{ background: 'none', border: 'none', color: '#1A7A4A', cursor: 'pointer', fontSize: 12, padding: '4px 0 0', fontWeight: 600 }}>+ Add another phone</button>
                  )}
                </div>
                <div style={s.formField}>
                  <label style={s.label}>Company</label>
                  <input style={s.input} placeholder="Company name" value={editDraft.company_name} onChange={e => setEditDraft(p => ({ ...p, company_name: e.target.value }))} />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={{ ...s.formField, flex: 2 }}>
                  <label style={s.label}>Billing Address</label>
                  <input style={s.input} placeholder="123 Main St, City, TX 78550" value={editDraft.billing_address} onChange={e => setEditDraft(p => ({ ...p, billing_address: e.target.value }))} />
                </div>
                <div style={{ ...s.formField, flex: 2 }}>
                  <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8 }}>
                    Service Address
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 400, color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={editSameAsBilling} onChange={e => { setEditSameAsBilling(e.target.checked); if (e.target.checked) setEditDraft(p => ({ ...p, service_address: '' })); }} style={{ margin: 0 }} />
                      Same as billing
                    </label>
                  </label>
                  {!editSameAsBilling && (
                    <input ref={editSvcAddrInputRef} style={s.input} placeholder="123 Service St, City, TX 78550" value={editDraft.service_address} onChange={e => setEditDraft(p => ({ ...p, service_address: e.target.value }))} />
                  )}
                </div>
              </div>
            </div>

            {/* Line items */}
            <div style={s.formSection}>
              <div style={s.formSectionLabel}>Line Items</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, padding: '6px 8px' }}>Description</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 60, textAlign: 'center' }}>Qty</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 90, textAlign: 'right' }}>Rate</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 90, textAlign: 'right' }}>Amount</th>
                    <th style={{ ...s.th, padding: '6px 8px', width: 28 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {editDraft.line_items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '3px 3px' }}>
                        <input style={{ ...s.input, padding: '6px 8px', width: '100%', boxSizing: 'border-box' }} placeholder="Description" value={item.description} onChange={e => updateEditLineItem(idx, 'description', e.target.value)} />
                      </td>
                      <td style={{ padding: '3px 3px' }}>
                        <input style={{ ...s.input, padding: '6px 8px', width: '100%', textAlign: 'center', boxSizing: 'border-box' }} type="number" min="1" step="1" value={item.quantity} onChange={e => updateEditLineItem(idx, 'quantity', e.target.value)} />
                      </td>
                      <td style={{ padding: '3px 3px' }}>
                        <input style={{ ...s.input, padding: '6px 8px', width: '100%', textAlign: 'right', boxSizing: 'border-box' }} type="number" min="0" step="0.01" placeholder="0.00" value={item.rate} onChange={e => updateEditLineItem(idx, 'rate', e.target.value)} />
                      </td>
                      <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: '#111827', fontSize: 13 }}>{fmtMoney(item.amount)}</td>
                      <td style={{ padding: '3px 3px', textAlign: 'center' }}>
                        {editDraft.line_items.length > 1 && (
                          <button onClick={() => removeEditLineItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '2px 4px' }}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addEditLineItem} style={{ ...s.refreshBtn, fontSize: 12 }}>+ Add Line Item</button>
            </div>

            {/* Payment terms + Tax */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={s.label}>Payment Terms</label>
                <select style={{ ...s.input, width: 'auto', padding: '5px 10px' }} value={editDraft.payment_terms} onChange={e => setEditDraft(p => ({ ...p, payment_terms: e.target.value }))}>
                  <option value="receipt">Due on receipt</option>
                  <option value="net7">Net 7</option>
                  <option value="net15">Net 15</option>
                  <option value="net30">Net 30</option>
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151', marginLeft: 'auto' }}>
                <input type="checkbox" checked={editDraft.tax_enabled !== false} onChange={e => setEditDraft(p => ({ ...p, tax_enabled: e.target.checked }))} style={{ width: 15, height: 15 }} />
                Charge sales tax (8.25%)
              </label>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 12 }}>
              <label style={s.label}>Notes (optional)</label>
              <input style={{ ...s.input, width: '100%', boxSizing: 'border-box' }} placeholder="Payment terms, special instructions..." value={editDraft.notes} onChange={e => setEditDraft(p => ({ ...p, notes: e.target.value }))} />
            </div>

            {/* Terms & Conditions */}
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
              <label style={{ ...s.label, marginBottom: 6 }}>Terms &amp; Conditions</label>
              <textarea style={{ ...s.input, width: '100%', boxSizing: 'border-box', minHeight: 60, resize: 'vertical', fontFamily: 'inherit', fontSize: 12 }} value={editDraft.terms_and_conditions} onChange={e => setEditDraft(p => ({ ...p, terms_and_conditions: e.target.value }))} />
            </div>

            {/* Preview totals */}
            <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 16px', marginBottom: 16, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Preview</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: '#6b7280' }}>Subtotal</span><span>{fmtMoney(editSub)}</span>
              </div>
              {editDraft.tax_enabled !== false && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#6b7280' }}>Sales Tax (8.25%)</span><span>{fmtMoney(editTax)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, borderTop: '1px solid #e5e7eb', paddingTop: 8, marginTop: 4 }}>
                <span>Total</span><span style={{ color: '#1A7A4A' }}>{fmtMoney(editTotal)}</span>
              </div>
            </div>

            {editError && <p style={{ ...s.error, marginBottom: 12 }}>{editError}</p>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setEditingInv(null); setEditDraft(null); }} style={s.refreshBtn}>Cancel</button>
              <button onClick={saveEditedInvoice} disabled={editSaving} style={s.btnInvSend}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Invoice confirmation ── */}
      {invDeleteTarget && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modalBox, maxWidth: 400 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>Delete {displayNum(invDeleteTarget)}?</h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              This can't be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setInvDeleteTarget(null)} style={s.refreshBtn}>Cancel</button>
              <button onClick={confirmDeleteInvoice} style={s.btnSmRed}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rejection panel modal ── */}
      {showRejectionPanel && (
        <div style={s.modalOverlay} onClick={function(e) { if (e.target === e.currentTarget) { setShowRejectionPanel(false); setSelectedRejection(null); setRejectionHaulers([]); } }}>
          <div style={{ ...s.modalBox, maxWidth: 680, maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>
                  🔴 Hauler Rejections
                </h3>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{rejections.length} unacknowledged</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {rejections.length > 1 && (
                  <button onClick={acknowledgeAllRejections} style={{ ...s.refreshBtn, fontSize: 12, padding: '6px 14px' }}>
                    Dismiss All
                  </button>
                )}
                <button onClick={function() { setShowRejectionPanel(false); setSelectedRejection(null); setRejectionHaulers([]); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1 }}>×</button>
              </div>
            </div>

            {/* Rejection list */}
            {!selectedRejection ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {rejections.map(function(r) {
                  return (
                    <div key={r.id} style={{ border: '1px solid #fee2e2', borderLeft: '4px solid #dc2626', borderRadius: 8, padding: '14px 16px', background: '#fff5f5' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 4 }}>
                            {r.hauler_name} declined #{r.order_number}
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>{r.delivery_address}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                            {r.size ? r.size.replace('yd', '-Yard') : '—'} · {new Date(r.rejected_at).toLocaleString()}
                          </div>
                          {r.reassigned_to_name ? (
                            <div style={{ fontSize: 12, color: '#1A7A4A', fontWeight: 600 }}>
                              ✓ Auto-forwarded to {r.reassigned_to_name}
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                              ⚠ No auto-forward — manual action needed
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={function() {
                              setSelectedRejection(r);
                              fetchRejectionHaulers(r.order_id);
                            }}
                            style={{ ...s.btnSmGreen, fontSize: 11, padding: '6px 14px', whiteSpace: 'nowrap' }}
                          >
                            View Haulers
                          </button>
                          <button
                            onClick={function() { acknowledgeRejection(r.id); }}
                            style={{ ...s.refreshBtn, fontSize: 11, padding: '5px 14px', whiteSpace: 'nowrap' }}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Rejection detail view */
              <div>
                <button
                  onClick={function() { setSelectedRejection(null); setRejectionHaulers([]); }}
                  style={{ ...s.refreshBtn, fontSize: 12, padding: '5px 12px', marginBottom: 16 }}
                >
                  ← Back to list
                </button>

                {/* Summary */}
                <div style={{ border: '1px solid #fee2e2', borderLeft: '4px solid #dc2626', borderRadius: 8, padding: '14px 16px', background: '#fff5f5', marginBottom: 18 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 4 }}>
                    {selectedRejection.hauler_name} declined #{selectedRejection.order_number}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>{selectedRejection.delivery_address}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                    {selectedRejection.size ? selectedRejection.size.replace('yd', '-Yard') : '—'} · {new Date(selectedRejection.rejected_at).toLocaleString()}
                  </div>
                  {selectedRejection.reassigned_to_name ? (
                    <div style={{ fontSize: 12, color: '#1A7A4A', fontWeight: 600 }}>
                      ✓ Auto-forwarded to {selectedRejection.reassigned_to_name}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                      ⚠ No auto-forward — manually dispatch below
                    </div>
                  )}
                </div>

                {/* Available haulers */}
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280', marginBottom: 10 }}>
                  Available Haulers for this Order
                </div>
                {rejectionHaulersLoading ? (
                  <div style={{ fontSize: 13, color: '#6b7280', padding: '12px 0' }}>Loading haulers...</div>
                ) : rejectionHaulers.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#ef4444', padding: '12px 0', fontWeight: 600 }}>No eligible haulers found.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {rejectionHaulers.map(function(h) {
                      const isLoading = redispatchLoading === h.id;
                      return (
                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: `1px solid ${h.margin_tier === 'red' ? '#fca5a5' : h.margin_tier === 'yellow' ? '#fde68a' : '#e5e7eb'}`, borderRadius: 8, padding: '10px 14px', background: h.already_rejected ? '#fef2f2' : h.margin_tier === 'red' ? '#fff1f2' : h.margin_tier === 'yellow' ? '#fffbeb' : '#fff', opacity: h.already_rejected ? 0.7 : 1 }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', marginBottom: 2 }}>
                              {h.company_name}
                              {h.margin_tier === 'red' && <span style={{ marginLeft: 6, fontSize: 11, color: '#dc2626', fontWeight: 700 }}>⛔ LOSS</span>}
                              {h.margin_tier === 'yellow' && <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706', fontWeight: 700 }}>⚠ Low margin</span>}
                              {h.already_rejected && <span style={{ marginLeft: 6, fontSize: 11, color: '#dc2626', fontWeight: 500 }}>Already declined</span>}
                              {h.already_offered && !h.already_rejected && <span style={{ marginLeft: 6, fontSize: 11, color: '#f59e0b', fontWeight: 500 }}>Offer pending</span>}
                            </div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>
                              {h.distance_miles !== null ? `${h.distance_miles} mi` : 'Distance unknown'}
                              {' · '}{h.quantity} in stock
                              {(function() { const s = inventoryStaleness(h.last_confirmed_at); return s ? <span style={{ marginLeft: 4, color: s.color, fontWeight: 600 }}>({s.label})</span> : null; })()}
                              {' · '}${h.hauler_rate.toFixed(2)} payout
                              {h.margin_pct !== undefined && <span style={{ color: h.margin_tier === 'red' ? '#dc2626' : h.margin_tier === 'yellow' ? '#d97706' : '#6b7280' }}>{' · '}{h.margin_pct}% margin</span>}
                            </div>
                          </div>
                          <button
                            onClick={function() { redispatchHauler(selectedRejection.order_id, h.id); }}
                            disabled={isLoading || h.already_offered}
                            style={{ ...s.btnSmGreen, fontSize: 11, padding: '6px 14px', whiteSpace: 'nowrap',
                              opacity: (isLoading || h.already_offered) ? 0.5 : 1,
                              cursor: (isLoading || h.already_offered) ? 'default' : 'pointer' }}
                          >
                            {isLoading ? 'Sending...' : h.already_offered ? 'Already offered' : 'Re-send offer'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Dismiss */}
                <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={function() { acknowledgeRejection(selectedRejection.id); }}
                    style={{ ...s.refreshBtn, fontSize: 12, padding: '7px 18px' }}
                  >
                    Acknowledge &amp; Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helper components ────────────────────────────────────────

function StatCard({ label, value }) {
  return (
    <div style={s.card}>
      <div style={s.cardValue}>{value}</div>
      <div style={s.cardLabel}>{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { pending: '#f59e0b', accepted: '#3b82f6', delivered: '#10b981', cancelled: '#ef4444' };
  return (
    <span style={{ background: colors[status] || '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
      {status}
    </span>
  );
}

function InvoiceBadge({ status }) {
  const map = {
    draft:           { bg: '#6b7280', label: 'Draft' },
    sent:            { bg: '#3b82f6', label: 'Sent' },
    paid:            { bg: '#10b981', label: 'Paid' },
    partially_paid:  { bg: '#f59e0b', label: 'Partial' },
    overdue:         { bg: '#ef4444', label: 'Overdue' },
    void:            { bg: '#9ca3af', label: 'Void' },
  };
  const { bg, label } = map[status] || { bg: '#6b7280', label: status };
  return (
    <span style={{ background: bg, color: '#fff', padding: '3px 9px', borderRadius: 5, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', display: 'inline-block' }}>
      {label}
    </span>
  );
}

function ViewedBadge({ inv }) {
  function fmtCT(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  function srcLabel(src) {
    if (src === 'email') return ' via email';
    if (src === 'sms') return ' via text';
    if (src === 'direct') return '';
    return src ? ' via ' + src : '';
  }
  if (!inv || !(inv.view_count > 0)) {
    return <span style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', background: '#f3f4f6', padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' }}>Not viewed</span>;
  }
  const tooltip = 'Viewed ' + fmtCT(inv.last_viewed_at) + srcLabel(inv.last_view_source);
  return (
    <span title={tooltip} style={{ fontSize: 10, fontWeight: 600, color: '#1d4ed8', background: '#eff6ff', padding: '2px 7px', borderRadius: 20, cursor: 'default', whiteSpace: 'nowrap' }}>
      Viewed
    </span>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#111827' }}>{value || '—'}</div>
    </div>
  );
}

function HaulerStatusBadge({ status }) {
  const map = { pending: { bg: '#f59e0b', label: 'Pending' }, approved: { bg: '#10b981', label: 'Approved' }, rejected: { bg: '#ef4444', label: 'Rejected' }, suspended: { bg: '#d97706', label: 'Suspended' } };
  const { bg, label } = map[status] || { bg: '#6b7280', label: status };
  return (
    <span style={{ background: bg, color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.02em', display: 'inline-block' }}>
      {label}
    </span>
  );
}

function ClientStatusBadge({ status }) {
  const map = { pending: { bg: '#f59e0b', label: 'Pending' }, approved: { bg: '#10b981', label: 'Approved' }, rejected: { bg: '#ef4444', label: 'Rejected' }, suspended: { bg: '#d97706', label: 'Suspended' } };
  const { bg, label } = map[status] || { bg: '#6b7280', label: status || '—' };
  return (
    <span style={{ background: bg, color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.02em', display: 'inline-block' }}>
      {label}
    </span>
  );
}

function InvoiceDetail({ inv, actionId, error, isOwner,
  onSend, onMarkPaid, onUnmarkPaid, onDelete, onEdit,
  onRecordPayment, onRemovePayment, onVoid, onDuplicate, onDownloadPdf, onOwnerDelete }) {
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const actionsRef = React.useRef(null);

  React.useEffect(function() {
    if (!actionsOpen) return;
    function handler(e) { if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false); }
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [actionsOpen]);

  const ds = invDisplayStatus(inv);
  const isPaid = ds === 'paid';
  const isVoid = ds === 'void';
  const entries = inv.payment_log_entries || [];
  const paymentsTotal = entries.reduce(function(s, p) { return s + parseFloat(p.amount || 0); }, 0);
  const balanceDue = (isPaid && paymentsTotal === 0) ? 0 : Math.max(0, Math.round((parseFloat(inv.total || 0) - paymentsTotal) * 100) / 100);
  const items = inv.line_items || [];
  const actionBusy = !!actionId;

  const methodLabel = { cash: 'Cash', check: 'Check', zelle: 'Zelle', card_in_person: 'Card (in person)', card_online: 'Card (online)', other: 'Other', stripe_pi: 'Card (Stripe)', manual_override: 'Manual override' };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: error ? 8 : 18, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 17 }}>{displayNum(inv)}</span>
          <InvoiceBadge status={ds} />
        </div>
        <div style={{ position: 'relative' }} ref={actionsRef}>
          <button
            onClick={function() { setActionsOpen(function(o) { return !o; }); }}
            disabled={actionBusy}
            style={{ ...s.btnSm, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, borderColor: '#d1d5db' }}
          >
            Actions <span style={{ fontSize: 10 }}>▾</span>
          </button>
          {actionsOpen && (
            <div style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.13)', zIndex: 200, minWidth: 190, overflow: 'hidden' }}>
              {[
                !isVoid && { label: 'Edit', action: onEdit },
                !isVoid && !isPaid && { label: 'Record Payment', action: onRecordPayment, bold: true },
                !isVoid && { label: inv.status === 'sent' ? 'Resend' : 'Send', action: onSend, disabled: !inv.guest_email },
                !isVoid && { label: 'Duplicate', action: onDuplicate },
                { label: 'Download PDF', action: onDownloadPdf },
                { label: 'Print / Preview', action: onDownloadPdf },
                !isVoid && isPaid && { label: 'Unmark Paid', action: onUnmarkPaid },
                !isVoid && !isPaid && { label: 'Mark Paid (override)', action: onMarkPaid },
                !isVoid && { label: 'Void', action: onVoid, danger: true },
                isOwner && !isVoid && { label: 'Delete (owner)', action: onOwnerDelete || onDelete, danger: true },
                !isOwner && !isPaid && !isVoid && { label: 'Delete', action: onDelete, danger: true },
              ].filter(Boolean).map(function(item, i) {
                return (
                  <button key={i} onClick={function() { setActionsOpen(false); if (!item.disabled) item.action && item.action(); }}
                    disabled={!!item.disabled}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'none', fontSize: 13,
                      fontWeight: item.bold ? 700 : 500,
                      color: item.disabled ? '#d1d5db' : item.danger ? '#dc2626' : '#111',
                      cursor: item.disabled ? 'not-allowed' : 'pointer',
                      borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                    }}>
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {error && (
        <p style={{ color: '#b91c1c', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', marginBottom: 14, marginTop: 0 }}>{error}</p>
      )}

      {/* Invoice card */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.06)', position: 'relative' }}>
        {/* VOID stamp */}
        {isVoid && (
          <div style={{ position: 'absolute', top: 60, right: 32, transform: 'rotate(-22deg)', border: '3px solid #9ca3af', borderRadius: 4, padding: '4px 14px', color: '#9ca3af', fontSize: 28, fontWeight: 900, letterSpacing: 6, pointerEvents: 'none', zIndex: 10, opacity: 0.7 }}>
            VOID
          </div>
        )}
        {/* B&W header */}
        <div style={{ background: '#0D0D0D', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', letterSpacing: 1 }}>Tambo</div>
            <div style={{ fontSize: 9, color: '#888', marginTop: 2, letterSpacing: '1.2px', textTransform: 'uppercase' }}>Fast Local Dumpsters.</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>INVOICE</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{displayNum(inv)}</div>
          </div>
        </div>

        {/* From / Bill To / Dates */}
        <div style={{ display: 'flex', gap: 24, padding: '20px 28px 16px', flexWrap: 'wrap', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={s.detailLabel}>From</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Tambo Technologies, LLC</div>
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7, marginTop: 2 }}>
              1212 East Harrison Avenue<br />
              Harlingen, Texas 78550<br />
              (844) 878-2626<br />
              office@tambodumpsters.com
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={s.detailLabel}>Bill To</div>
            {inv.guest_name && <div style={{ fontWeight: 700, fontSize: 13 }}>{inv.guest_name}</div>}
            {inv.company_name && <div style={{ fontSize: 12, color: '#374151' }}>{inv.company_name}</div>}
            {inv.billing_address && <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7, marginTop: 2 }}>{inv.billing_address}</div>}
            {inv.guest_email && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{inv.guest_email}</div>}
            {inv.guest_phone && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{inv.guest_phone}</div>}
            {!inv.guest_name && !inv.company_name && <div style={{ fontSize: 12, color: '#9ca3af' }}>—</div>}
            {((inv.extra_emails && inv.extra_emails.length > 0) || (inv.extra_phones && inv.extra_phones.length > 0)) && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #e5e7eb' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 3 }}>Also sent to:</div>
                {(inv.extra_emails || []).map((em, i) => <div key={i} style={{ fontSize: 11, color: '#9ca3af' }}>{em}</div>)}
                {(inv.extra_phones || []).map((ph, i) => <div key={i} style={{ fontSize: 11, color: '#9ca3af' }}>{ph}</div>)}
              </div>
            )}
          </div>
          {inv.service_address && (
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={s.detailLabel}>Service Address</div>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7 }}>{inv.service_address}</div>
            </div>
          )}
          <div style={{ minWidth: 140, textAlign: 'right' }}>
            <div style={s.detailLabel}>Details</div>
            <table style={{ marginLeft: 'auto', fontSize: 12 }}>
              <tbody>
                <tr><td style={{ color: '#9ca3af', paddingRight: 10, paddingBottom: 3, whiteSpace: 'nowrap' }}>Invoice No</td><td style={{ fontWeight: 700 }}>{displayNum(inv)}</td></tr>
                <tr><td style={{ color: '#9ca3af', paddingRight: 10, paddingBottom: 3 }}>Date</td><td>{fmtDate(inv.created_at)}</td></tr>
                <tr><td style={{ color: '#9ca3af', paddingRight: 10 }}>Due</td><td>{fmtDate(inv.due_date)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Line items */}
        <div style={{ padding: '16px 28px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[['Description', 'left', ''], ['Qty', 'center', '60px'], ['Rate', 'right', '90px'], ['Amount', 'right', '90px']].map(([h, align, w]) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: align, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', fontWeight: 700, width: w || 'auto' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: 10, color: '#9ca3af', textAlign: 'center' }}>No line items</td></tr>
              ) : items.map((item, i) => (
                <tr key={i}>
                  <td style={{ padding: '9px 10px', borderBottom: '1px solid #f3f4f6' }}>{item.description}</td>
                  <td style={{ padding: '9px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>{item.quantity}</td>
                  <td style={{ padding: '9px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{fmtMoney(item.rate)}</td>
                  <td style={{ padding: '9px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <table style={{ marginLeft: 'auto', fontSize: 13, marginTop: 8, minWidth: 230 }}>
            <tbody>
              <tr><td style={{ padding: '5px 10px', color: '#6b7280' }}>Subtotal</td><td style={{ padding: '5px 10px', textAlign: 'right' }}>{fmtMoney(inv.subtotal)}</td></tr>
              {inv.tax_enabled !== false && (
                <tr><td style={{ padding: '5px 10px', color: '#6b7280' }}>Sales Tax (8.25%)</td><td style={{ padding: '5px 10px', textAlign: 'right' }}>{fmtMoney(inv.tax_amount)}</td></tr>
              )}
              <tr>
                <td style={{ padding: '8px 10px', fontWeight: 700, borderTop: '2px solid #e5e7eb' }}>Total</td>
                <td style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right', borderTop: '2px solid #e5e7eb' }}>{fmtMoney(inv.total)}</td>
              </tr>
            </tbody>
          </table>

          {/* Payments & Refunds */}
          {(entries.length > 0 || (isPaid && inv.paid_at && entries.length === 0)) && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: 8 }}>Payments &amp; Refunds</div>
              {entries.length === 0 && isPaid && inv.paid_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>Payment received {fmtDate(inv.paid_at)}</span>
                  <span style={{ color: '#0D0D0D', fontWeight: 600 }}>−{fmtMoney(inv.total)}</span>
                </div>
              )}
              {entries.map(function(p, i) {
                const meth = methodLabel[p.payment_type] || p.payment_method || p.payment_type || '—';
                const amt = parseFloat(p.amount || 0);
                const hasAmt = p.amount != null;
                const dt = p.payment_date ? new Date(String(p.payment_date).split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : fmtDate(p.created_at);
                return (
                  <div key={p.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 13, marginBottom: 8, padding: '8px 10px', background: '#f9fafb', borderRadius: 7 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{meth} {p.reference_number ? <span style={{ color: '#6b7280', fontWeight: 400 }}>· #{p.reference_number}</span> : null}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                        {dt}{p.performed_by ? ' · by ' + p.performed_by : ''}{p.reason ? ' · ' + p.reason : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
                      <span style={{ fontWeight: 700, color: '#0D0D0D' }}>{hasAmt ? '−' + fmtMoney(amt) : '(full)'}</span>
                      {isOwner && (
                        <button onClick={function() { onRemovePayment && onRemovePayment(p.id); }}
                          title="Remove payment entry (owner only)"
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: '2px 4px' }}>✕</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Balance Due */}
          {!isVoid && (
            <div style={{ background: balanceDue === 0 ? '#f0fdf4' : ds === 'overdue' ? '#fef2f2' : '#f5f5f3', borderRadius: 6, padding: '12px 14px', marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: '#0D0D0D', fontSize: 14 }}>{balanceDue === 0 ? 'Paid in Full' : 'Balance Due'}</span>
              <span style={{ fontWeight: 900, color: ds === 'overdue' ? '#ef4444' : '#0D0D0D', fontSize: 18 }}>{fmtMoney(balanceDue)}</span>
            </div>
          )}

          {/* View stats */}
          {(function() {
            function fmtCT(ts) {
              if (!ts) return '';
              return new Date(ts).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
            }
            function srcLabel(src) {
              if (src === 'email') return ' (email)';
              if (src === 'sms') return ' (text)';
              if (src === 'direct') return '';
              return src ? ' (' + src + ')' : '';
            }
            if (!(inv.view_count > 0)) return null;
            const count = inv.view_count;
            const parts = ['Viewed ' + count + (count === 1 ? ' time' : ' times')];
            if (inv.first_viewed_at) parts.push('first ' + fmtCT(inv.first_viewed_at) + (count === 1 ? srcLabel(inv.last_view_source) : ''));
            if (count > 1 && inv.last_viewed_at) parts.push('last ' + fmtCT(inv.last_viewed_at) + srcLabel(inv.last_view_source));
            return (
              <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280', padding: '6px 4px' }}>
                {parts.join(' · ')}
              </div>
            );
          })()}

          {inv.notes && (
            <p style={{ marginTop: 14, padding: '10px 14px', background: '#f9f9f7', borderRadius: 6, fontSize: 12, color: '#6b7280', lineHeight: 1.6, margin: '14px 0 0' }}>
              <strong>Notes:</strong> {inv.notes}
            </p>
          )}

          {inv.terms_and_conditions && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#f9f9f7', borderRadius: 6, fontSize: 11, color: '#9ca3af', lineHeight: 1.7, borderTop: '1px solid #f3f4f6' }}>
              <strong style={{ display: 'block', marginBottom: 4, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Terms &amp; Conditions</strong>
              {inv.terms_and_conditions}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ background: '#f9fafb', padding: '12px 28px', textAlign: 'center', fontSize: 11, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
          Tambo Technologies, LLC &middot; 1212 East Harrison Avenue, Harlingen, TX 78550 &middot; (844) 878-2626 &middot; office@tambodumpsters.com
        </div>
      </div>
    </div>
  );
}

// Inline "move to market" cell used in MarketDetailPortal's orders table
function MoveCell({ orderId, currentMarket, mktList, onMoved }) {
  const [sel,    setSel]    = useState(currentMarket);
  const [saving, setSaving] = useState(false);

  async function doMove() {
    if (sel === currentMarket || saving) return;
    setSaving(true);
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/orders/${orderId}/market`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ market_name: sel }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      onMoved(orderId);
    } catch (err) {
      alert(err.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      <select
        value={sel}
        onChange={function(e) { setSel(e.target.value); }}
        style={{ fontSize: 11, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', color: '#374151', maxWidth: 130, cursor: 'pointer' }}
      >
        {mktList.map(function(m) { return <option key={m} value={m}>{m}</option>; })}
      </select>
      {sel !== currentMarket && (
        <button
          onClick={doMove}
          disabled={saving}
          style={{ fontSize: 10, padding: '3px 7px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          {saving ? '…' : 'Move'}
        </button>
      )}
    </div>
  );
}

function OrderDetail({ order: orderProp, onMarketChanged, onClientUpdated, onRefresh }) {
  const [order, setOrder] = useState(orderProp);
  const [confirmModal, setConfirmModal] = useState(null); // { message, onConfirm, label? }

  const [markets,   setMarkets]   = useState([]);
  const [curMarket, setCurMarket] = useState(order.market_name || 'Unassigned');
  const [selMarket, setSelMarket] = useState(order.market_name || 'Unassigned');
  const [mktSaving, setMktSaving] = useState(false);
  const [mktMsg,    setMktMsg]    = useState('');

  const [editingClient, setEditingClient] = useState(false);
  const [clientForm,    setClientForm]    = useState({});
  const [clientSaving,  setClientSaving]  = useState(false);
  const [clientErr,     setClientErr]     = useState('');

  const [haulerList,        setHaulerList]        = useState([]);
  const [haulerListLoading, setHaulerListLoading] = useState(false);
  const [assignLoading,     setAssignLoading]     = useState(null);
  const [assignMsg,         setAssignMsg]         = useState('');

  const [statusOverride, setStatusOverride] = useState(order.status);
  const [statusSaving,   setStatusSaving]   = useState(false);
  const [statusMsg,      setStatusMsg]      = useState('');

  const [reassignHaulerList,    setReassignHaulerList]    = useState([]);
  const [reassignHaulerLoading, setReassignHaulerLoading] = useState(false);
  const [reassignLoading,       setReassignLoading]       = useState(null);
  const [reassignMsg,           setReassignMsg]           = useState('');
  const [showReassign,          setShowReassign]          = useState(false);

  const [clearingAttn,  setClearingAttn]  = useState(false);
  const [refreshing,    setRefreshing]    = useState(false);

  function openClientEdit() {
    setClientForm({
      guest_name:    order.guest_name    || '',
      guest_email:   order.guest_email   || '',
      guest_phone:   order.guest_phone   || '',
      guest_company: order.guest_company || '',
      guest_address: order.guest_address || '',
    });
    setClientErr('');
    setEditingClient(true);
  }

  async function saveClientInfo() {
    setClientSaving(true); setClientErr('');
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/client`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update');
      setEditingClient(false);
      if (onClientUpdated) onClientUpdated(d.fields);
    } catch (err) {
      setClientErr(err.message);
    } finally {
      setClientSaving(false);
    }
  }

  useEffect(function() {
    if (order.status === 'pending') {
      setHaulerListLoading(true);
      adminFetch(`${BACKEND}/api/admin/orders/${order.id}/available-haulers`)
        .then(function(r) { return r.json(); })
        .then(function(d) { setHaulerList(d.haulers || []); })
        .catch(function() {})
        .finally(function() { setHaulerListLoading(false); });
    }
  }, [order.id, order.status]); // eslint-disable-line react-hooks/exhaustive-deps

  async function assignHauler(haulerId, force) {
    setAssignLoading(haulerId); setAssignMsg('');
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/redispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hauler_id: haulerId, force: !!force }),
      });
      const d = await r.json();
      if (!r.ok) {
        if (d.margin_tier === 'red' && !force) {
          setAssignLoading(null);
          setConfirmModal({ message: `⛔ LOSS ALERT: Payout $${parseFloat(d.payout || 0).toFixed(2)} > subtotal $${parseFloat(d.client_subtotal || 0).toFixed(2)} (−$${parseFloat(d.shortfall || 0).toFixed(2)}). This order will LOSE MONEY. Assign anyway?`, label: 'Assign Anyway', onConfirm: function() { assignHauler(haulerId, true); } });
          return;
        }
        throw new Error(d.error || 'Assignment failed');
      }
      if (d.margin_tier === 'yellow') setAssignMsg(`⚠ Dispatched with low margin ($${parseFloat(d.payout || 0).toFixed(2)} payout).`);
      else setAssignMsg('Dispatched! Awaiting hauler response.');
      setOrder(function(prev) { return { ...prev, needs_attention: false }; });
    } catch (err) {
      setAssignMsg('Error: ' + err.message);
    } finally {
      setAssignLoading(null);
    }
  }

  async function overrideStatus() {
    if (statusOverride === order.status) return;
    setStatusSaving(true); setStatusMsg('');
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: statusOverride }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setOrder(function(prev) { return { ...prev, status: statusOverride }; });
      setStatusMsg('Status updated');
    } catch (err) {
      setStatusMsg('Error: ' + err.message);
    } finally {
      setStatusSaving(false);
    }
  }

  function loadReassignHaulers() {
    setReassignHaulerLoading(true);
    adminFetch(`${BACKEND}/api/admin/orders/${order.id}/available-haulers`)
      .then(function(r) { return r.json(); })
      .then(function(d) { setReassignHaulerList(d.haulers || []); })
      .catch(function() {})
      .finally(function() { setReassignHaulerLoading(false); });
  }

  async function reassignHauler(haulerId, force) {
    setReassignLoading(haulerId); setReassignMsg('');
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/hauler`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hauler_id: haulerId, force: !!force }),
      });
      const d = await r.json();
      if (!r.ok) {
        if (d.margin_tier === 'red' && !force) {
          setReassignLoading(null);
          setConfirmModal({ message: `⛔ LOSS ALERT: Payout $${parseFloat(d.payout || 0).toFixed(2)} > subtotal $${parseFloat(d.client_subtotal || 0).toFixed(2)} (−$${parseFloat(d.shortfall || 0).toFixed(2)}). This order will LOSE MONEY. Assign anyway?`, label: 'Assign Anyway', onConfirm: function() { reassignHauler(haulerId, true); } });
          return;
        }
        throw new Error(d.error || 'Assignment failed');
      }
      if (d.margin_tier === 'yellow') setReassignMsg(`⚠ Assigned with low margin ($${parseFloat(d.payout || 0).toFixed(2)} payout).`);
      else setReassignMsg(`Assigned to ${d.hauler}.`);
      setOrder(function(prev) { return { ...prev, needs_attention: false, hauler_name: d.hauler }; });
      setShowReassign(false);
    } catch (err) {
      setReassignMsg('Error: ' + err.message);
    } finally {
      setReassignLoading(null);
    }
  }

  async function clearAttention() {
    setClearingAttn(true);
    try {
      await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/clear-attention`, { method: 'POST' });
      setOrder(function(prev) { return { ...prev, needs_attention: false }; });
    } catch(e) { /* silent */ } finally { setClearingAttn(false); }
  }

  async function refreshOrder() {
    setRefreshing(true);
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}`);
      const d = await r.json();
      if (d.order) {
        setOrder(d.order);
        setStatusOverride(d.order.status);
        setCurMarket(d.order.market_name || 'Unassigned');
        setSelMarket(d.order.market_name || 'Unassigned');
        setStatusMsg(''); setAssignMsg(''); setReassignMsg('');
      }
    } catch(e) { /* silent */ } finally { setRefreshing(false); }
  }

  useEffect(function() {
    adminFetch(`${BACKEND}/api/admin/markets`)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        var names = (d.markets || [])
          .map(function(m) { return m.market_name; })
          .filter(function(n) { return n !== 'Default'; });
        if (!names.includes('Unassigned')) names.unshift('Unassigned');
        setMarkets(names);
      })
      .catch(function() {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveMarket() {
    if (selMarket === curMarket || mktSaving) return;
    setMktSaving(true); setMktMsg('');
    try {
      const r = await adminFetch(`${BACKEND}/api/admin/orders/${order.id}/market`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ market_name: selMarket }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update market');
      setCurMarket(selMarket);
      setMktMsg('Market updated');
      if (onMarketChanged) onMarketChanged(order.id, selMarket);
    } catch (err) {
      setMktMsg(err.message);
    } finally {
      setMktSaving(false);
    }
  }

  const DUR_LABELS = { '3_day': '3 Days', '5_day': '5 Days', '7_day': '7 Days' };
  const STATUS_STEPS = [
    { key: 'pending',   label: 'Placed' },
    { key: 'accepted',  label: 'Hauler Assigned' },
    { key: 'delivered', label: 'Delivered' },
    { key: 'completed', label: 'Completed' },
  ];
  const stepIdx = STATUS_STEPS.findIndex(function(st) { return st.key === order.status; });
  const currentStep = stepIdx === -1 ? 0 : stepIdx;

  function fmtWindow(start, end) {
    if (!start || !end) return '—';
    function to12(t) {
      const [h, m] = String(t).split(':');
      const hr = parseInt(h);
      return `${hr > 12 ? hr - 12 : (hr || 12)}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
    }
    return `${to12(start)} – ${to12(end)}`;
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 17 }}>{order.order_number}</span>
          <StatusBadge status={order.status} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={refreshOrder} disabled={refreshing} style={{ ...s.btnSm, background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}>
            {refreshing ? '⟳ …' : '⟳ Refresh'}
          </button>
          <a
            href={`/order/${order.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...s.btnSm, textDecoration: 'none', display: 'inline-block' }}
          >
            View Tracking Page ↗
          </a>
        </div>
      </div>

      {/* Needs attention banner */}
      {order.needs_attention && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 13 }}>Needs Manual Hauler Assignment</div>
              <div style={{ fontSize: 12, color: '#991b1b', marginTop: 2 }}>
                {order.hauler_name
                  ? `Hauler ${order.hauler_name} is assigned but this flag was not cleared — use "Clear Flag" if resolved.`
                  : 'The assigned hauler did not respond after 30 min. Use the panel below to dispatch a replacement.'}
              </div>
            </div>
          </div>
          <button onClick={clearAttention} disabled={clearingAttn}
            style={{ padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {clearingAttn ? '…' : 'Clear Flag'}
          </button>
        </div>
      )}

      {/* Inline hauler assignment for pending orders */}
      {order.status === 'pending' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '20px 28px', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 12 }}>Assign Hauler</div>
          {haulerListLoading ? (
            <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>Loading available haulers...</p>
          ) : haulerList.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>No available haulers for this size in range.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {haulerList.map(function(h) {
                const isLoading = assignLoading === h.id;
                return (
                  <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: `1px solid ${h.margin_tier === 'red' ? '#fca5a5' : h.margin_tier === 'yellow' ? '#fde68a' : '#e5e7eb'}`, borderRadius: 8, padding: '10px 14px', background: h.margin_tier === 'red' ? '#fff1f2' : h.margin_tier === 'yellow' ? '#fffbeb' : '#fff' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', marginBottom: 2 }}>
                        {h.company_name}
                        {h.margin_tier === 'red' && <span style={{ marginLeft: 6, fontSize: 11, color: '#dc2626', fontWeight: 700 }}>⛔ LOSS</span>}
                        {h.margin_tier === 'yellow' && <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706', fontWeight: 700 }}>⚠ Low margin</span>}
                        {h.already_rejected && <span style={{ marginLeft: 6, fontSize: 11, color: '#dc2626', fontWeight: 500 }}>Declined before</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {h.distance_miles !== null ? `${h.distance_miles} mi` : 'Distance unknown'}
                        {' · '}{h.quantity} in stock
                        {(function() { const s = inventoryStaleness(h.last_confirmed_at); return s ? <span style={{ marginLeft: 4, color: s.color, fontWeight: 600 }}>({s.label})</span> : null; })()}
                        {' · '}${h.hauler_rate.toFixed(2)} payout
                        {h.margin_pct !== undefined && <span style={{ color: h.margin_tier === 'red' ? '#dc2626' : h.margin_tier === 'yellow' ? '#d97706' : '#6b7280' }}>{' · '}{h.margin_pct}% margin</span>}
                      </div>
                    </div>
                    <button
                      onClick={function() { assignHauler(h.id, false); }}
                      disabled={isLoading || h.already_offered}
                      style={{ padding: '6px 14px', background: h.margin_tier === 'red' ? '#dc2626' : '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: (isLoading || h.already_offered) ? 'default' : 'pointer', opacity: (isLoading || h.already_offered) ? 0.5 : 1, whiteSpace: 'nowrap' }}
                    >
                      {isLoading ? 'Sending...' : h.already_offered ? 'Already offered' : 'Dispatch'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {assignMsg && <p style={{ marginTop: 10, fontSize: 13, color: assignMsg.startsWith('Error') ? '#dc2626' : '#1A7A4A', fontWeight: 600, margin: '10px 0 0' }}>{assignMsg}</p>}
        </div>
      )}

      {/* Status override */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 10 }}>Override Status</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={statusOverride}
            onChange={function(e) { setStatusOverride(e.target.value); setStatusMsg(''); }}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, background: '#fff', color: '#111', minWidth: 180 }}
          >
            {['pending','accepted','delivered','pickup_requested','completed','cancelled'].map(function(s) {
              return <option key={s} value={s}>{s}</option>;
            })}
          </select>
          <button
            onClick={overrideStatus}
            disabled={statusSaving || statusOverride === order.status}
            style={{ padding: '7px 16px', background: statusOverride !== order.status && !statusSaving ? '#1A7A4A' : '#e5e7eb', color: statusOverride !== order.status && !statusSaving ? '#fff' : '#9ca3af', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: statusOverride !== order.status && !statusSaving ? 'pointer' : 'not-allowed' }}>
            {statusSaving ? 'Saving…' : 'Apply'}
          </button>
          {statusMsg && <span style={{ fontSize: 12, color: statusMsg.startsWith('Error') ? '#dc2626' : '#1A7A4A', fontWeight: 600 }}>{statusMsg}</span>}
        </div>
      </div>

      {/* Reassign hauler (non-pending orders) */}
      {order.status !== 'pending' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showReassign ? 12 : 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af' }}>
              {order.hauler_name ? `Reassign Hauler (current: ${order.hauler_name})` : 'Assign Hauler'}
            </div>
            <button
              onClick={function() {
                setShowReassign(function(prev) {
                  if (!prev) loadReassignHaulers();
                  return !prev;
                });
                setReassignMsg('');
              }}
              style={{ padding: '4px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
              {showReassign ? 'Hide' : 'Select Hauler'}
            </button>
          </div>
          {showReassign && (
            reassignHaulerLoading ? (
              <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>Loading haulers…</p>
            ) : reassignHaulerList.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>No eligible haulers found for this size.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {reassignHaulerList.map(function(h) {
                  const isLoading = reassignLoading === h.id;
                  return (
                    <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: `1px solid ${h.margin_tier === 'red' ? '#fca5a5' : h.margin_tier === 'yellow' ? '#fde68a' : '#e5e7eb'}`, borderRadius: 8, padding: '10px 14px', background: h.margin_tier === 'red' ? '#fff1f2' : h.margin_tier === 'yellow' ? '#fffbeb' : '#fff' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                          {h.company_name}
                          {h.margin_tier === 'red' && <span style={{ marginLeft: 6, fontSize: 11, color: '#dc2626', fontWeight: 700 }}>⛔ LOSS</span>}
                          {h.margin_tier === 'yellow' && <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706', fontWeight: 700 }}>⚠ Low margin</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          {h.distance_miles !== null ? `${h.distance_miles} mi` : 'Distance unknown'}
                          {' · '}{h.quantity} in stock
                          {(function() { const s = inventoryStaleness(h.last_confirmed_at); return s ? <span style={{ marginLeft: 4, color: s.color, fontWeight: 600 }}>({s.label})</span> : null; })()}
                          {' · '}${h.hauler_rate.toFixed(2)} payout
                        </div>
                      </div>
                      <button
                        onClick={function() { reassignHauler(h.id, false); }}
                        disabled={isLoading}
                        style={{ padding: '6px 14px', background: h.margin_tier === 'red' ? '#dc2626' : '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: isLoading ? 'default' : 'pointer', opacity: isLoading ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                        {isLoading ? 'Assigning…' : 'Assign'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )
          )}
          {reassignMsg && <p style={{ marginTop: 10, fontSize: 13, color: reassignMsg.startsWith('Error') ? '#dc2626' : '#1A7A4A', fontWeight: 600, margin: '10px 0 0' }}>{reassignMsg}</p>}
        </div>
      )}

      {/* Status timeline */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '20px 28px', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 16 }}>Status Timeline</div>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {STATUS_STEPS.map(function(step, i) {
            const done = currentStep >= i;
            const active = currentStep === i;
            return (
              <React.Fragment key={step.key}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: done ? '#1A7A4A' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6, fontSize: 11, color: done ? '#fff' : '#9ca3af', fontWeight: 700 }}>
                    {done ? '✓' : i + 1}
                  </div>
                  <div style={{ fontSize: 10, textAlign: 'center', color: done ? '#1A7A4A' : '#9ca3af', fontWeight: active ? 700 : 500, lineHeight: 1.3 }}>{step.label}</div>
                </div>
                {i < STATUS_STEPS.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: currentStep > i ? '#1A7A4A' : '#e5e7eb', marginTop: 12 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Detail card */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
        {/* Green header */}
        <div style={{ background: '#1A7A4A', padding: '16px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 1 }}>Tambo</div>
            <div style={{ fontSize: 10, color: '#9FDCB8', marginTop: 1, letterSpacing: '0.8px' }}>FAST DUMPSTER DELIVERY</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{order.order_number}</div>
            <div style={{ fontSize: 11, color: '#9FDCB8', marginTop: 1 }}>Placed {fmtDate(order.created_at)}</div>
          </div>
        </div>

        {/* Client */}
        <div style={{ padding: '20px 28px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={s.detailLabel}>Client</div>
            {!editingClient && (
              <button
                onClick={openClientEdit}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A7A4A', fontSize: 12, fontWeight: 600, padding: '2px 4px', lineHeight: 1 }}
              >✏ Edit</button>
            )}
          </div>
          {editingClient ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: '10px 16px', marginBottom: 12 }}>
                {[
                  { key: 'guest_name',    label: 'Name',    type: 'text'  },
                  { key: 'guest_email',   label: 'Email',   type: 'email' },
                  { key: 'guest_phone',   label: 'Phone',   type: 'tel'   },
                  { key: 'guest_company', label: 'Company', type: 'text'  },
                  { key: 'guest_address', label: 'Address', type: 'text'  },
                ].map(function(f) {
                  return (
                    <div key={f.key}>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{f.label}</div>
                      <input
                        type={f.type}
                        value={clientForm[f.key]}
                        onChange={function(e) { setClientForm(function(p) { return { ...p, [f.key]: e.target.value }; }); }}
                        style={{ width: '100%', padding: '7px 9px', border: '1.5px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>
                  );
                })}
              </div>
              {clientErr && <p style={{ margin: '0 0 10px', fontSize: 13, color: '#ef4444' }}>{clientErr}</p>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={function() { setEditingClient(false); setClientErr(''); }}
                  style={{ padding: '6px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
                >Cancel</button>
                <button
                  onClick={saveClientInfo}
                  disabled={clientSaving}
                  style={{ padding: '6px 14px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: clientSaving ? 'default' : 'pointer', opacity: clientSaving ? 0.6 : 1 }}
                >{clientSaving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px 24px' }}>
              <DetailField label="Name" value={order.guest_name} />
              <DetailField label="Email" value={order.guest_email} />
              <DetailField label="Phone" value={order.guest_phone} />
              <DetailField label="Company" value={order.guest_company} />
              <DetailField label="Address" value={order.guest_address || order.delivery_address} />
            </div>
          )}
        </div>

        {/* Service details */}
        <div style={{ padding: '20px 28px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={s.detailLabel}>Service Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px 24px' }}>
            <DetailField label="Dumpster Size" value={SIZE_LABELS[order.size] || order.size} />
            <DetailField label="Duration" value={DUR_LABELS[order.duration] || order.duration} />
            <DetailField label="Delivery Date" value={fmtDate(order.requested_delivery_date)} />
            <DetailField label="Time Window" value={fmtWindow(order.delivery_window_start, order.delivery_window_end)} />
            <DetailField label="Delivery ZIP" value={order.delivery_zip} />
          </div>
        </div>

        {/* Special instructions */}
        {order.special_instructions && (
          <div style={{ padding: '20px 28px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={s.detailLabel}>Special Instructions</div>
            <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', marginTop: 6, lineHeight: 1.6 }}>
              {order.special_instructions}
            </div>
          </div>
        )}

        {/* Dumpster placement map */}
        {order.placement_lat && order.placement_lng && (
          <div style={{ padding: '20px 28px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={s.detailLabel}>Dumpster Placement</div>
            <img
              src={`${BACKEND}/api/placement-map/${order.id}`}
              alt="Dumpster placement"
              style={{ width: '100%', maxWidth: 500, borderRadius: 8, marginTop: 8, display: 'block' }}
            />
          </div>
        )}

        {/* Hauler */}
        {order.hauler_name && (
          <div style={{ padding: '20px 28px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={s.detailLabel}>Hauler Assigned</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px 24px' }}>
              <DetailField label="Company" value={order.hauler_name} />
              <DetailField label="Phone" value={order.hauler_phone} />
            </div>
          </div>
        )}

        {/* Pricing */}
        <div style={{ padding: '20px 28px' }}>
          <div style={s.detailLabel}>Pricing</div>
          <table style={{ marginLeft: 'auto', fontSize: 13, minWidth: 240 }}>
            <tbody>
              <tr><td style={{ padding: '4px 16px 4px 0', color: '#6b7280' }}>Dumpster</td><td style={{ padding: '4px 0', textAlign: 'right' }}>{fmtMoney(order.client_price)}</td></tr>
              <tr><td style={{ padding: '4px 16px 4px 0', color: '#6b7280' }}>Environmental Fee</td><td style={{ padding: '4px 0', textAlign: 'right' }}>{fmtMoney(15)}</td></tr>
              <tr><td style={{ padding: '4px 16px 4px 0', color: '#6b7280' }}>Tax (8.25%)</td><td style={{ padding: '4px 0', textAlign: 'right' }}>{fmtMoney(order.tax_amount)}</td></tr>
              <tr>
                <td style={{ padding: '8px 16px 4px 0', fontWeight: 700, borderTop: '2px solid #e5e7eb' }}>Total Charged</td>
                <td style={{ padding: '8px 0 4px', fontWeight: 700, textAlign: 'right', borderTop: '2px solid #e5e7eb' }}>{fmtMoney(order.total_charged)}</td>
              </tr>
              {order.hauler_payout && (
                <tr><td style={{ padding: '4px 16px 4px 0', color: '#6b7280' }}>Hauler Payout</td><td style={{ padding: '4px 0', textAlign: 'right', color: '#ef4444' }}>−{fmtMoney(order.hauler_payout)}</td></tr>
              )}
              {order.tambo_commission && (
                <tr>
                  <td style={{ padding: '6px 16px 4px 0', fontWeight: 700, color: '#1A7A4A', borderTop: '1px solid #e5e7eb' }}>Tambo Commission</td>
                  <td style={{ padding: '6px 0 4px', fontWeight: 700, textAlign: 'right', color: '#1A7A4A', borderTop: '1px solid #e5e7eb' }}>{fmtMoney(order.tambo_commission)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Market assignment */}
        <div style={{ padding: '20px 28px', borderTop: '1px solid #f3f4f6' }}>
          <div style={s.detailLabel}>Market Assignment</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <select
              value={selMarket}
              onChange={function(e) { setSelMarket(e.target.value); setMktMsg(''); }}
              style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, background: '#fff', color: '#111', cursor: 'pointer', minWidth: 160 }}
            >
              {markets.length === 0 && <option value={curMarket}>{curMarket}</option>}
              {markets.map(function(m) { return <option key={m} value={m}>{m}</option>; })}
            </select>
            <button
              onClick={saveMarket}
              disabled={selMarket === curMarket || mktSaving}
              style={{ padding: '7px 14px', background: selMarket !== curMarket && !mktSaving ? '#1A7A4A' : '#e5e7eb', color: selMarket !== curMarket && !mktSaving ? '#fff' : '#9ca3af', border: 'none', borderRadius: 7, cursor: selMarket !== curMarket && !mktSaving ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', transition: 'background 0.15s' }}
            >
              {mktSaving ? 'Saving…' : 'Update Market'}
            </button>
            {mktMsg && (
              <span style={{ fontSize: 12, color: mktMsg === 'Market updated' ? '#1A7A4A' : '#dc2626', fontWeight: 600 }}>{mktMsg}</span>
            )}
          </div>
        </div>
      </div>
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <p style={{ fontSize: 15, color: '#111', lineHeight: 1.55, margin: '0 0 24px' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={function() { setConfirmModal(null); }} style={{ flex: 1, padding: '10px 0', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={function() { var fn = confirmModal.onConfirm; setConfirmModal(null); fn(); }} style={{ flex: 1, padding: '10px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>{confirmModal.label || 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  loginWrap: { maxWidth: 360, margin: '80px auto', textAlign: 'center', fontFamily: 'sans-serif', padding: '0 16px' },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  input: { padding: '10px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', fontFamily: 'inherit' },
  btn: { padding: '10px 0', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnRed: { padding: '6px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnApprove: { padding: '7px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  btnReject: { padding: '7px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  btnSuspend: { padding: '7px 16px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  btnRemove: { padding: '7px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  refreshBtn: { padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  btnInvNew: { padding: '7px 16px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  btnInvDraft: { padding: '9px 20px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  btnInvSend: { padding: '9px 20px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  btnSm: { padding: '4px 10px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  btnSmBlue: { padding: '4px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  btnSmGreen: { padding: '4px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  btnSmRed: { padding: '4px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  error: { color: '#ef4444', fontSize: 13, margin: 0 },
  wrap: { maxWidth: 1280, margin: '0 auto', padding: '24px 20px', fontFamily: 'sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  tabBar: { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 28 },
  tabBtn: { padding: '8px 20px', background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 },
  tabBtnActive: { borderBottomColor: '#1d4ed8', color: '#1d4ed8', fontWeight: 700 },
  badge: { background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px', minWidth: 18, textAlign: 'center' },
  stats: { display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' },
  card: { flex: '1 1 160px', background: '#f3f4f6', borderRadius: 10, padding: '20px 24px' },
  cardValue: { fontSize: 30, fontWeight: 700, color: '#111827' },
  cardLabel: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  expandPanel: { background: '#f8faff', padding: '20px 24px', borderLeft: '3px solid #3b82f6' },
  expandGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px 24px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 700 },
  th: { textAlign: 'left', padding: '10px 12px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151' },
  row: { transition: 'background 0.1s' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' },
  modalBox: { background: '#fff', borderRadius: 10, padding: '28px 28px 24px', width: '100%', maxWidth: 760, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' },
  formSection: { marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f3f4f6' },
  formSectionLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 10 },
  formRow: { display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' },
  formField: { flex: 1, minWidth: 200 },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  detailLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: 6 },
};
